// ⚠️  GENERATED — do not edit by hand.
// Source: vantage-backend/api-docs/openapi-combined.json (OpenAPI 3.1, snake_case API contract).
// Regenerate:  bun tools/codegen/openapi-to-zod.mjs
// These mirror the backend response/request shapes exactly; map to camelCase app models in
// the data-access layer. Zod-first per CLAUDE.md rule #16.
import { z } from 'zod';

export const AccountActivityResponseSchema = z.object({
  acted_by_user_id: z.string().nullable().optional(),
  action: z.string(),
  actor_email: z.string().nullable().optional(),
  actor_id: z.string().nullable().optional(),
  actor_name: z.string().nullable().optional(),
  actor_type: z.string().nullable().optional(),
  created_at: z.iso.datetime(),
  diff: z.record(z.string(), z.unknown()).nullable().optional(),
  id: z.number().int(),
  record_id: z.string(),
  table_name: z.string(),
});
export type AccountActivityResponse = z.infer<typeof AccountActivityResponseSchema>;

/** The account head-band's AR balance: what this client still owes us. `ar_outstanding` is the sum of issued-but-unpaid invoice totals — the same predicate the Money head-band (`MoneySummaryResponse.ar_outstanding`) and the Home "AR out" tile sum, and the same set the client portal will accept a card/ACH payment for. Money shown as owed is money the payment rail will take. This account only: no rollup of a national parent's divisions, which is not ruled (see `accounts_api/core/db.py::ar_summary`). Totals, not balances — an invoice has no `amount_paid` yet, so a partial payment cannot be recorded (vantage-docs D15 §3A). */
export const AccountArSummarySchema = z.object({
  account_id: z.string(),
  ar_outstanding: z.number(),
  ar_overdue: z.number(),
  oldest_due_date: z.string().nullable().optional(),
  outstanding_invoice_count: z.number().int(),
  overdue_invoice_count: z.number().int(),
});
export type AccountArSummary = z.infer<typeof AccountArSummarySchema>;

export const AccountConversationCreateSchema = z.object({
  account_id: z.uuid(),
  channel: z.enum(["CLIENT", "INTERNAL", "CORPORATE"]),
  recipient_contact_id: z.uuid().nullable().optional(),
  territory_id: z.uuid(),
});
export type AccountConversationCreate = z.infer<typeof AccountConversationCreateSchema>;

export const AccountConversationMessageResponseSchema = z.object({
  author_contact_id: z.uuid().nullable(),
  author_name: z.string().nullable(),
  author_user_id: z.uuid().nullable(),
  content: z.string(),
  conversation_id: z.uuid(),
  created_at: z.iso.datetime(),
  id: z.uuid(),
});
export type AccountConversationMessageResponse = z.infer<typeof AccountConversationMessageResponseSchema>;

export const AccountConversationReadSchema = z.object({
  through_message_id: z.uuid(),
});
export type AccountConversationRead = z.infer<typeof AccountConversationReadSchema>;

export const AccountConversationResponseSchema = z.object({
  account_id: z.uuid(),
  account_name: z.string().nullable(),
  channel: z.enum(["CLIENT", "INTERNAL", "CORPORATE"]),
  created_at: z.iso.datetime(),
  id: z.uuid(),
  recipient_contact_id: z.uuid().nullable(),
  recipient_contact_name: z.string().nullable(),
  territory_id: z.uuid(),
  territory_name: z.string().nullable(),
  unread_count: z.number().int(),
  writable: z.boolean(),
});
export type AccountConversationResponse = z.infer<typeof AccountConversationResponseSchema>;

export const AccountConversationSendSchema = z.object({
  content: z.string().min(1).max(10000),
});
export type AccountConversationSend = z.infer<typeof AccountConversationSendSchema>;

export const AccountCreateSchema = z.object({
  account_type: z.enum(["PROSPECT", "CLIENT"]).nullable().optional(),
  address_line1: z.string().nullable().optional(),
  address_line2: z.string().nullable().optional(),
  assigned_rep_id: z.string().nullable().optional(),
  city: z.string().nullable().optional(),
  company_name: z.string().min(1).max(255),
  contract_end_date: z.string().nullable().optional(),
  contract_start_date: z.string().nullable().optional(),
  do_not_contact: z.boolean().optional(),
  email: z.string().nullable().optional(),
  external_ref: z.string().nullable().optional(),
  industry: z.string().nullable().optional(),
  is_national: z.boolean().optional(),
  negotiated_checklist: z.record(z.string(), z.unknown()).nullable().optional(),
  parent_account_id: z.string().nullable().optional(),
  payment_terms: z.string().nullable().optional(),
  phone: z.string().nullable().optional(),
  segment: z.string().nullable().optional(),
  service_frequency: z.string().nullable().optional(),
  service_line: z.enum(["JANITORIAL", "OTHER_SERVICES", "BOTH"]).nullable().optional(),
  square_footage: z.number().int().nullable().optional(),
  state: z.string().nullable().optional(),
  status: z.enum(["PROSPECT", "ACTIVE"]).optional(),
  tax_id: z.string().max(32).nullable().optional(),
  territory_id: z.string(),
  website: z.string().nullable().optional(),
  zip: z.string().nullable().optional(),
});
export type AccountCreate = z.infer<typeof AccountCreateSchema>;

export const AccountHealthSchema = z.object({
  account_id: z.string(),
  delivery: z.number().nullable().optional(),
  inspection: z.number().nullable().optional(),
  insufficient_data: z.boolean(),
  problems: z.number(),
  score: z.number(),
  trend: z.enum(["up", "down", "flat"]),
});
export type AccountHealth = z.infer<typeof AccountHealthSchema>;

export const AccountHealthSummarySchema = z.object({
  insufficient_data: z.boolean(),
  score: z.number(),
  trend: z.enum(["up", "down", "flat"]),
});
export type AccountHealthSummary = z.infer<typeof AccountHealthSummarySchema>;

export const AccountHealthScoresResponseSchema = z.object({
  scores: z.record(z.string(), z.unknown()),
});
export type AccountHealthScoresResponse = z.infer<typeof AccountHealthScoresResponseSchema>;

export const AccountImportRowSchema = z.object({
  account_type: z.enum(["PROSPECT", "CLIENT"]).optional(),
  address_line1: z.string().nullable().optional(),
  address_line2: z.string().nullable().optional(),
  city: z.string().nullable().optional(),
  company_name: z.string().min(1).max(255),
  email: z.string().nullable().optional(),
  external_ref: z.string().nullable().optional(),
  industry: z.string().nullable().optional(),
  is_national: z.boolean().optional(),
  payment_terms: z.string().nullable().optional(),
  phone: z.string().nullable().optional(),
  state: z.string().nullable().optional(),
  territory_id: z.string().nullable().optional(),
  website: z.string().nullable().optional(),
  zip: z.string().nullable().optional(),
});
export type AccountImportRow = z.infer<typeof AccountImportRowSchema>;

export const AccountImportRequestSchema = z.object({
  commit: z.boolean().optional(),
  rows: z.array(AccountImportRowSchema),
});
export type AccountImportRequest = z.infer<typeof AccountImportRequestSchema>;

export const AccountImportRowResultSchema = z.object({
  account_id: z.string().nullable().optional(),
  action: z.enum(["CREATE", "UPDATE", "ERROR"]),
  company_name: z.string(),
  error: z.string().nullable().optional(),
  row: z.number().int(),
});
export type AccountImportRowResult = z.infer<typeof AccountImportRowResultSchema>;

export const AccountImportResponseSchema = z.object({
  committed: z.boolean(),
  created: z.number().int(),
  errors: z.number().int(),
  rows: z.array(AccountImportRowResultSchema),
  updated: z.number().int(),
});
export type AccountImportResponse = z.infer<typeof AccountImportResponseSchema>;

export const AccountRuleCreateSchema = z.object({
  rule_key: z.string().min(1).max(128),
  service_category: z.string().nullable().optional(),
  value_bool: z.boolean().nullable().optional(),
  value_numeric: z.number().nullable().optional(),
  value_text: z.string().nullable().optional(),
});
export type AccountRuleCreate = z.infer<typeof AccountRuleCreateSchema>;

export const AccountRuleResponseSchema = z.object({
  created_at: z.iso.datetime(),
  created_by_id: z.string().nullable(),
  id: z.string(),
  inherited: z.boolean().optional(),
  rule_key: z.string(),
  scope_id: z.string(),
  scope_type: z.string(),
  service_category: z.string().nullable(),
  source_account_id: z.string().nullable().optional(),
  source_company_name: z.string().nullable().optional(),
  territory_id: z.string(),
  updated_at: z.iso.datetime(),
  value_bool: z.boolean().nullable(),
  value_numeric: z.number().nullable(),
  value_text: z.string().nullable(),
});
export type AccountRuleResponse = z.infer<typeof AccountRuleResponseSchema>;

export const AccountRuleUpdateSchema = z.object({
  service_category: z.string().nullable().optional(),
  value_bool: z.boolean().nullable().optional(),
  value_numeric: z.number().nullable().optional(),
  value_text: z.string().nullable().optional(),
});
export type AccountRuleUpdate = z.infer<typeof AccountRuleUpdateSchema>;

export const AccountUpdateSchema = z.object({
  address_line1: z.string().nullable().optional(),
  address_line2: z.string().nullable().optional(),
  assigned_rep_id: z.string().nullable().optional(),
  city: z.string().nullable().optional(),
  company_name: z.string().min(1).max(255).nullable().optional(),
  contract_end_date: z.string().nullable().optional(),
  contract_start_date: z.string().nullable().optional(),
  do_not_contact: z.boolean().nullable().optional(),
  email: z.string().nullable().optional(),
  external_ref: z.string().nullable().optional(),
  inactive_reason: z.enum(["NON_PAYMENT", "SEASONAL", "RENOVATION", "OWNERSHIP_CHANGE", "OTHER"]).nullable().optional(),
  industry: z.string().nullable().optional(),
  is_national: z.boolean().nullable().optional(),
  negotiated_checklist: z.record(z.string(), z.unknown()).nullable().optional(),
  parent_account_id: z.string().nullable().optional(),
  payment_terms: z.string().nullable().optional(),
  phone: z.string().nullable().optional(),
  segment: z.string().nullable().optional(),
  service_frequency: z.string().nullable().optional(),
  service_line: z.enum(["JANITORIAL", "OTHER_SERVICES", "BOTH"]).nullable().optional(),
  square_footage: z.number().int().nullable().optional(),
  state: z.string().nullable().optional(),
  status: z.enum(["PROSPECT", "ACTIVE", "INACTIVE", "CHURNED"]).nullable().optional(),
  tax_id: z.string().max(32).nullable().optional(),
  territory_id: z.string().nullable().optional(),
  version: z.number().int(),
  website: z.string().nullable().optional(),
  zip: z.string().nullable().optional(),
});
export type AccountUpdate = z.infer<typeof AccountUpdateSchema>;

export const AccountsSummarySchema = z.object({
  accounts: z.number().int(),
  contacts: z.number().int(),
  locations: z.number().int(),
});
export type AccountsSummary = z.infer<typeof AccountsSummarySchema>;

export const ActivityCreateSchema = z.object({
  account_id: z.string().nullable().optional(),
  client_rating: z.number().min(1).max(10).nullable().optional(),
  client_seen: z.boolean().nullable().optional(),
  contact_id: z.string().nullable().optional(),
  deal_id: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
  inspection_id: z.string().nullable().optional(),
  location_id: z.string().nullable().optional(),
  quote_id: z.string().nullable().optional(),
  subcontractor_id: z.string().nullable().optional(),
  territory_id: z.string().nullable().optional(),
  title: z.string().min(1),
  type: z.string().min(1),
  work_order_id: z.string().nullable().optional(),
});
export type ActivityCreate = z.infer<typeof ActivityCreateSchema>;

export const ActivityResponseSchema = z.object({
  account_id: z.string().nullable(),
  client_rating: z.number().nullable().optional(),
  client_seen: z.boolean().nullable().optional(),
  contact_id: z.string().nullable(),
  created_at: z.iso.datetime(),
  deal_id: z.string().nullable(),
  deal_title: z.string().nullable().optional(),
  description: z.string().nullable(),
  fsm_rating: z.number().nullable().optional(),
  id: z.string(),
  inspection_id: z.string().nullable().optional(),
  location_id: z.string().nullable().optional(),
  location_name: z.string().nullable().optional(),
  quote_id: z.string().nullable().optional(),
  subcontractor_id: z.string().nullable(),
  territory_id: z.string(),
  title: z.string(),
  type: z.string(),
  user_id: z.string().nullable(),
  user_name: z.string().nullable().optional(),
  work_order_id: z.string().nullable(),
});
export type ActivityResponse = z.infer<typeof ActivityResponseSchema>;

export const AssistantConfirmRequestSchema = z.object({
  action_id: z.string(),
  approved: z.boolean(),
});
export type AssistantConfirmRequest = z.infer<typeof AssistantConfirmRequestSchema>;

export const AssistantConversationCreateSchema = z.object({
  model_key: z.string().nullable().optional(),
});
export type AssistantConversationCreate = z.infer<typeof AssistantConversationCreateSchema>;

export const AssistantConversationResponseSchema = z.object({
  conversation_id: z.string(),
  created_at: z.number().int(),
  model_key: z.string(),
  title: z.string().optional(),
  turn_count: z.number().int().optional(),
  updated_at: z.number().int(),
});
export type AssistantConversationResponse = z.infer<typeof AssistantConversationResponseSchema>;

export const AssistantMessageResponseSchema = z.object({
  content: z.unknown(),
  created_at: z.number().int(),
  model_key: z.string().optional(),
  role: z.string(),
  seq: z.number().int(),
});
export type AssistantMessageResponse = z.infer<typeof AssistantMessageResponseSchema>;

export const AssistantConversationDetailResponseSchema = z.object({
  conversation: AssistantConversationResponseSchema,
  messages: z.array(AssistantMessageResponseSchema).optional(),
});
export type AssistantConversationDetailResponse = z.infer<typeof AssistantConversationDetailResponseSchema>;

/** One entry of the server-governed model allowlist, role-filtered for the caller. `key` is the only model identifier that crosses the wire. */
export const AssistantModelResponseSchema = z.object({
  default: z.boolean().optional(),
  display_name: z.string(),
  key: z.string(),
});
export type AssistantModelResponse = z.infer<typeof AssistantModelResponseSchema>;

export const AssistantTurnCreateSchema = z.object({
  conversation_id: z.string(),
  message: z.string().min(1).max(8000),
  model_key: z.string().nullable().optional(),
});
export type AssistantTurnCreate = z.infer<typeof AssistantTurnCreateSchema>;

export const AssistantTurnResponseSchema = z.object({
  conversation_id: z.string(),
  status: z.string(),
  turn_id: z.string(),
});
export type AssistantTurnResponse = z.infer<typeof AssistantTurnResponseSchema>;

export const AssistantTurnStatusResponseSchema = z.object({
  conversation_id: z.string(),
  events: z.array(z.record(z.string(), z.unknown())).optional(),
  status: z.string(),
  turn_id: z.string(),
});
export type AssistantTurnStatusResponse = z.infer<typeof AssistantTurnStatusResponseSchema>;

export const AttentionQueueItemSchema = z.object({
  account: z.string().nullable(),
  action_href: z.string(),
  action_label: z.string(),
  age_days: z.number().int(),
  id: z.string(),
  kind: z.string(),
  severity: z.enum(["critical", "warning", "info"]),
  title: z.string(),
});
export type AttentionQueueItem = z.infer<typeof AttentionQueueItemSchema>;

export const AuditLogItemSchema = z.object({
  acted_by_user_id: z.string().nullable(),
  action: z.string(),
  actor_email: z.string().nullable(),
  actor_id: z.string().nullable(),
  actor_type: z.string().nullable(),
  created_at: z.iso.datetime(),
  diff: z.unknown(),
  id: z.number().int(),
  ip_address: z.string().nullable(),
  record_id: z.string(),
  table_name: z.string(),
  territory_id: z.string().nullable(),
});
export type AuditLogItem = z.infer<typeof AuditLogItemSchema>;

export const AuthEventItemSchema = z.object({
  challenge_type: z.string().nullable(),
  cognito_sub: z.string(),
  details: z.unknown(),
  email: z.string(),
  event_type: z.string(),
  id: z.number().int(),
  ip_address: z.string().nullable(),
  occurred_at: z.iso.datetime(),
  portal: z.string().nullable(),
  user_agent: z.string().nullable(),
});
export type AuthEventItem = z.infer<typeof AuthEventItemSchema>;

export const AwardedUnquotedResponseSchema = z.object({
  account_id: z.string().nullable().optional(),
  account_name: z.string().nullable().optional(),
  awarded_amount: z.number(),
  awarded_bid_id: z.string(),
  bid_notes: z.string().nullable().optional(),
  bid_request_id: z.string(),
  description: z.string().nullable().optional(),
  estimated_hours: z.number().nullable().optional(),
  location_id: z.string().nullable().optional(),
  location_name: z.string().nullable().optional(),
  proposed_end: z.iso.datetime().nullable().optional(),
  proposed_start: z.iso.datetime().nullable().optional(),
  recurring: z.boolean().optional(),
  request_number: z.string(),
  service_category: z.string().nullable().optional(),
  service_category_id: z.string().nullable().optional(),
  service_category_name: z.string().nullable().optional(),
  service_frequency: z.string().nullable().optional(),
  subcontractor_name: z.string().nullable().optional(),
  title: z.string(),
});
export type AwardedUnquotedResponse = z.infer<typeof AwardedUnquotedResponseSchema>;

/** Corporate releasing the subcontractor half when a sub goes silent (J3). `reason` is required and non-blank because `bids_override_is_attributed` is a CHECK — an override with no reason cannot be stored at all, so refusing it here turns a constraint violation into a legible 422. */
export const BidAcceptanceOverrideSchema = z.object({
  reason: z.string().min(1).max(1000),
});
export type BidAcceptanceOverride = z.infer<typeof BidAcceptanceOverrideSchema>;

/** Optional body on POST /bid-requests/{br}/bids/{bid}/reject (#502). The reason is recorded in the AUDIT entry, not on the bid. `bids.notes` is the subcontractor's own note on their bid and there is no rejection-reason column, so writing it to the row would overwrite their text with ours. */
export const BidRejectSchema = z.object({
  reason: z.string().max(1000).nullable().optional(),
});
export type BidReject = z.infer<typeof BidRejectSchema>;

export const BidRequestCreateSchema = z.object({
  account_id: z.string().nullable().optional(),
  budget_max: z.number().nullable().optional(),
  budget_min: z.number().nullable().optional(),
  deadline: z.iso.datetime().nullable().optional(),
  description: z.string().nullable().optional(),
  location_id: z.string().nullable().optional(),
  notes: z.string().nullable().optional(),
  quote_id: z.string().nullable().optional(),
  quote_item_id: z.string().nullable().optional(),
  quote_photo_ids: z.array(z.string()).optional(),
  recurring: z.boolean().optional(),
  scheduled_end: z.iso.datetime().nullable().optional(),
  scheduled_start: z.iso.datetime().nullable().optional(),
  service_category_id: z.string().nullable().optional(),
  service_frequency: z.enum(["DAILY", "WEEKLY", "BIWEEKLY", "MONTHLY", "QUARTERLY", "YEARLY"]).nullable().optional(),
  territory_id: z.string().nullable().optional(),
  title: z.string().min(1).max(255),
});
export type BidRequestCreate = z.infer<typeof BidRequestCreateSchema>;

export const BidRequestDistributionSummarySchema = z.object({
  batch_id: z.string(),
  budget_max: z.number().nullable().optional(),
  budget_min: z.number().nullable().optional(),
  created_at: z.iso.datetime(),
  created_by_id: z.string().nullable(),
  created_by_name: z.string().nullable(),
  deadline: z.iso.datetime().nullable(),
  division_count: z.number().int(),
  national_account_id: z.string(),
  national_account_name: z.string().nullable(),
  recurring: z.boolean(),
  request_count: z.number().int(),
  response_count: z.number().int(),
  scheduled_end: z.iso.datetime().nullable().optional(),
  scheduled_start: z.iso.datetime().nullable().optional(),
  service_frequency: z.string().nullable(),
  status: z.string(),
  territory_count: z.number().int(),
  title: z.string().nullable(),
});
export type BidRequestDistributionSummary = z.infer<typeof BidRequestDistributionSummarySchema>;

/** Many subs in one call (#167). Separate from BidRequestInvitationCreate rather than widening it, so the single-invite response stays an object -- the live frontend reads `.subcontractor_id` off it. */
export const BidRequestInvitationBulkCreateSchema = z.object({
  subcontractor_ids: z.array(z.string()),
});
export type BidRequestInvitationBulkCreate = z.infer<typeof BidRequestInvitationBulkCreateSchema>;

export const BidRequestInvitationCreateSchema = z.object({
  subcontractor_id: z.string(),
});
export type BidRequestInvitationCreate = z.infer<typeof BidRequestInvitationCreateSchema>;

export const BidRequestInvitationResponseSchema = z.object({
  bid_request_id: z.string(),
  delivered_at: z.iso.datetime().nullable().optional(),
  delivery_error: z.string().nullable().optional(),
  delivery_status: z.string().optional(),
  id: z.string(),
  intent_at: z.iso.datetime().nullable().optional(),
  intent_reason: z.string().nullable().optional(),
  intent_status: z.string().nullable().optional(),
  invited_at: z.iso.datetime(),
  last_reminded_at: z.iso.datetime().nullable().optional(),
  subcontractor_id: z.string(),
  subcontractor_name: z.string().nullable().optional(),
  territory_id: z.string(),
  viewed_at: z.iso.datetime().nullable(),
});
export type BidRequestInvitationResponse = z.infer<typeof BidRequestInvitationResponseSchema>;

/** The outcome of a publish, itemised rather than summarised. A count alone cannot distinguish "nobody was invited" from "everyone opted out" from "we hold no email for any of them" -- and those need different actions from the person who pressed the button. Notification failures are reported here rather than raised, because the request DID go live; failing the call would misreport that. */
export const BidRequestPublishResponseSchema = z.object({
  failed: z.array(z.string()).optional(),
  id: z.string(),
  invited_subcontractor_count: z.number().int(),
  notified: z.array(z.string()).optional(),
  skipped_no_email: z.array(z.string()).optional(),
  skipped_opted_out: z.array(z.string()).optional(),
  status: z.string(),
});
export type BidRequestPublishResponse = z.infer<typeof BidRequestPublishResponseSchema>;

export const BidRequestResponseSchema = z.object({
  account_id: z.string().nullable(),
  account_is_national: z.boolean().optional(),
  account_name: z.string().nullable().optional(),
  awarded_to_name: z.string().nullable().optional(),
  budget_max: z.number().nullable(),
  budget_min: z.number().nullable(),
  closeout_required: z.boolean().optional(),
  created_at: z.iso.datetime(),
  created_by_id: z.string().nullable(),
  created_by_name: z.string().nullable().optional(),
  deadline: z.iso.datetime().nullable(),
  description: z.string().nullable(),
  distribution_batch_id: z.string().nullable().optional(),
  id: z.string(),
  location_id: z.string().nullable(),
  location_name: z.string().nullable().optional(),
  location_square_footage: z.number().nullable().optional(),
  notes: z.string().nullable(),
  quote_id: z.string().nullable().optional(),
  quote_item_id: z.string().nullable().optional(),
  recurring: z.boolean(),
  request_number: z.string(),
  response_quote_id: z.string().nullable().optional(),
  response_quote_status: z.string().nullable().optional(),
  response_quote_total: z.number().nullable().optional(),
  scheduled_end: z.iso.datetime().nullable(),
  scheduled_start: z.iso.datetime().nullable(),
  service_category_id: z.string().nullable(),
  service_category_name: z.string().nullable().optional(),
  service_frequency: z.string().nullable().optional(),
  status: z.string(),
  territory_id: z.string(),
  territory_name: z.string().nullable().optional(),
  title: z.string(),
  updated_at: z.iso.datetime(),
  updated_by_id: z.string().nullable(),
  updated_by_name: z.string().nullable().optional(),
  version: z.number().int(),
  work_order_id: z.string().nullable(),
});
export type BidRequestResponse = z.infer<typeof BidRequestResponseSchema>;

export const BidRequestUpdateSchema = z.object({
  budget_max: z.number().nullable().optional(),
  budget_min: z.number().nullable().optional(),
  deadline: z.iso.datetime().nullable().optional(),
  description: z.string().nullable().optional(),
  location_id: z.string().nullable().optional(),
  notes: z.string().nullable().optional(),
  recurring: z.boolean().nullable().optional(),
  reopen_reason: z.string().min(1).max(1000).nullable().optional(),
  scheduled_end: z.iso.datetime().nullable().optional(),
  scheduled_start: z.iso.datetime().nullable().optional(),
  service_category_id: z.string().nullable().optional(),
  service_frequency: z.enum(["DAILY", "WEEKLY", "BIWEEKLY", "MONTHLY", "QUARTERLY", "YEARLY"]).nullable().optional(),
  status: z.enum(["DRAFT", "OPEN", "CLOSED", "AWARDED", "CANCELLED"]).nullable().optional(),
  title: z.string().min(1).max(255).nullable().optional(),
  version: z.number().int(),
});
export type BidRequestUpdate = z.infer<typeof BidRequestUpdateSchema>;

export const BidResponseSchema = z.object({
  acceptance_expires_at: z.iso.datetime().nullable().optional(),
  acceptance_overridden_at: z.iso.datetime().nullable().optional(),
  acceptance_overridden_by_id: z.string().nullable().optional(),
  acceptance_overridden_by_name: z.string().nullable().optional(),
  acceptance_override_reason: z.string().nullable().optional(),
  acceptance_status: z.string().optional(),
  accepted_at: z.iso.datetime().nullable().optional(),
  amount: z.number(),
  below_floor: z.boolean().nullable().optional(),
  bid_request_id: z.string(),
  declined_at: z.iso.datetime().nullable().optional(),
  estimated_hours: z.number().nullable(),
  floor_margin_pct: z.number().nullable().optional(),
  id: z.string(),
  is_awarded: z.boolean(),
  margin_pct: z.number().nullable().optional(),
  notes: z.string().nullable(),
  proposed_end: z.iso.datetime().nullable(),
  proposed_start: z.iso.datetime().nullable(),
  reviewed_at: z.iso.datetime().nullable(),
  reviewed_by_id: z.string().nullable(),
  status: z.string(),
  subcontractor_id: z.string(),
  subcontractor_name: z.string().nullable().optional(),
  submitted_at: z.iso.datetime(),
  territory_id: z.string(),
});
export type BidResponse = z.infer<typeof BidResponseSchema>;

/** One id a bulk action did not act on, and why. The reasons are a CLOSED set so the caller can branch on them: `not-found` (no such row, or none the caller may read), `not-allowed` (the row is there and readable but this caller may not write it), `invalid-status` (the row is there but its current state forbids the transition). An untyped dict here would put the vocabulary in a docstring, where the generated spec cannot carry it and nothing checks a typo. */
export const BulkFailureSchema = z.object({
  id: z.string(),
  reason: z.enum(["not-found", "not-allowed", "invalid-status"]),
});
export type BulkFailure = z.infer<typeof BulkFailureSchema>;

export const BulkActionResponseSchema = z.object({
  failed: z.array(BulkFailureSchema),
  succeeded: z.array(z.string()),
});
export type BulkActionResponse = z.infer<typeof BulkActionResponseSchema>;

/** The `{ ids }` body shared by bulk actions — cap 50 (a UI multi-select, not an import). Pairs with BulkActionResponse: `succeeded` ids + per-id `failed` `{id, reason}`. */
export const BulkIdsRequestSchema = z.object({
  ids: z.array(z.string()),
});
export type BulkIdsRequest = z.infer<typeof BulkIdsRequestSchema>;

export const CalendarEventSchema = z.object({
  accountId: z.string().nullable().optional(),
  accountName: z.string().nullable().optional(),
  allDay: z.boolean().nullable().optional(),
  color: z.string().nullable().optional(),
  end: z.string().nullable().optional(),
  entityId: z.string(),
  id: z.string(),
  start: z.string(),
  status: z.string().nullable().optional(),
  title: z.string(),
  type: z.enum(["work_order", "inspection", "recurring_schedule", "task"]),
});
export type CalendarEvent = z.infer<typeof CalendarEventSchema>;

export const ChangeOrderCreateSchema = z.object({
  amount: z.number().gt(0),
  client_billable: z.boolean().optional(),
  contractor_cost: z.number().nullable().optional(),
  description: z.string().nullable().optional(),
  po_number: z.string().nullable().optional(),
  title: z.string().min(1).max(255),
});
export type ChangeOrderCreate = z.infer<typeof ChangeOrderCreateSchema>;

export const ChangeOrderResponseSchema = z.object({
  change_order_status: z.string(),
  client_billable: z.boolean(),
  client_signed_at: z.iso.datetime().nullable().optional(),
  client_signed_by: z.string().nullable().optional(),
  invoice_id: z.string().nullable().optional(),
  parent_work_order_id: z.string(),
  pending_approval: z.boolean().optional(),
  vendor_bill_id: z.string().nullable().optional(),
  work_order_id: z.string(),
});
export type ChangeOrderResponse = z.infer<typeof ChangeOrderResponseSchema>;

export const ChangeOrderSignRequestSchema = z.object({
  signature_path: z.string().nullable().optional(),
  signed_by: z.string().min(1).max(255),
});
export type ChangeOrderSignRequest = z.infer<typeof ChangeOrderSignRequestSchema>;

export const ChecklistTemplateAttachmentCreateSchema = z.object({
  recurring_schedule_id: z.string().nullable().optional(),
  work_order_id: z.string().nullable().optional(),
});
export type ChecklistTemplateAttachmentCreate = z.infer<typeof ChecklistTemplateAttachmentCreateSchema>;

export const ChecklistTemplateAttachmentResponseSchema = z.object({
  checklist_template_id: z.string(),
  created_at: z.iso.datetime(),
  created_by_id: z.string().nullable(),
  id: z.string(),
  recurring_schedule_id: z.string().nullable(),
  territory_id: z.string(),
  work_order_id: z.string().nullable(),
});
export type ChecklistTemplateAttachmentResponse = z.infer<typeof ChecklistTemplateAttachmentResponseSchema>;

export const ChecklistTemplateItemCreateSchema = z.object({
  label: z.string().min(1),
  requires_note: z.boolean().optional(),
  requires_photo: z.boolean().optional(),
  sort_order: z.number().int().optional(),
});
export type ChecklistTemplateItemCreate = z.infer<typeof ChecklistTemplateItemCreateSchema>;

export const ChecklistTemplateCreateSchema = z.object({
  items: z.array(ChecklistTemplateItemCreateSchema).optional(),
  name: z.string().min(1).max(255),
  territory_id: z.string(),
});
export type ChecklistTemplateCreate = z.infer<typeof ChecklistTemplateCreateSchema>;

export const ChecklistTemplateItemResponseSchema = z.object({
  id: z.string(),
  label: z.string(),
  requires_note: z.boolean(),
  requires_photo: z.boolean(),
  sort_order: z.number().int(),
  template_id: z.string(),
});
export type ChecklistTemplateItemResponse = z.infer<typeof ChecklistTemplateItemResponseSchema>;

export const ChecklistTemplateResponseSchema = z.object({
  created_at: z.iso.datetime(),
  created_by_id: z.string().nullable(),
  id: z.string(),
  is_active: z.boolean(),
  items: z.array(ChecklistTemplateItemResponseSchema).optional(),
  name: z.string(),
  territory_id: z.string(),
  updated_at: z.iso.datetime(),
});
export type ChecklistTemplateResponse = z.infer<typeof ChecklistTemplateResponseSchema>;

export const ChecklistTemplateUpdateSchema = z.object({
  is_active: z.boolean().nullable().optional(),
  items: z.array(ChecklistTemplateItemCreateSchema).nullable().optional(),
  name: z.string().min(1).max(255).nullable().optional(),
});
export type ChecklistTemplateUpdate = z.infer<typeof ChecklistTemplateUpdateSchema>;

export const ClientAccountConversationMessageResponseSchema = z.object({
  author_alias: z.string().nullable(),
  author_contact_id: z.uuid().nullable(),
  author_user_id: z.uuid().nullable(),
  content: z.string(),
  conversation_id: z.uuid(),
  created_at: z.iso.datetime(),
  id: z.uuid(),
});
export type ClientAccountConversationMessageResponse = z.infer<typeof ClientAccountConversationMessageResponseSchema>;

export const ClientAccountConversationResponseSchema = z.object({
  account_id: z.uuid(),
  account_name: z.string().nullable(),
  channel: z.string(),
  created_at: z.iso.datetime(),
  id: z.uuid(),
  recipient_contact_id: z.uuid().nullable(),
  recipient_contact_name: z.string().nullable(),
  territory_id: z.uuid(),
  unread_count: z.number().int(),
  writable: z.boolean(),
});
export type ClientAccountConversationResponse = z.infer<typeof ClientAccountConversationResponseSchema>;

/** One dated cell on the client-portal calendar (read-only month view, `/client/v1/calendar`). Two shapes share the model — the client's month is drawn from the contract's recurring service plus any one-off dated work, exactly as the iaprototype states: - **recurring service**, projected from an active recurring schedule's rule across the window: ``type='recurring_service'``, ``id='<schedule_id>:<YYYY-MM-DD>'`` (unique per night so the web can track each cell), no ``work_order_id`` (view-only — a client cannot open a schedule). - **a one-off dated work order**: ``type='work_order'``, ``id`` = the WO id, ``work_order_id`` and ``order_number`` set so the web links the cell to the WO detail. ``status`` reflects the night: a recurring visit is ``COMPLETED`` once its date is in the past (serviced) and ``SCHEDULED`` for today or later — matching the design's ✓-past / upcoming split; a work order carries its own status. No cost or subcontractor data is exposed (§4.4). */
export const ClientCalendarEventSchema = z.object({
  id: z.string(),
  order_number: z.string().nullable().optional(),
  scheduled_time: z.string().nullable().optional(),
  service_date: z.string(),
  status: z.string(),
  title: z.string(),
  type: z.enum(["recurring_service", "work_order"]),
  work_order_id: z.string().nullable().optional(),
});
export type ClientCalendarEvent = z.infer<typeof ClientCalendarEventSchema>;

/** What a CLIENT may declare when registering an attachment (#429). DELIBERATELY NOT `DocumentCreate`. That model carries `account_id`, `subcontractor_id`, `work_order_id`, `client_visible`, `document_type` and the rest of the CRM's link surface; every one of them is server-set or forbidden here, and a shared model would mean the only thing standing between a client and those fields is the handler remembering to ignore them. A separate model makes it structural: a field absent from the schema cannot be sent. `client_message_id` is REQUIRED. A client has no route to register a document that is not an attachment — that is the whole of the grant, and it matches the RLS arm, which refuses a row with a null link. */
export const ClientDocumentCreateSchema = z.object({
  client_message_id: z.string(),
  file_size: z.number().int().min(0),
  file_type: z.string().nullable().optional(),
  filename: z.string().min(1).max(500),
  original_name: z.string().nullable().optional(),
});
export type ClientDocumentCreate = z.infer<typeof ClientDocumentCreateSchema>;

/** A document as the client sees it — only client_visible docs are returned by RLS. The uploader (`subcontractor_id` / `uploaded_by_*`) and territory are not exposed. */
export const ClientDocumentResponseSchema = z.object({
  account_id: z.string().nullable(),
  created_at: z.iso.datetime(),
  description: z.string().nullable(),
  document_type: z.string().nullable(),
  expiry_date: z.string().nullable(),
  file_size: z.number().int().nullable(),
  file_type: z.string().nullable(),
  filename: z.string(),
  id: z.string(),
  original_name: z.string().nullable(),
  work_order_id: z.string().nullable(),
});
export type ClientDocumentResponse = z.infer<typeof ClientDocumentResponseSchema>;

/** The registered attachment plus its short-lived presigned PUT target. Carries `ClientDocumentResponse`, not `DocumentResponse` — the latter ships `subcontractor_id`, `client_visible` and `uploaded_by_type`, which are internal bookkeeping a portal user has no use for. */
export const ClientDocumentUploadResponseSchema = z.object({
  document: ClientDocumentResponseSchema,
  upload_url: z.string(),
});
export type ClientDocumentUploadResponse = z.infer<typeof ClientDocumentUploadResponseSchema>;

/** Client-submitted WO feedback. `submitted_by` / `contact_id` are server-set from the caller (never client-supplied). */
export const ClientFeedbackCreateSchema = z.object({
  comments: z.string().nullable().optional(),
  rating: z.number().int().min(1).max(5),
});
export type ClientFeedbackCreate = z.infer<typeof ClientFeedbackCreateSchema>;

/** An invoice line as the client sees it — price only, NO contractor_cost. */
export const ClientInvoiceLineResponseSchema = z.object({
  description: z.string(),
  id: z.string(),
  quantity: z.number(),
  service_category: z.string().nullable(),
  total_price: z.number(),
  unit_price: z.number(),
});
export type ClientInvoiceLineResponse = z.infer<typeof ClientInvoiceLineResponseSchema>;

/** An invoice as the client sees it. `cost_subtotal` + CRM audit/territory/internal (`path`, `document_id`, `version`) fields are not exposed. */
export const ClientInvoiceResponseSchema = z.object({
  account_id: z.string(),
  created_at: z.iso.datetime(),
  due_date: z.string().nullable(),
  id: z.string(),
  invoice_number: z.string(),
  lines: z.array(ClientInvoiceLineResponseSchema).nullable().optional(),
  notes: z.string().nullable(),
  paid_date: z.string().nullable(),
  payment_status: z.string().nullable().optional(),
  payment_terms: z.string().nullable(),
  po_number: z.string().nullable(),
  receipt_url: z.string().nullable().optional(),
  status: z.string(),
  subtotal: z.number().nullable(),
  tax: z.number().nullable(),
  total: z.number().nullable(),
  updated_at: z.iso.datetime(),
  work_order_id: z.string().nullable(),
});
export type ClientInvoiceResponse = z.infer<typeof ClientInvoiceResponseSchema>;

export const ClientMeResponseSchema = z.object({
  account_id: z.string(),
  account_name: z.string(),
  cognito_sub: z.string(),
  email: z.string().nullable(),
  first_name: z.string(),
  id: z.string(),
  last_name: z.string(),
  phone: z.string().nullable().optional(),
  portal_role: z.string(),
  site_address_line1: z.string().nullable().optional(),
  site_city: z.string().nullable().optional(),
  site_name: z.string().nullable().optional(),
  site_state: z.string().nullable().optional(),
  site_zip: z.string().nullable().optional(),
});
export type ClientMeResponse = z.infer<typeof ClientMeResponseSchema>;

/** PATCH /client/v1/dashboard/me — the caller's own contact details (#536). First name, last name and phone, the caller's own contacts row only. No email (the Cognito login identity), no account/site fields — those are the CRM's to manage. Ruled an ordinary audited edit by @ryanlacy1 on #536; both client roles may use it (the caller's own identity is not account data, which is what CLIENT_READER is read- only about). An explicit null phone clears it; an omitted field is unchanged. */
export const ClientMeUpdateSchema = z.object({
  first_name: z.string().min(1).max(255).nullable().optional(),
  last_name: z.string().min(1).max(255).nullable().optional(),
  phone: z.string().nullable().optional(),
});
export type ClientMeUpdate = z.infer<typeof ClientMeUpdateSchema>;

/** A document attached to a client message (`documents.client_message_id`). FOUR FIELDS, AND THAT IS THE POINT. No uploader, no user id, no `uploaded_by_type`: the anonymization ruling (#390) governs this object exactly as it governs the message that carries it, and a nested shape is precisely where an identity field gets added without anyone noticing. `tests/shared/test_client_portal_anonymization.py` inspects it for that reason. The bytes are fetched through the client-portal documents download flow. */
export const ClientMessageAttachmentSchema = z.object({
  file_size: z.number().int().nullable().optional(),
  file_type: z.string().nullable().optional(),
  filename: z.string(),
  id: z.string(),
});
export type ClientMessageAttachment = z.infer<typeof ClientMessageAttachmentSchema>;

export const ClientMessageCreateSchema = z.object({
  content: z.string().min(1),
  phase: z.enum(["BEFORE", "DURING", "AFTER"]).nullable().optional(),
  subject: z.string().nullable().optional(),
  work_order_id: z.string().nullable().optional(),
});
export type ClientMessageCreate = z.infer<typeof ClientMessageCreateSchema>;

export const ClientMessageResponseSchema = z.object({
  account_id: z.string(),
  attachments: z.array(ClientMessageAttachmentSchema).optional(),
  author_alias: z.string().nullable().optional(),
  contact_id: z.string().nullable(),
  content: z.string(),
  created_at: z.iso.datetime(),
  id: z.string(),
  is_from_client: z.boolean(),
  is_read: z.boolean(),
  phase: z.enum(["BEFORE", "DURING", "AFTER"]).nullable().optional(),
  sender_brand: z.string().nullable().optional(),
  subject: z.string().nullable(),
  user_id: z.string().nullable(),
  work_order_id: z.string().nullable(),
  work_order_number: z.string().nullable().optional(),
});
export type ClientMessageResponse = z.infer<typeof ClientMessageResponseSchema>;

export const ClientQuoteAcceptSchema = z.object({
  consent_version: z.string().nullable().optional(),
  reviewed_terms_sha256: z.string().nullable().optional(),
  signature_method: z.enum(["typed", "drawn"]).optional(),
  signature_path: z.string().nullable().optional(),
  signed_by: z.string().min(1).max(255),
});
export type ClientQuoteAccept = z.infer<typeof ClientQuoteAcceptSchema>;

/** Optional body on POST /client/v1/quotes/{id}/decline (#540 / PORT-065). Everything here is optional — a client who just wants out is never forced to justify it, and a bodyless POST keeps working exactly as before. The canned code is what makes declines aggregatable (count by category, not read one at a time); the free-text note is allowed alongside ANY code or none, not just OTHER. Unlike the office's bid-decline reason (BidReject, #524 — audit entry only), this one is stored on the quote and surfaced to the territory, because re-quote / drop-the-price / drop-the-account is decided from the quote itself. */
export const ClientQuoteDeclineSchema = z.object({
  reason: z.string().max(1000).nullable().optional(),
  reason_code: z.enum(["PRICE", "TIMING", "WENT_ELSEWHERE", "NO_LONGER_NEEDED", "OTHER"]).nullable().optional(),
});
export type ClientQuoteDecline = z.infer<typeof ClientQuoteDeclineSchema>;

/** A quote line as the client sees it — price only, NO contractor_cost / margin_pct. */
export const ClientQuoteItemResponseSchema = z.object({
  basis: z.string().nullable(),
  description: z.string(),
  id: z.string(),
  location_id: z.string().nullable().optional(),
  location_name: z.string().nullable().optional(),
  quantity: z.number(),
  service_category: z.string().nullable(),
  total_price: z.number(),
  unit_price: z.number(),
});
export type ClientQuoteItemResponse = z.infer<typeof ClientQuoteItemResponseSchema>;

export const ClientQuoteRequestCategorySchema = z.object({
  code: z.string(),
  label: z.string(),
});
export type ClientQuoteRequestCategory = z.infer<typeof ClientQuoteRequestCategorySchema>;

export const ClientQuoteRequestCreateSchema = z.object({
  access_notes: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
  location_id: z.string().nullable().optional(),
  requested_date: z.string().nullable().optional(),
  service_category: z.string().nullable().optional(),
  title: z.string().min(1).max(255),
});
export type ClientQuoteRequestCreate = z.infer<typeof ClientQuoteRequestCreateSchema>;

export const ClientQuoteRequestLocationSchema = z.object({
  account_id: z.string().nullable().optional(),
  account_name: z.string().nullable().optional(),
  address: z.string(),
  id: z.string(),
  name: z.string(),
});
export type ClientQuoteRequestLocation = z.infer<typeof ClientQuoteRequestLocationSchema>;

export const ClientQuoteRequestOptionsSchema = z.object({
  categories: z.array(ClientQuoteRequestCategorySchema),
  locations: z.array(ClientQuoteRequestLocationSchema),
});
export type ClientQuoteRequestOptions = z.infer<typeof ClientQuoteRequestOptionsSchema>;

/** The same row as `QuoteRequestResponse`, as a CLIENT PORTAL user is allowed to see it. A separate model rather than a filtered view of the staff one, because the difference is a privacy boundary and boundaries should be visible in the type. Two fields are missing and both were live leaks until #408: * `territory_name` - a routing label. @ryanlacy1's ruling on #390: portal users are not shown Corporate, territory names, office names or internal routing labels. * `created_by_name` - resolved through `user_display_name`, so a customer was receiving the STAFF MEMBER'S REAL NAME. That is exactly what #390 and migration 099 exist to prevent; it survived because this endpoint reads a projection shared with the CRM rather than one of its own. `account_name` and `location_name` stay: those are the CUSTOMER'S OWN account and site, which they named, and neither is staff identity nor routing. */
export const ClientQuoteRequestResponseSchema = z.object({
  access_notes: z.string().nullable().optional(),
  account_id: z.string(),
  account_name: z.string().nullable().optional(),
  contact_id: z.string().nullable(),
  created_at: z.iso.datetime(),
  created_by_id: z.string().nullable().optional(),
  description: z.string().nullable(),
  id: z.string(),
  location_id: z.string().nullable(),
  location_name: z.string().nullable().optional(),
  notes: z.string().nullable(),
  quote_available: z.boolean().optional(),
  quote_id: z.string().nullable(),
  quote_number: z.string().nullable().optional(),
  requested_date: z.string().nullable(),
  service_category: z.string().nullable(),
  service_category_label: z.string().nullable().optional(),
  source: z.string().nullable(),
  status: z.string(),
  territory_id: z.string(),
  title: z.string(),
  updated_at: z.iso.datetime(),
  version: z.number().int(),
});
export type ClientQuoteRequestResponse = z.infer<typeof ClientQuoteRequestResponseSchema>;

/** A quote as the client sees it. Cost/margin internals (`cost_subtotal`, `margin_pct`, per-line `contractor_cost`) and CRM audit/territory fields are not exposed. */
export const ClientQuoteResponseSchema = z.object({
  accepted_at: z.iso.datetime().nullable(),
  account_id: z.string(),
  contact_id: z.string().nullable(),
  converted_work_order_id: z.string().nullable().optional(),
  created_at: z.iso.datetime(),
  declined_at: z.iso.datetime().nullable(),
  declined_reason: z.string().nullable().optional(),
  declined_reason_code: z.string().nullable().optional(),
  description: z.string().nullable(),
  id: z.string(),
  items: z.array(ClientQuoteItemResponseSchema).nullable().optional(),
  location_id: z.string().nullable(),
  notes: z.string().nullable(),
  quote_number: z.string(),
  recurring: z.boolean(),
  reviewed_terms_sha256: z.string().nullable().optional(),
  sent_at: z.iso.datetime().nullable(),
  service_category: z.string().nullable(),
  signed_at: z.iso.datetime().nullable(),
  signed_by: z.string().nullable(),
  status: z.string(),
  subtotal: z.number().nullable(),
  tax: z.number().nullable(),
  title: z.string(),
  total: z.number().nullable(),
  updated_at: z.iso.datetime(),
  valid_until: z.string().nullable(),
  viewed_at: z.iso.datetime().nullable(),
});
export type ClientQuoteResponse = z.infer<typeof ClientQuoteResponseSchema>;

/** #168: an active service-request category the client SR form feeds its dropdown from, so the web stops hardcoding the list (it must match `service_request_categories` or the file 422s). */
export const ClientServiceRequestCategorySchema = z.object({
  code: z.string(),
  label: z.string(),
});
export type ClientServiceRequestCategory = z.infer<typeof ClientServiceRequestCategorySchema>;

/** A client files an SR for ITS OWN account — account/contact/reported_by are server-set, and the NBD→NAM vs LOCAL→territory routing is computed server-side (Flow C). */
export const ClientServiceRequestCreateSchema = z.object({
  category: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
  location_id: z.string().nullable().optional(),
  preferred_date: z.iso.datetime().nullable().optional(),
  priority: z.enum(["LOW", "MEDIUM", "HIGH", "URGENT"]).optional(),
  title: z.string().min(1).max(255),
});
export type ClientServiceRequestCreate = z.infer<typeof ClientServiceRequestCreateSchema>;

/** An SR as the client sees it. The internal routing (`assigned_to_id`, `pending_approval`) and CRM audit/territory fields are not exposed; the outcome (`resolution_notes`, `resolved_at`) is. */
export const ClientServiceRequestResponseSchema = z.object({
  account_id: z.string(),
  category: z.string().nullable(),
  category_label: z.string().nullable().optional(),
  contact_id: z.string().nullable(),
  created_at: z.iso.datetime(),
  description: z.string().nullable(),
  id: z.string(),
  location_id: z.string().nullable(),
  preferred_date: z.iso.datetime().nullable(),
  priority: z.string(),
  reported_by: z.string().nullable(),
  request_number: z.string(),
  resolution_notes: z.string().nullable(),
  resolved_at: z.iso.datetime().nullable(),
  status: z.string(),
  title: z.string(),
  updated_at: z.iso.datetime(),
  work_order_id: z.string().nullable(),
});
export type ClientServiceRequestResponse = z.infer<typeof ClientServiceRequestResponseSchema>;

/** A sent invoice linked to the client's work order — number, status and total, for the WO's "Linked records" back-link (the client already sees these on its Invoices tab; cost/margin internals are not carried). DRAFT invoices (pre-issuance internal state) are never included, so the row appears exactly when the WO reaches the "Invoiced" step. */
export const ClientWorkOrderInvoiceRefSchema = z.object({
  id: z.string(),
  invoice_number: z.string(),
  status: z.string(),
  total: z.number().nullable(),
});
export type ClientWorkOrderInvoiceRef = z.infer<typeof ClientWorkOrderInvoiceRefSchema>;

/** A work order as the client sees it — schedule/status only. Cost internals (`estimated_cost`/`actual_cost`/`not_to_exceed`, lines), the assigned `subcontractor_id` (OIP's vendor), and CRM audit/territory fields are not exposed. Linked records (the iaprototype client WO surface): the originating quote — resolved to its number + title so the web renders "QT-00001 — <name>" — plus any SENT invoice(s) for the WO. Both point only at records the client is already entitled to see: a WO's source quote was accepted, and invoices appear once issued (DRAFT is hidden). */
export const ClientWorkOrderResponseSchema = z.object({
  account_id: z.string(),
  completed_date: z.iso.datetime().nullable(),
  created_at: z.iso.datetime(),
  description: z.string().nullable(),
  id: z.string(),
  invoices: z.array(ClientWorkOrderInvoiceRefSchema),
  location_address_line1: z.string().nullable().optional(),
  location_city: z.string().nullable().optional(),
  location_id: z.string().nullable(),
  location_name: z.string().nullable().optional(),
  location_state: z.string().nullable().optional(),
  location_zip: z.string().nullable().optional(),
  notes: z.string().nullable(),
  order_number: z.string(),
  order_type: z.string().nullable(),
  priority: z.string().nullable(),
  scheduled_date: z.iso.datetime().nullable(),
  scheduled_duration: z.number().int().nullable(),
  scheduled_time: z.string().nullable(),
  service_category: z.string().nullable(),
  service_category_label: z.string().nullable().optional(),
  source_quote_id: z.string().nullable(),
  source_quote_name: z.string().nullable(),
  source_quote_number: z.string().nullable(),
  started_at: z.iso.datetime().nullable(),
  status: z.string(),
  title: z.string(),
  updated_at: z.iso.datetime(),
  week_start: z.string().nullable(),
});
export type ClientWorkOrderResponse = z.infer<typeof ClientWorkOrderResponseSchema>;

export const CollectionItemCreateSchema = z.object({
  code: z.string().min(1),
  color: z.string().nullable().optional(),
  days: z.number().int().nullable().optional(),
  label: z.string().min(1),
  sort_order: z.number().int().optional(),
});
export type CollectionItemCreate = z.infer<typeof CollectionItemCreateSchema>;

export const CollectionItemUpdateSchema = z.object({
  color: z.string().nullable().optional(),
  days: z.number().int().nullable().optional(),
  is_active: z.boolean().nullable().optional(),
  label: z.string().min(1).nullable().optional(),
  sort_order: z.number().int().nullable().optional(),
});
export type CollectionItemUpdate = z.infer<typeof CollectionItemUpdateSchema>;

export const ConsolidatedInvoiceChildCreateSchema = z.object({
  child_invoice_id: z.string(),
});
export type ConsolidatedInvoiceChildCreate = z.infer<typeof ConsolidatedInvoiceChildCreateSchema>;

export const ConsolidatedInvoiceChildResponseSchema = z.object({
  child_invoice_id: z.string(),
  child_territory_id: z.string(),
  consolidated_invoice_id: z.string(),
  id: z.string(),
  territory_id: z.string(),
});
export type ConsolidatedInvoiceChildResponse = z.infer<typeof ConsolidatedInvoiceChildResponseSchema>;

export const ConsolidatedInvoiceCreateSchema = z.object({
  national_account_id: z.string(),
});
export type ConsolidatedInvoiceCreate = z.infer<typeof ConsolidatedInvoiceCreateSchema>;

export const ConsolidatedInvoiceResponseSchema = z.object({
  created_at: z.iso.datetime(),
  id: z.string(),
  national_account_id: z.string(),
  status: z.string(),
  territory_id: z.string(),
  total: z.number(),
});
export type ConsolidatedInvoiceResponse = z.infer<typeof ConsolidatedInvoiceResponseSchema>;

export const ConsolidatedInvoiceUpdateSchema = z.object({
  status: z.enum(["draft", "issued", "paid", "void"]),
});
export type ConsolidatedInvoiceUpdate = z.infer<typeof ConsolidatedInvoiceUpdateSchema>;

export const ContactCreateSchema = z.object({
  account_id: z.string(),
  do_not_contact: z.boolean().optional(),
  email: z.string().nullable().optional(),
  first_name: z.string().min(1).max(255),
  is_primary: z.boolean().optional(),
  last_name: z.string().min(1).max(255),
  location_id: z.string().nullable().optional(),
  mobile: z.string().nullable().optional(),
  notes: z.string().nullable().optional(),
  phone: z.string().nullable().optional(),
  title: z.string().nullable().optional(),
});
export type ContactCreate = z.infer<typeof ContactCreateSchema>;

/** Body for `POST /contacts/{id}/portal-access` (grant) and PATCH (re-role). */
export const ContactPortalGrantSchema = z.object({
  portal_role: z.enum(["CLIENT_WRITER", "CLIENT_READER"]),
});
export type ContactPortalGrant = z.infer<typeof ContactPortalGrantSchema>;

export const ContactResponseSchema = z.object({
  accepted_at: z.iso.datetime().nullable().optional(),
  account_do_not_contact: z.boolean().optional(),
  account_id: z.string(),
  account_is_national: z.boolean().optional(),
  account_name: z.string().nullable().optional(),
  cognito_sub: z.string().nullable(),
  created_at: z.iso.datetime(),
  created_by_id: z.string().nullable().optional(),
  created_by_name: z.string().nullable().optional(),
  do_not_contact: z.boolean(),
  effective_do_not_contact: z.boolean().optional(),
  email: z.string().nullable(),
  first_name: z.string(),
  has_portal_access: z.boolean(),
  id: z.string(),
  invited_at: z.iso.datetime().nullable().optional(),
  is_primary: z.boolean(),
  last_name: z.string(),
  location_do_not_contact: z.boolean().optional(),
  location_id: z.string().nullable(),
  location_name: z.string().nullable().optional(),
  mobile: z.string().nullable(),
  notes: z.string().nullable(),
  phone: z.string().nullable(),
  portal_role: z.string().nullable(),
  portal_status: z.string().optional(),
  territory_id: z.string(),
  territory_name: z.string().nullable().optional(),
  title: z.string().nullable(),
  updated_at: z.iso.datetime(),
  updated_by_id: z.string().nullable().optional(),
  updated_by_name: z.string().nullable().optional(),
});
export type ContactResponse = z.infer<typeof ContactResponseSchema>;

export const ContactUpdateSchema = z.object({
  account_id: z.string().nullable().optional(),
  do_not_contact: z.boolean().nullable().optional(),
  email: z.string().nullable().optional(),
  first_name: z.string().min(1).max(255).nullable().optional(),
  is_primary: z.boolean().nullable().optional(),
  last_name: z.string().min(1).max(255).nullable().optional(),
  location_id: z.string().nullable().optional(),
  mobile: z.string().nullable().optional(),
  notes: z.string().nullable().optional(),
  phone: z.string().nullable().optional(),
  title: z.string().nullable().optional(),
});
export type ContactUpdate = z.infer<typeof ContactUpdateSchema>;

/** Review corrections apply to the new quote, preserving the submitted request. */
export const ConvertQuoteRequestSchema = z.object({
  description: z.string().min(1).nullable().optional(),
  location_id: z.string().nullable().optional(),
  service_category: z.string().nullable().optional(),
});
export type ConvertQuoteRequest = z.infer<typeof ConvertQuoteRequestSchema>;

export const CorporateEntityCreateSchema = z.object({
  name: z.string().min(1).max(255),
});
export type CorporateEntityCreate = z.infer<typeof CorporateEntityCreateSchema>;

export const CorporateEntityResponseSchema = z.object({
  created_at: z.iso.datetime(),
  id: z.string(),
  name: z.string(),
  stripe_connect_status: z.string().nullable(),
});
export type CorporateEntityResponse = z.infer<typeof CorporateEntityResponseSchema>;

/** Onboarding-link response for POST /corporate-entities/{id}/stripe-connect. */
export const CorporateEntityStripeConnectResponseSchema = z.object({
  onboarding_url: z.string(),
  stripe_connect_status: z.string(),
});
export type CorporateEntityStripeConnectResponse = z.infer<typeof CorporateEntityStripeConnectResponseSchema>;

export const CorporateTerritoryStatSchema = z.object({
  account_count: z.number().int(),
  billed_revenue: z.number(),
  code: z.string(),
  contract_value: z.number(),
  id: z.string(),
  name: z.string(),
  open_service_requests: z.number().int(),
  pipeline_value: z.number(),
  user_count: z.number().int(),
  work_order_count: z.number().int(),
});
export type CorporateTerritoryStat = z.infer<typeof CorporateTerritoryStatSchema>;

export const CrmClientMessageCreateSchema = z.object({
  account_id: z.string(),
  content: z.string().min(1),
  subject: z.string().nullable().optional(),
  work_order_id: z.string().nullable().optional(),
});
export type CrmClientMessageCreate = z.infer<typeof CrmClientMessageCreateSchema>;

export const CrmClientMessageResponseSchema = z.object({
  account_id: z.string(),
  account_name: z.string().nullable().optional(),
  author_is_corporate: z.boolean().optional(),
  contact_id: z.string().nullable(),
  contact_name: z.string().nullable().optional(),
  content: z.string(),
  created_at: z.iso.datetime(),
  id: z.string(),
  is_from_client: z.boolean(),
  is_read: z.boolean(),
  phase: z.enum(["BEFORE", "DURING", "AFTER"]).nullable().optional(),
  subject: z.string().nullable(),
  user_id: z.string().nullable(),
  user_name: z.string().nullable().optional(),
  work_order_id: z.string().nullable(),
  work_order_number: z.string().nullable().optional(),
});
export type CrmClientMessageResponse = z.infer<typeof CrmClientMessageResponseSchema>;

export const CrmMessageCreateSchema = z.object({
  content: z.string().min(1),
  message_type: z.string().nullable().optional(),
  subcontractor_id: z.string(),
  subject: z.string().nullable().optional(),
  thread_id: z.string().nullable().optional(),
  work_order_id: z.string().nullable().optional(),
});
export type CrmMessageCreate = z.infer<typeof CrmMessageCreateSchema>;

export const CursorPage_AccountActivityResponse_Schema = z.object({
  next_cursor: z.string().nullable().optional(),
  results: z.array(AccountActivityResponseSchema),
});
export type CursorPage_AccountActivityResponse_ = z.infer<typeof CursorPage_AccountActivityResponse_Schema>;

export const CursorPage_ActivityResponse_Schema = z.object({
  next_cursor: z.string().nullable().optional(),
  results: z.array(ActivityResponseSchema),
});
export type CursorPage_ActivityResponse_ = z.infer<typeof CursorPage_ActivityResponse_Schema>;

export const CursorPage_AuditLogItem_Schema = z.object({
  next_cursor: z.string().nullable().optional(),
  results: z.array(AuditLogItemSchema),
});
export type CursorPage_AuditLogItem_ = z.infer<typeof CursorPage_AuditLogItem_Schema>;

export const CursorPage_AuthEventItem_Schema = z.object({
  next_cursor: z.string().nullable().optional(),
  results: z.array(AuthEventItemSchema),
});
export type CursorPage_AuthEventItem_ = z.infer<typeof CursorPage_AuthEventItem_Schema>;

export const CursorPage_AwardedUnquotedResponse_Schema = z.object({
  next_cursor: z.string().nullable().optional(),
  results: z.array(AwardedUnquotedResponseSchema),
});
export type CursorPage_AwardedUnquotedResponse_ = z.infer<typeof CursorPage_AwardedUnquotedResponse_Schema>;

export const CursorPage_BidRequestDistributionSummary_Schema = z.object({
  next_cursor: z.string().nullable().optional(),
  results: z.array(BidRequestDistributionSummarySchema),
});
export type CursorPage_BidRequestDistributionSummary_ = z.infer<typeof CursorPage_BidRequestDistributionSummary_Schema>;

export const CursorPage_BidRequestResponse_Schema = z.object({
  next_cursor: z.string().nullable().optional(),
  results: z.array(BidRequestResponseSchema),
});
export type CursorPage_BidRequestResponse_ = z.infer<typeof CursorPage_BidRequestResponse_Schema>;

export const CursorPage_ChecklistTemplateResponse_Schema = z.object({
  next_cursor: z.string().nullable().optional(),
  results: z.array(ChecklistTemplateResponseSchema),
});
export type CursorPage_ChecklistTemplateResponse_ = z.infer<typeof CursorPage_ChecklistTemplateResponse_Schema>;

export const CursorPage_ClientDocumentResponse_Schema = z.object({
  next_cursor: z.string().nullable().optional(),
  results: z.array(ClientDocumentResponseSchema),
});
export type CursorPage_ClientDocumentResponse_ = z.infer<typeof CursorPage_ClientDocumentResponse_Schema>;

export const CursorPage_ClientInvoiceResponse_Schema = z.object({
  next_cursor: z.string().nullable().optional(),
  results: z.array(ClientInvoiceResponseSchema),
});
export type CursorPage_ClientInvoiceResponse_ = z.infer<typeof CursorPage_ClientInvoiceResponse_Schema>;

export const CursorPage_ClientMessageResponse_Schema = z.object({
  next_cursor: z.string().nullable().optional(),
  results: z.array(ClientMessageResponseSchema),
});
export type CursorPage_ClientMessageResponse_ = z.infer<typeof CursorPage_ClientMessageResponse_Schema>;

export const CursorPage_ClientQuoteRequestResponse_Schema = z.object({
  next_cursor: z.string().nullable().optional(),
  results: z.array(ClientQuoteRequestResponseSchema),
});
export type CursorPage_ClientQuoteRequestResponse_ = z.infer<typeof CursorPage_ClientQuoteRequestResponse_Schema>;

export const CursorPage_ClientQuoteResponse_Schema = z.object({
  next_cursor: z.string().nullable().optional(),
  results: z.array(ClientQuoteResponseSchema),
});
export type CursorPage_ClientQuoteResponse_ = z.infer<typeof CursorPage_ClientQuoteResponse_Schema>;

export const CursorPage_ClientServiceRequestResponse_Schema = z.object({
  next_cursor: z.string().nullable().optional(),
  results: z.array(ClientServiceRequestResponseSchema),
});
export type CursorPage_ClientServiceRequestResponse_ = z.infer<typeof CursorPage_ClientServiceRequestResponse_Schema>;

export const CursorPage_ClientWorkOrderResponse_Schema = z.object({
  next_cursor: z.string().nullable().optional(),
  results: z.array(ClientWorkOrderResponseSchema),
});
export type CursorPage_ClientWorkOrderResponse_ = z.infer<typeof CursorPage_ClientWorkOrderResponse_Schema>;

export const CursorPage_ConsolidatedInvoiceResponse_Schema = z.object({
  next_cursor: z.string().nullable().optional(),
  results: z.array(ConsolidatedInvoiceResponseSchema),
});
export type CursorPage_ConsolidatedInvoiceResponse_ = z.infer<typeof CursorPage_ConsolidatedInvoiceResponse_Schema>;

export const CursorPage_ContactResponse_Schema = z.object({
  next_cursor: z.string().nullable().optional(),
  results: z.array(ContactResponseSchema),
});
export type CursorPage_ContactResponse_ = z.infer<typeof CursorPage_ContactResponse_Schema>;

export const CursorPage_CorporateEntityResponse_Schema = z.object({
  next_cursor: z.string().nullable().optional(),
  results: z.array(CorporateEntityResponseSchema),
});
export type CursorPage_CorporateEntityResponse_ = z.infer<typeof CursorPage_CorporateEntityResponse_Schema>;

export const CursorPage_CrmClientMessageResponse_Schema = z.object({
  next_cursor: z.string().nullable().optional(),
  results: z.array(CrmClientMessageResponseSchema),
});
export type CursorPage_CrmClientMessageResponse_ = z.infer<typeof CursorPage_CrmClientMessageResponse_Schema>;

export const DashboardActivitySchema = z.object({
  created_at: z.iso.datetime(),
  id: z.string(),
  title: z.string(),
  type: z.string(),
  user_name: z.string().nullable().optional(),
});
export type DashboardActivity = z.infer<typeof DashboardActivitySchema>;

export const DashboardScheduleItemSchema = z.object({
  account_name: z.string().optional(),
  id: z.string(),
  order_number: z.string(),
  scheduled_date: z.string(),
  scheduled_time: z.string().nullable().optional(),
  subcontractor_id: z.string().nullable().optional(),
  title: z.string(),
});
export type DashboardScheduleItem = z.infer<typeof DashboardScheduleItemSchema>;

export const DashboardTotalsSchema = z.object({
  accounts: z.number().int(),
  contract_value: z.number(),
  national_ar: z.number().optional(),
  pipeline: z.number(),
  territories: z.number().int(),
});
export type DashboardTotals = z.infer<typeof DashboardTotalsSchema>;

export const DashboardWorkOrderSchema = z.object({
  account_name: z.string().optional(),
  estimated_cost: z.number().nullable().optional(),
  id: z.string(),
  order_number: z.string(),
  scheduled_date: z.string().nullable().optional(),
  status: z.string(),
  title: z.string(),
});
export type DashboardWorkOrder = z.infer<typeof DashboardWorkOrderSchema>;

export const DealCreateSchema = z.object({
  account_id: z.string(),
  contact_id: z.string().nullable().optional(),
  deal_type: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
  expected_close_date: z.string().nullable().optional(),
  location_id: z.string().nullable().optional(),
  owner_id: z.string().nullable().optional(),
  probability: z.number().nullable().optional(),
  source: z.string().nullable().optional(),
  stage: z.string().optional(),
  title: z.string().min(1),
  value: z.number().nullable().optional(),
});
export type DealCreate = z.infer<typeof DealCreateSchema>;

export const DealResponseSchema = z.object({
  account_id: z.string(),
  account_name: z.string().nullable().optional(),
  actual_close_date: z.string().nullable(),
  contact_id: z.string().nullable(),
  created_at: z.iso.datetime(),
  created_by_id: z.string().nullable(),
  created_by_name: z.string().nullable().optional(),
  deal_type: z.string().nullable(),
  description: z.string().nullable(),
  expected_close_date: z.string().nullable(),
  id: z.string(),
  location_id: z.string().nullable(),
  location_name: z.string().nullable().optional(),
  lost_reason: z.string().nullable(),
  owner_id: z.string().nullable(),
  owner_name: z.string().nullable().optional(),
  probability: z.number().nullable(),
  source: z.string().nullable(),
  stage: z.string(),
  territory_id: z.string(),
  territory_name: z.string().nullable().optional(),
  title: z.string(),
  updated_at: z.iso.datetime(),
  updated_by_id: z.string().nullable(),
  updated_by_name: z.string().nullable().optional(),
  value: z.number().nullable(),
  version: z.number().int(),
});
export type DealResponse = z.infer<typeof DealResponseSchema>;

export const CursorPage_DealResponse_Schema = z.object({
  next_cursor: z.string().nullable().optional(),
  results: z.array(DealResponseSchema),
});
export type CursorPage_DealResponse_ = z.infer<typeof CursorPage_DealResponse_Schema>;

export const DealUpdateSchema = z.object({
  actual_close_date: z.string().nullable().optional(),
  contact_id: z.string().nullable().optional(),
  deal_type: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
  expected_close_date: z.string().nullable().optional(),
  location_id: z.string().nullable().optional(),
  lost_reason: z.string().nullable().optional(),
  owner_id: z.string().nullable().optional(),
  probability: z.number().nullable().optional(),
  source: z.string().nullable().optional(),
  stage: z.string().nullable().optional(),
  title: z.string().min(1).nullable().optional(),
  value: z.number().nullable().optional(),
  version: z.number().int(),
});
export type DealUpdate = z.infer<typeof DealUpdateSchema>;

export const DocumentCreateSchema = z.object({
  account_id: z.string().nullable().optional(),
  bid_id: z.string().nullable().optional(),
  bid_request_id: z.string().nullable().optional(),
  client_visible: z.boolean().nullable().optional(),
  description: z.string().nullable().optional(),
  distribution_batch_id: z.string().nullable().optional(),
  document_request_id: z.string().nullable().optional(),
  document_type: z.string().nullable().optional(),
  expiry_date: z.string().nullable().optional(),
  file_size: z.number().int().min(0),
  file_type: z.string().nullable().optional(),
  filename: z.string().min(1).max(500),
  inspection_id: z.string().nullable().optional(),
  inspection_item_id: z.string().nullable().optional(),
  original_name: z.string().nullable().optional(),
  quote_id: z.string().nullable().optional(),
  subcontractor_id: z.string().nullable().optional(),
  work_order_id: z.string().nullable().optional(),
});
export type DocumentCreate = z.infer<typeof DocumentCreateSchema>;

export const DocumentDownloadResponseSchema = z.object({
  content_sha256: z.string().nullable().optional(),
  download_url: z.string(),
});
export type DocumentDownloadResponse = z.infer<typeof DocumentDownloadResponseSchema>;

export const DocumentRequestCreateSchema = z.object({
  document_type: z.string().min(1).max(100),
  due_date: z.string().nullable().optional(),
  notes: z.string().nullable().optional(),
});
export type DocumentRequestCreate = z.infer<typeof DocumentRequestCreateSchema>;

/** Body for a staff member accepting a document request. `document_id` is OPTIONAL since the review step landed. The ordinary path is now that the sub has already uploaded and the row names its own document, so accepting it needs no argument at all. Naming one explicitly is still allowed, for the case where staff hold the document themselves and accept in a single step - a person is confirming either way, which is the whole requirement. Omitting it on a request with nothing attached is refused rather than assumed. */
export const DocumentRequestFulfilSchema = z.object({
  document_id: z.string().nullable().optional(),
  expiry_date: z.string().nullable().optional(),
});
export type DocumentRequestFulfil = z.infer<typeof DocumentRequestFulfilSchema>;

/** Body for a staff member refusing what a sub submitted. Refusing returns the row to REQUESTED rather than a terminal REJECTED state: the obligation did not go away because the certificate was wrong, and REQUESTED is exactly what "this sub still owes us a valid document" means. A reason is REQUIRED - a refusal the sub cannot act on just produces another wrong upload. */
export const DocumentRequestRejectSchema = z.object({
  document_id: z.string().nullable().optional(),
  reason: z.string().min(1).max(500),
});
export type DocumentRequestReject = z.infer<typeof DocumentRequestRejectSchema>;

export const DocumentRequestSubmissionSchema = z.object({
  document_id: z.string(),
  filename: z.string(),
  rejection_reason: z.string().nullable().optional(),
  reviewed_at: z.iso.datetime().nullable().optional(),
  status: z.enum(["SUBMITTED", "ACCEPTED", "REJECTED"]),
  submitted_at: z.iso.datetime(),
});
export type DocumentRequestSubmission = z.infer<typeof DocumentRequestSubmissionSchema>;

export const DocumentRequestResponseSchema = z.object({
  cancelled_at: z.iso.datetime().nullable().optional(),
  created_at: z.iso.datetime(),
  document_type: z.string(),
  due_date: z.string().nullable().optional(),
  fulfilled_at: z.iso.datetime().nullable().optional(),
  fulfilled_document_id: z.string().nullable().optional(),
  id: z.string(),
  notes: z.string().nullable().optional(),
  rejection_reason: z.string().nullable().optional(),
  requested_by_id: z.string().nullable().optional(),
  requested_by_name: z.string().nullable().optional(),
  status: z.enum(["REQUESTED", "SUBMITTED", "RECEIVED", "CANCELLED"]),
  subcontractor_id: z.string(),
  subcontractor_name: z.string().nullable().optional(),
  submissions: z.array(DocumentRequestSubmissionSchema).optional(),
  submitted_at: z.iso.datetime().nullable().optional(),
  territory_id: z.string(),
  updated_at: z.iso.datetime(),
  work_order_id: z.string().nullable().optional(),
  work_order_number: z.string().nullable().optional(),
});
export type DocumentRequestResponse = z.infer<typeof DocumentRequestResponseSchema>;

export const CursorPage_DocumentRequestResponse_Schema = z.object({
  next_cursor: z.string().nullable().optional(),
  results: z.array(DocumentRequestResponseSchema),
});
export type CursorPage_DocumentRequestResponse_ = z.infer<typeof CursorPage_DocumentRequestResponse_Schema>;

export const DocumentResponseSchema = z.object({
  account_id: z.string().nullable().optional(),
  bid_id: z.string().nullable().optional(),
  bid_request_id: z.string().nullable().optional(),
  client_visible: z.boolean(),
  created_at: z.iso.datetime(),
  description: z.string().nullable(),
  distribution_batch_id: z.string().nullable().optional(),
  document_type: z.string().nullable(),
  expiry_date: z.string().nullable(),
  file_size: z.number().int().nullable(),
  file_type: z.string().nullable(),
  filename: z.string(),
  id: z.string(),
  inspection_id: z.string().nullable().optional(),
  inspection_item_id: z.string().nullable().optional(),
  original_name: z.string().nullable(),
  quote_id: z.string().nullable().optional(),
  subcontractor_id: z.string().nullable(),
  uploaded_by_type: z.string().nullable(),
  work_order_id: z.string().nullable(),
});
export type DocumentResponse = z.infer<typeof DocumentResponseSchema>;

export const CursorPage_DocumentResponse_Schema = z.object({
  next_cursor: z.string().nullable().optional(),
  results: z.array(DocumentResponseSchema),
});
export type CursorPage_DocumentResponse_ = z.infer<typeof CursorPage_DocumentResponse_Schema>;

/** The registered document plus a short-lived presigned PUT URL for the client to upload the bytes to S3 (the row's `file_path` is the destination key). */
export const DocumentUploadResponseSchema = z.object({
  document: DocumentResponseSchema,
  upload_url: z.string(),
});
export type DocumentUploadResponse = z.infer<typeof DocumentUploadResponseSchema>;

export const DocumentVisibilityUpdateSchema = z.object({
  client_visible: z.boolean(),
});
export type DocumentVisibilityUpdate = z.infer<typeof DocumentVisibilityUpdateSchema>;

export const ErrorResponseSchema = z.object({
  code: z.string().nullable().optional(),
  detail: z.string(),
});
export type ErrorResponse = z.infer<typeof ErrorResponseSchema>;

export const ExpiringDocumentItemSchema = z.object({
  document: z.string(),
  expiry_date: z.string(),
  status: z.string(),
});
export type ExpiringDocumentItem = z.infer<typeof ExpiringDocumentItemSchema>;

export const ExpiringDocumentsResponseSchema = z.object({
  company_name: z.string(),
  email: z.string().nullable().optional(),
  issues: z.array(ExpiringDocumentItemSchema),
  phone: z.string().nullable().optional(),
  status: z.string(),
  subcontractor_id: z.string(),
});
export type ExpiringDocumentsResponse = z.infer<typeof ExpiringDocumentsResponseSchema>;

export const FeedbackCreateSchema = z.object({
  category: z.enum(["BUG", "FEATURE_REQUEST", "QUESTION", "PRAISE"]),
  message: z.string().min(10).max(2000),
  page_url: z.string().max(500).nullable().optional(),
});
export type FeedbackCreate = z.infer<typeof FeedbackCreateSchema>;

/** One triage row opened on its own (`GET /feedback/admin/{id}`, SUPER_ADMIN). Adds the two columns the list omits and the detail screen needs. `page_url` is the whole point of the screen: it is where the submitter was standing when they wrote the message, and it is what tells a triager which team the ticket belongs to. It is captured on submit (`FeedbackCreate.page_url`) and was stored-but-unreadable until this response declared it. */
export const FeedbackDetailResponseSchema = z.object({
  category: z.string(),
  created_at: z.iso.datetime(),
  id: z.string(),
  message: z.string(),
  page_url: z.string().nullable().optional(),
  resolved_at: z.iso.datetime().nullable().optional(),
  status: z.string(),
  territory_name: z.string().nullable().optional(),
  updated_at: z.iso.datetime(),
  user_email: z.string().nullable().optional(),
  user_name: z.string().nullable().optional(),
});
export type FeedbackDetailResponse = z.infer<typeof FeedbackDetailResponseSchema>;

export const FeedbackRowSchema = z.object({
  category: z.string(),
  created_at: z.iso.datetime(),
  id: z.string(),
  message: z.string(),
  resolved_at: z.iso.datetime().nullable().optional(),
  status: z.string(),
  territory_name: z.string().nullable().optional(),
  user_email: z.string().nullable().optional(),
  user_name: z.string().nullable().optional(),
});
export type FeedbackRow = z.infer<typeof FeedbackRowSchema>;

export const FeedbackListResponseSchema = z.object({
  hasNext: z.boolean(),
  hasPrev: z.boolean(),
  rows: z.array(FeedbackRowSchema),
});
export type FeedbackListResponse = z.infer<typeof FeedbackListResponseSchema>;

export const FeedbackStatusUpdateSchema = z.object({
  status: z.enum(["NEW", "IN_PROGRESS", "RESOLVED", "WONT_FIX"]),
});
export type FeedbackStatusUpdate = z.infer<typeof FeedbackStatusUpdateSchema>;

/** The one GLOBAL scoring policy row. Two halves worth telling apart. The `weight_*` trio says how much each component COUNTS and must sum to 1. The `sr_penalty_*` schedule says what a problem COSTS inside the Active-problems component (`100 - sum(priority penalty) - sum(stale penalty)`) and has no such relationship — the penalties are points off a 100-point component, so they are independent and only have to be non-negative. Before the `sr_penalty_*` columns existed that schedule was a literal in three SQL strings, so an admin could tune the first half and not the second. */
export const HealthScoreConfigResponseSchema = z.object({
  green_threshold: z.number().int(),
  id: z.string(),
  missing_data_score: z.number().int(),
  sr_age_bucket_1_days: z.number().int(),
  sr_age_bucket_2_days: z.number().int(),
  sr_penalty_default: z.number().int(),
  sr_penalty_high: z.number().int(),
  sr_penalty_stale: z.number().int(),
  sr_penalty_urgent: z.number().int(),
  stale_sr_days: z.number().int(),
  trend_threshold: z.number().nullable(),
  updated_at: z.iso.datetime(),
  weight_delivery: z.number(),
  weight_inspection: z.number(),
  weight_problems: z.number(),
  window_days: z.number().int(),
  yellow_threshold: z.number().int(),
});
export type HealthScoreConfigResponse = z.infer<typeof HealthScoreConfigResponseSchema>;

export const HealthScoreConfigUpdateSchema = z.object({
  green_threshold: z.number().int().nullable().optional(),
  missing_data_score: z.number().int().nullable().optional(),
  sr_age_bucket_1_days: z.number().int().min(1).nullable().optional(),
  sr_age_bucket_2_days: z.number().int().min(1).nullable().optional(),
  sr_penalty_default: z.number().int().min(0).nullable().optional(),
  sr_penalty_high: z.number().int().min(0).nullable().optional(),
  sr_penalty_stale: z.number().int().min(0).nullable().optional(),
  sr_penalty_urgent: z.number().int().min(0).nullable().optional(),
  stale_sr_days: z.number().int().nullable().optional(),
  trend_threshold: z.number().nullable().optional(),
  weight_delivery: z.number().nullable().optional(),
  weight_inspection: z.number().nullable().optional(),
  weight_problems: z.number().nullable().optional(),
  window_days: z.number().int().nullable().optional(),
  yellow_threshold: z.number().int().nullable().optional(),
});
export type HealthScoreConfigUpdate = z.infer<typeof HealthScoreConfigUpdateSchema>;

export const IdResponseSchema = z.object({
  id: z.string(),
});
export type IdResponse = z.infer<typeof IdResponseSchema>;

export const InspectionCreateSchema = z.object({
  account_id: z.string(),
  follow_up_notes: z.string().nullable().optional(),
  follow_up_required: z.boolean().optional(),
  inspection_type: z.string(),
  inspector_id: z.string().nullable().optional(),
  location_id: z.string().nullable().optional(),
  notes: z.string().nullable().optional(),
  scheduled_date: z.iso.datetime().nullable().optional(),
  service_categories: z.array(z.string()).optional(),
  subcontractor_id: z.string().nullable().optional(),
  title: z.string().min(1).max(255),
  work_order_day_id: z.string().nullable().optional(),
  work_order_id: z.string().nullable().optional(),
});
export type InspectionCreate = z.infer<typeof InspectionCreateSchema>;

/** A line added by hand on the spot. Always stored with `source = 'MANUAL'` — provenance is server-set, never claimed by the caller, or A2's audited deletion records a fiction. */
export const InspectionItemCreateSchema = z.object({
  area: z.string().nullable().optional(),
  item: z.string().min(1),
  notes: z.string().nullable().optional(),
  photo_path: z.string().nullable().optional(),
  rating: z.number().int().min(0).max(5).nullable().optional(),
  requires_note: z.boolean().optional(),
  requires_photo: z.boolean().optional(),
  service_category: z.string().nullable().optional(),
  sort_order: z.number().int().optional(),
});
export type InspectionItemCreate = z.infer<typeof InspectionItemCreateSchema>;

export const InspectionItemResponseSchema = z.object({
  area: z.string().nullable(),
  id: z.string(),
  inspection_id: z.string(),
  item: z.string().nullable(),
  notes: z.string().nullable(),
  photo_path: z.string().nullable(),
  rating: z.number().int().nullable(),
  requires_note: z.boolean().optional(),
  requires_photo: z.boolean().optional(),
  service_category: z.string().nullable(),
  sort_order: z.number().int().optional(),
  source: z.string().nullable().optional(),
  source_ref: z.string().nullable().optional(),
  territory_id: z.string(),
});
export type InspectionItemResponse = z.infer<typeof InspectionItemResponseSchema>;

/** NOTE WHAT IS ABSENT: `photo_path`, `source` and `source_ref`. `photo_path` is READ-ONLY on update (#232 / A5): evidence arrives by uploading a document linked to the item (`documents.inspection_item_id`), and a writable path field is a way to satisfy the PHOTO_REQUIRED gate by typing a string. Inbound values are ignored rather than refused, so the legacy web call stops mattering without breaking. Provenance is not editable at all: the audit trail A2 requires is only worth keeping if the line's stated origin is the one it actually had. */
export const InspectionItemUpdateSchema = z.object({
  area: z.string().nullable().optional(),
  item: z.string().min(1).nullable().optional(),
  notes: z.string().nullable().optional(),
  rating: z.number().int().min(0).max(5).nullable().optional(),
  requires_note: z.boolean().nullable().optional(),
  requires_photo: z.boolean().nullable().optional(),
  service_category: z.string().nullable().optional(),
  sort_order: z.number().int().nullable().optional(),
});
export type InspectionItemUpdate = z.infer<typeof InspectionItemUpdateSchema>;

/** One line of the create form's preview — what WOULD compose, without creating anything. */
export const InspectionPreviewItemSchema = z.object({
  area: z.string().nullable().optional(),
  item: z.string(),
  requires_note: z.boolean().optional(),
  requires_photo: z.boolean().optional(),
  service_category: z.string().nullable().optional(),
  sort_order: z.number().int().optional(),
  source: z.string(),
});
export type InspectionPreviewItem = z.infer<typeof InspectionPreviewItemSchema>;

export const InspectionPreviewAreaSchema = z.object({
  area: z.string().nullable().optional(),
  items: z.array(InspectionPreviewItemSchema).optional(),
});
export type InspectionPreviewArea = z.infer<typeof InspectionPreviewAreaSchema>;

export const InspectionPreviewCategorySchema = z.object({
  areas: z.array(InspectionPreviewAreaSchema).optional(),
  service_category: z.string().nullable().optional(),
  unconfigured: z.boolean().optional(),
});
export type InspectionPreviewCategory = z.infer<typeof InspectionPreviewCategorySchema>;

export const InspectionPreviewResponseSchema = z.object({
  as_of: z.string(),
  categories: z.array(InspectionPreviewCategorySchema).optional(),
  location_id: z.string(),
  service_categories: z.array(z.string()).optional(),
});
export type InspectionPreviewResponse = z.infer<typeof InspectionPreviewResponseSchema>;

export const InspectionResponseSchema = z.object({
  account_id: z.string(),
  account_name: z.string().nullable().optional(),
  carries_qv_protocol: z.boolean().optional(),
  client_improvement_answer: z.string().nullable().optional(),
  client_rating: z.number().nullable().optional(),
  client_seen: z.boolean().nullable().optional(),
  client_unable_reason: z.string().nullable().optional(),
  completed_date: z.iso.datetime().nullable(),
  created_at: z.iso.datetime(),
  created_by_id: z.string().nullable(),
  created_by_name: z.string().nullable().optional(),
  debrief_held: z.boolean().optional(),
  debrief_held_reason: z.string().nullable().optional(),
  debrief_message_id: z.string().nullable().optional(),
  debrief_verdict: z.string().nullable().optional(),
  follow_up_notes: z.string().nullable(),
  follow_up_required: z.boolean(),
  fsm_rating: z.number().nullable().optional(),
  id: z.string(),
  inspection_number: z.string(),
  inspection_type: z.string().nullable(),
  inspector_id: z.string().nullable(),
  inspector_name: z.string().nullable().optional(),
  location_address: z.string().nullable().optional(),
  location_id: z.string().nullable(),
  location_name: z.string().nullable().optional(),
  notes: z.string().nullable(),
  overall_score: z.number().nullable(),
  pending_approval: z.boolean(),
  remediation_work_order_id: z.string().nullable(),
  scheduled_date: z.iso.datetime().nullable(),
  scope_pending_amendments: z.number().int().optional(),
  scope_pending_categories: z.array(z.string()).optional(),
  service_categories: z.array(z.string()).optional(),
  standing_scope_lines: z.number().int().optional(),
  status: z.string(),
  subcontractor_id: z.string().nullable(),
  subcontractor_name: z.string().nullable().optional(),
  supersede_reason: z.string().nullable().optional(),
  supersedes_inspection_id: z.string().nullable().optional(),
  territory_id: z.string(),
  territory_name: z.string().nullable().optional(),
  title: z.string(),
  unconfigured_service_categories: z.array(z.string()).optional(),
  updated_at: z.iso.datetime(),
  updated_by_id: z.string().nullable(),
  updated_by_name: z.string().nullable().optional(),
  version: z.number().int(),
  what_went_well: z.string().nullable().optional(),
  work_order_day_id: z.string().nullable().optional(),
  work_order_id: z.string().nullable(),
  work_order_number: z.string().nullable().optional(),
});
export type InspectionResponse = z.infer<typeof InspectionResponseSchema>;

export const CursorPage_InspectionResponse_Schema = z.object({
  next_cursor: z.string().nullable().optional(),
  results: z.array(InspectionResponseSchema),
});
export type CursorPage_InspectionResponse_ = z.infer<typeof CursorPage_InspectionResponse_Schema>;

/** A4: reopen a completed inspection as a corrective visit. The reason is the whole point of the endpoint existing rather than a plain create: superseding somebody's completed judgement without saying why is the version of this feature that gets used to quietly bury a bad result. */
export const InspectionSupersedeRequestSchema = z.object({
  reason: z.string().min(1).max(1000),
});
export type InspectionSupersedeRequest = z.infer<typeof InspectionSupersedeRequestSchema>;

export const InspectionTemplateCreateSchema = z.object({
  account_id: z.string().nullable().optional(),
  area: z.string().nullable().optional(),
  location_id: z.string().nullable().optional(),
  service_category: z.string().nullable().optional(),
  territory_id: z.string().nullable().optional(),
});
export type InspectionTemplateCreate = z.infer<typeof InspectionTemplateCreateSchema>;

export const InspectionTemplateItemCreateSchema = z.object({
  item: z.string().min(1),
  requires_note: z.boolean().optional(),
  requires_photo: z.boolean().optional(),
  sort_order: z.number().int().optional(),
});
export type InspectionTemplateItemCreate = z.infer<typeof InspectionTemplateItemCreateSchema>;

export const InspectionTemplateItemResponseSchema = z.object({
  id: z.string(),
  item: z.string(),
  requires_note: z.boolean(),
  requires_photo: z.boolean(),
  sort_order: z.number().int(),
  template_id: z.string(),
});
export type InspectionTemplateItemResponse = z.infer<typeof InspectionTemplateItemResponseSchema>;

export const InspectionTemplateItemUpdateSchema = z.object({
  item: z.string().min(1).nullable().optional(),
  requires_note: z.boolean().nullable().optional(),
  requires_photo: z.boolean().nullable().optional(),
  sort_order: z.number().int().nullable().optional(),
});
export type InspectionTemplateItemUpdate = z.infer<typeof InspectionTemplateItemUpdateSchema>;

export const InspectionTemplateResponseSchema = z.object({
  account_id: z.string().nullable(),
  area: z.string().nullable(),
  created_at: z.iso.datetime(),
  created_by_id: z.string().nullable(),
  id: z.string(),
  is_active: z.boolean(),
  location_id: z.string().nullable(),
  service_category: z.string().nullable(),
  territory_id: z.string(),
  updated_at: z.iso.datetime(),
});
export type InspectionTemplateResponse = z.infer<typeof InspectionTemplateResponseSchema>;

export const CursorPage_InspectionTemplateResponse_Schema = z.object({
  next_cursor: z.string().nullable().optional(),
  results: z.array(InspectionTemplateResponseSchema),
});
export type CursorPage_InspectionTemplateResponse_ = z.infer<typeof CursorPage_InspectionTemplateResponse_Schema>;

export const InspectionTemplateUpdateSchema = z.object({
  account_id: z.string().nullable().optional(),
  area: z.string().nullable().optional(),
  is_active: z.boolean().nullable().optional(),
  location_id: z.string().nullable().optional(),
  service_category: z.string().nullable().optional(),
});
export type InspectionTemplateUpdate = z.infer<typeof InspectionTemplateUpdateSchema>;

export const InspectionUpdateSchema = z.object({
  client_improvement_answer: z.string().nullable().optional(),
  client_rating: z.number().min(1).max(10).nullable().optional(),
  client_seen: z.boolean().nullable().optional(),
  client_unable_reason: z.string().nullable().optional(),
  debrief_held: z.boolean().nullable().optional(),
  debrief_held_reason: z.string().nullable().optional(),
  debrief_verdict: z.string().nullable().optional(),
  follow_up_notes: z.string().nullable().optional(),
  follow_up_required: z.boolean().nullable().optional(),
  fsm_rating: z.number().min(1).max(10).nullable().optional(),
  inspection_type: z.string().nullable().optional(),
  inspector_id: z.string().nullable().optional(),
  notes: z.string().nullable().optional(),
  remediation_work_order_id: z.string().nullable().optional(),
  scheduled_date: z.iso.datetime().nullable().optional(),
  status: z.enum(["SCHEDULED", "IN_PROGRESS", "COMPLETED", "CANCELLED"]).nullable().optional(),
  subcontractor_id: z.string().nullable().optional(),
  title: z.string().min(1).max(255).nullable().optional(),
  version: z.number().int(),
  what_went_well: z.string().nullable().optional(),
  work_order_day_id: z.string().nullable().optional(),
  work_order_id: z.string().nullable().optional(),
});
export type InspectionUpdate = z.infer<typeof InspectionUpdateSchema>;

/** One row of the unified triage surface. `kind` says which door it came through, and is what the dispatcher reclassifies when the client picked the wrong one. */
export const IntakeQueueItemSchema = z.object({
  account_id: z.string(),
  account_name: z.string().nullable().optional(),
  age_days: z.number().int(),
  created_at: z.iso.datetime(),
  description: z.string().nullable().optional(),
  id: z.string(),
  intent: z.string().nullable().optional(),
  kind: z.enum(["SERVICE_REQUEST", "QUOTE_REQUEST"]),
  location_id: z.string().nullable().optional(),
  location_name: z.string().nullable().optional(),
  priority: z.string().nullable().optional(),
  source: z.string().nullable().optional(),
  status: z.string(),
  title: z.string(),
});
export type IntakeQueueItem = z.infer<typeof IntakeQueueItemSchema>;

export const CursorPage_IntakeQueueItem_Schema = z.object({
  next_cursor: z.string().nullable().optional(),
  results: z.array(IntakeQueueItemSchema),
});
export type CursorPage_IntakeQueueItem_ = z.infer<typeof CursorPage_IntakeQueueItem_Schema>;

/** Backward-compatible paged form of the unified intake queue for workspace UIs. */
export const IntakeQueuePageSchema = z.object({
  has_next: z.boolean(),
  items: z.array(IntakeQueueItemSchema),
  limit: z.number().int(),
  offset: z.number().int(),
  total: z.number().int(),
});
export type IntakeQueuePage = z.infer<typeof IntakeQueuePageSchema>;

/** One row in the corporate payout worklist: an invoice paid via the corporate rail (Leg 1, migration 183) with no `invoice_territory_payouts` row yet — GET /invoice-territory-payouts/awaiting. Human-initiated (owner ruling 2026-09-08): a corporate admin reviews this list and triggers each payout explicitly, mirroring vendor_bills' "Pay Electronically" worklist rather than firing automatically off the Leg-1 webhook. */
export const InvoiceAwaitingTerritoryPayoutResponseSchema = z.object({
  account_id: z.string(),
  account_name: z.string().nullable(),
  amount: z.number().int(),
  corporate_entity_id: z.string(),
  corporate_entity_name: z.string().nullable(),
  invoice_id: z.string(),
  invoice_number: z.string(),
  paid_date: z.string().nullable(),
  territory_id: z.string(),
  territory_name: z.string().nullable(),
});
export type InvoiceAwaitingTerritoryPayoutResponse = z.infer<typeof InvoiceAwaitingTerritoryPayoutResponseSchema>;

export const InvoiceCreateSchema = z.object({
  account_id: z.string(),
  due_date: z.string().nullable().optional(),
  notes: z.string().nullable().optional(),
  path: z.enum(["bc_export", "internal"]).optional(),
  payment_terms: z.string().nullable().optional(),
  po_number: z.string().nullable().optional(),
  tax: z.number().nullable().optional(),
  work_order_id: z.string().nullable().optional(),
});
export type InvoiceCreate = z.infer<typeof InvoiceCreateSchema>;

export const InvoiceLineCreateSchema = z.object({
  contractor_cost: z.number().nullable().optional(),
  description: z.string().min(1),
  quantity: z.number().optional(),
  service_category: z.string().nullable().optional(),
  unit_price: z.number(),
  work_order_day_id: z.string().nullable().optional(),
});
export type InvoiceLineCreate = z.infer<typeof InvoiceLineCreateSchema>;

export const InvoiceLineResponseSchema = z.object({
  contractor_cost: z.number().nullable(),
  description: z.string(),
  id: z.string(),
  invoice_id: z.string(),
  quantity: z.number(),
  service_category: z.string().nullable(),
  territory_id: z.string(),
  total_price: z.number(),
  unit_price: z.number(),
  work_order_day_id: z.string().nullable(),
});
export type InvoiceLineResponse = z.infer<typeof InvoiceLineResponseSchema>;

export const InvoiceLineUpdateSchema = z.object({
  contractor_cost: z.number().nullable().optional(),
  description: z.string().min(1).nullable().optional(),
  quantity: z.number().nullable().optional(),
  service_category: z.string().nullable().optional(),
  unit_price: z.number().nullable().optional(),
  work_order_day_id: z.string().nullable().optional(),
});
export type InvoiceLineUpdate = z.infer<typeof InvoiceLineUpdateSchema>;

/** One payment method's gross-up quote (client-invoice rail). Named apart from vendor-bills' `PaymentMethodQuote` deliberately: the sub-payout leg (#713) ships the older fee_cents/total_cents shape and is live; this rail's methods-list shape landed second. Two classes, two wire shapes — a shared name here silently shadowed one of them and every quote 500'd. — see `_surcharge_cents` for the formula. All amounts in cents. `stripe_cut_estimate` is Stripe's own real cut, for display parity with what the payer is actually being asked to cover (never a number we bill separately). */
export const InvoicePaymentMethodQuoteSchema = z.object({
  amount: z.number().int(),
  capped: z.boolean().optional(),
  charged: z.number().int(),
  method: z.string(),
  surcharge: z.number().int(),
});
export type InvoicePaymentMethodQuote = z.infer<typeof InvoicePaymentMethodQuoteSchema>;

/** GET /invoices/{id}/payment-quote — both methods' quotes, so the client can compare before choosing. Card+ACH gross-up (vantage-backend#711 design, applied identically to both the territory and client->NBD/corporate rails). */
export const InvoicePaymentQuoteResponseSchema = z.object({
  invoice_id: z.string(),
  methods: z.array(InvoicePaymentMethodQuoteSchema),
});
export type InvoicePaymentQuoteResponse = z.infer<typeof InvoicePaymentQuoteResponseSchema>;

export const InvoiceReminderResponseSchema = z.object({
  sent_at: z.iso.datetime(),
  sent_to: z.string(),
});
export type InvoiceReminderResponse = z.infer<typeof InvoiceReminderResponseSchema>;

export const InvoiceResponseSchema = z.object({
  account_id: z.string(),
  account_name: z.string().nullable().optional(),
  author_is_corporate: z.boolean().optional(),
  billing_blocked_code: z.string().nullable().optional(),
  billing_blocked_reason: z.string().nullable().optional(),
  cost_subtotal: z.number().nullable(),
  created_at: z.iso.datetime(),
  created_by_id: z.string().nullable(),
  created_by_name: z.string().nullable().optional(),
  document_id: z.string().nullable(),
  due_date: z.string().nullable(),
  id: z.string(),
  invoice_number: z.string(),
  notes: z.string().nullable(),
  paid_date: z.string().nullable(),
  path: z.string(),
  payment_terms: z.string().nullable(),
  po_number: z.string().nullable(),
  status: z.string(),
  subtotal: z.number().nullable(),
  tax: z.number().nullable(),
  territory_id: z.string(),
  territory_name: z.string().nullable().optional(),
  total: z.number().nullable(),
  updated_at: z.iso.datetime(),
  updated_by_id: z.string().nullable(),
  updated_by_name: z.string().nullable().optional(),
  version: z.number().int(),
  wo_invoice_count: z.number().int().nullable().optional(),
  wo_not_to_exceed: z.number().nullable().optional(),
  wo_total_billed: z.number().nullable().optional(),
  work_order_id: z.string().nullable(),
  work_order_number: z.string().nullable().optional(),
});
export type InvoiceResponse = z.infer<typeof InvoiceResponseSchema>;

export const CursorPage_InvoiceResponse_Schema = z.object({
  next_cursor: z.string().nullable().optional(),
  results: z.array(InvoiceResponseSchema),
});
export type CursorPage_InvoiceResponse_ = z.infer<typeof CursorPage_InvoiceResponse_Schema>;

export const InvoiceTerritoryPayoutCreateSchema = z.object({
  invoice_id: z.string(),
});
export type InvoiceTerritoryPayoutCreate = z.infer<typeof InvoiceTerritoryPayoutCreateSchema>;

/** A forwarded (or in-flight) NBD/corporate -> territory payout for one invoice paid via the corporate rail. Resolved-name metadata standard: territory_name, corporate_entity_name, invoice_number. */
export const InvoiceTerritoryPayoutResponseSchema = z.object({
  amount: z.number().int(),
  corporate_entity_id: z.string(),
  corporate_entity_name: z.string().nullable(),
  created_at: z.iso.datetime(),
  id: z.string(),
  invoice_id: z.string(),
  invoice_number: z.string().nullable(),
  paid_date: z.string().nullable(),
  payment_status: z.string().nullable(),
  receipt_url: z.string().nullable(),
  status: z.string(),
  territory_id: z.string(),
  territory_name: z.string().nullable(),
});
export type InvoiceTerritoryPayoutResponse = z.infer<typeof InvoiceTerritoryPayoutResponseSchema>;

export const CursorPage_InvoiceTerritoryPayoutResponse_Schema = z.object({
  next_cursor: z.string().nullable().optional(),
  results: z.array(InvoiceTerritoryPayoutResponseSchema),
});
export type CursorPage_InvoiceTerritoryPayoutResponse_ = z.infer<typeof CursorPage_InvoiceTerritoryPayoutResponse_Schema>;

export const InvoiceUpdateSchema = z.object({
  due_date: z.string().nullable().optional(),
  notes: z.string().nullable().optional(),
  path: z.enum(["bc_export", "internal"]).nullable().optional(),
  payment_terms: z.string().nullable().optional(),
  po_number: z.string().nullable().optional(),
  status: z.enum(["DRAFT", "PENDING_APPROVAL", "PENDING", "APPROVED", "PAID", "REJECTED", "VOID", "CANCELLED"]).nullable().optional(),
  tax: z.number().nullable().optional(),
  version: z.number().int(),
});
export type InvoiceUpdate = z.infer<typeof InvoiceUpdateSchema>;

/** The confirmed fields from the prefilled corrective Work Order draft (Decision BU3). The Issue supplies account, location, deficiency and expected result. The territory user must still confirm the scope title and the internal owner before a real Work Order is created. */
export const IssueCorrectiveWorkOrderCreateSchema = z.object({
  agreed_sub_cost: z.number().min(0).nullable().optional(),
  assigned_to_id: z.string(),
  estimated_cost: z.number().nullable().optional(),
  not_to_exceed: z.number().nullable().optional(),
  notes: z.string().nullable().optional(),
  order_type: z.string().nullable().optional(),
  po_number: z.string().nullable().optional(),
  priority: z.string().nullable().optional(),
  scheduled_date: z.iso.datetime().nullable().optional(),
  service_category: z.string().nullable().optional(),
  subcontractor_id: z.string().nullable().optional(),
  title: z.string().min(1).max(255),
});
export type IssueCorrectiveWorkOrderCreate = z.infer<typeof IssueCorrectiveWorkOrderCreateSchema>;

export const IssueCreateSchema = z.object({
  assignee_id: z.string().nullable().optional(),
  expected_result: z.string().max(2000).nullable().optional(),
  inspection_id: z.string().nullable().optional(),
  inspection_item_id: z.string().nullable().optional(),
  location_id: z.string(),
  service_request_id: z.string().nullable().optional(),
  severity: z.enum(["low", "medium", "high"]),
  source: z.enum(["INSPECTION_OBSERVATION", "CLIENT_COMPLAINT"]).nullable().optional(),
  summary: z.string().max(2000).nullable().optional(),
});
export type IssueCreate = z.infer<typeof IssueCreateSchema>;

/** #738: one event in an Issue's readable history, projected from the audit trail. The office reads the endpoints of a deficiency's life today — current links, final resolution — and nothing in between, on a form whose signed promises ("corrected within 3 working days", "repeat fail may trigger compliance action") are claims about a HISTORY. Assembling one client-side from whichever links are non-null right now would render convincingly and be wrong: no ordering, no middle timestamps, silent about anything undone. `detail` is the audit row's diff (old/new/details) verbatim — the reader decides the one-liner, the server does not editorialize. Territory-scoped like the Issue; never exposed to the crew (#709's boundary: they get the instruction, not the office record). */
export const IssueHistoryEntrySchema = z.object({
  actor_name: z.string().nullable().optional(),
  at: z.iso.datetime(),
  detail: z.unknown().optional(),
  event: z.string(),
});
export type IssueHistoryEntry = z.infer<typeof IssueHistoryEntrySchema>;

export const IssueResponseSchema = z.object({
  amendment_id: z.string().nullable().optional(),
  amendment_service_category: z.string().nullable().optional(),
  amendment_status: z.string().nullable().optional(),
  assignee_id: z.string().nullable(),
  author_is_corporate: z.boolean().optional(),
  carried_to_date: z.string().nullable().optional(),
  carried_to_work_order_day_id: z.string().nullable().optional(),
  carried_to_work_order_id: z.string().nullable().optional(),
  carried_to_work_order_number: z.string().nullable().optional(),
  created_at: z.iso.datetime(),
  created_by_id: z.string().nullable().optional(),
  created_by_name: z.string().nullable().optional(),
  expected_result: z.string().nullable().optional(),
  id: z.string(),
  inspection_id: z.string().nullable(),
  inspection_item_id: z.string().nullable().optional(),
  location_id: z.string(),
  quote_id: z.string().nullable().optional(),
  quote_number: z.string().nullable().optional(),
  reinspection_completed_at: z.iso.datetime().nullable().optional(),
  reinspection_id: z.string().nullable().optional(),
  reinspection_number: z.string().nullable().optional(),
  reinspection_status: z.string().nullable().optional(),
  remediation_status: z.string().nullable().optional(),
  remediation_work_complete_claimed_at: z.iso.datetime().nullable().optional(),
  remediation_work_order_id: z.string().nullable().optional(),
  resolution: z.string().nullable().optional(),
  service_request_id: z.string().nullable().optional(),
  service_request_number: z.string().nullable().optional(),
  severity: z.string(),
  source: z.enum(["INSPECTION_OBSERVATION", "CLIENT_COMPLAINT"]).nullable().optional(),
  status: z.string(),
  summary: z.string().nullable(),
  territory_id: z.string(),
  verified_at: z.iso.datetime().nullable().optional(),
  verified_by_id: z.string().nullable().optional(),
  verified_by_name: z.string().nullable().optional(),
  work_order_number: z.string().nullable().optional(),
});
export type IssueResponse = z.infer<typeof IssueResponseSchema>;

export const CursorPage_IssueResponse_Schema = z.object({
  next_cursor: z.string().nullable().optional(),
  results: z.array(IssueResponseSchema),
});
export type CursorPage_IssueResponse_ = z.infer<typeof CursorPage_IssueResponse_Schema>;

export const IssueUpdateSchema = z.object({
  amendment_id: z.string().nullable().optional(),
  assignee_id: z.string().nullable().optional(),
  carried_to_work_order_day_id: z.string().nullable().optional(),
  expected_result: z.string().nullable().optional(),
  quote_id: z.string().nullable().optional(),
  reinspection_id: z.string().nullable().optional(),
  remediation_work_order_id: z.string().nullable().optional(),
  resolution: z.string().max(2000).nullable().optional(),
  service_request_id: z.string().nullable().optional(),
  severity: z.enum(["low", "medium", "high"]).nullable().optional(),
  source: z.enum(["INSPECTION_OBSERVATION", "CLIENT_COMPLAINT"]).nullable().optional(),
  status: z.enum(["open", "in_progress", "resolved"]).nullable().optional(),
  summary: z.string().nullable().optional(),
});
export type IssueUpdate = z.infer<typeof IssueUpdateSchema>;

export const LocationCreateSchema = z.object({
  access_notes: z.string().nullable().optional(),
  account_id: z.string(),
  acreage: z.number().nullable().optional(),
  address_line1: z.string().nullable().optional(),
  address_line2: z.string().nullable().optional(),
  building_class: z.string().nullable().optional(),
  building_type_id: z.string().nullable().optional(),
  city: z.string().nullable().optional(),
  do_not_contact: z.boolean().optional(),
  floors: z.number().int().nullable().optional(),
  geo: z.record(z.string(), z.unknown()).nullable().optional(),
  is_active: z.boolean().optional(),
  monthly_rate: z.number().nullable().optional(),
  name: z.string().min(1).max(255),
  negotiated_checklist: z.record(z.string(), z.unknown()).nullable().optional(),
  notes: z.string().nullable().optional(),
  parent_location_id: z.string().nullable().optional(),
  parking_spaces: z.number().int().nullable().optional(),
  restrooms: z.number().int().nullable().optional(),
  square_footage: z.number().int().nullable().optional(),
  state: z.string().nullable().optional(),
  territory_id: z.string().nullable().optional(),
  timezone: z.string().nullable().optional(),
  zip: z.string().nullable().optional(),
});
export type LocationCreate = z.infer<typeof LocationCreateSchema>;

export const LocationFromAccountSchema = z.object({
  account_id: z.string(),
  name: z.string().nullable().optional(),
});
export type LocationFromAccount = z.infer<typeof LocationFromAccountSchema>;

export const LocationHealthSchema = z.object({
  delivery: z.number().nullable().optional(),
  inspection: z.number().nullable().optional(),
  insufficient_data: z.boolean(),
  location_id: z.string(),
  problems: z.number(),
  score: z.number(),
  trend: z.enum(["up", "down", "flat"]),
});
export type LocationHealth = z.infer<typeof LocationHealthSchema>;

export const LocationUpdateSchema = z.object({
  access_notes: z.string().nullable().optional(),
  acreage: z.number().nullable().optional(),
  address_line1: z.string().nullable().optional(),
  address_line2: z.string().nullable().optional(),
  building_class: z.string().nullable().optional(),
  building_type_id: z.string().nullable().optional(),
  city: z.string().nullable().optional(),
  do_not_contact: z.boolean().nullable().optional(),
  floors: z.number().int().nullable().optional(),
  geo: z.record(z.string(), z.unknown()).nullable().optional(),
  is_active: z.boolean().nullable().optional(),
  monthly_rate: z.number().nullable().optional(),
  name: z.string().min(1).max(255).nullable().optional(),
  negotiated_checklist: z.record(z.string(), z.unknown()).nullable().optional(),
  notes: z.string().nullable().optional(),
  parent_location_id: z.string().nullable().optional(),
  parking_spaces: z.number().int().nullable().optional(),
  restrooms: z.number().int().nullable().optional(),
  square_footage: z.number().int().nullable().optional(),
  state: z.string().nullable().optional(),
  territory_id: z.string().nullable().optional(),
  timezone: z.string().nullable().optional(),
  zip: z.string().nullable().optional(),
});
export type LocationUpdate = z.infer<typeof LocationUpdateSchema>;

/** Discovery payload: which collections + udc domains this API exposes. */
export const LookupIndexSchema = z.object({
  collections: z.array(z.string()),
  udc_domains: z.array(z.string()),
});
export type LookupIndex = z.infer<typeof LookupIndexSchema>;

/** A typed-table lookup row (service_request_categories, deal_stages, deal_sources, inspection_types, payment_terms, work_order_cancellation_reasons). Some fields belong to one collection only and are null elsewhere: `color` and `default_probability` are deal_stages, `days` is payment_terms, `carries_qv_protocol` is inspection_types; `sort_order` is null for payment_terms (ordered by `days`). */
export const LookupItemSchema = z.object({
  carries_qv_protocol: z.boolean().nullable().optional(),
  code: z.string(),
  color: z.string().nullable().optional(),
  days: z.number().int().nullable().optional(),
  default_probability: z.number().nullable().optional(),
  is_active: z.boolean(),
  label: z.string(),
  sort_order: z.number().int().nullable().optional(),
});
export type LookupItem = z.infer<typeof LookupItemSchema>;

export const MarginDefaultResponseSchema = z.object({
  category_id: z.string(),
  category_label: z.string().nullable().optional(),
  floor_margin_pct: z.number(),
  target_margin_pct: z.number().nullable().optional(),
  updated_at: z.iso.datetime(),
  updated_by_id: z.string().nullable().optional(),
  updated_by_name: z.string().nullable().optional(),
});
export type MarginDefaultResponse = z.infer<typeof MarginDefaultResponseSchema>;

/** SUPER_ADMIN only. A territory changes its OWN numbers through territory_margins, which always wins over this — editing here moves the fallback for every market that has not set one. RESTORED 2026-08-29, having been deleted the same day by the zero-consumer endpoint audit. It was never redundant: vantage-docs#65 asked for the defaults to be "visible and editable in Settings instead of buried in a function, so it can't become permanent by neglect", and only the VISIBLE half (`GET /defaults`, read by the quote form and the quote detail) was ever built into the web. The write had no caller because its UI is unbuilt, not because something else does the job — and without it the platform fallback is settable only by a migration, which is the "permanent by neglect" the ask was against. */
export const MarginDefaultUpdateSchema = z.object({
  floor_margin_pct: z.number().min(0).lt(100).nullable().optional(),
  target_margin_pct: z.number().min(0).lt(100).nullable().optional(),
});
export type MarginDefaultUpdate = z.infer<typeof MarginDefaultUpdateSchema>;

export const MessageEscalateRequestSchema = z.object({
  category: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
  location_id: z.string().nullable().optional(),
  priority: z.string().nullable().optional(),
  title: z.string().max(255).nullable().optional(),
});
export type MessageEscalateRequest = z.infer<typeof MessageEscalateRequestSchema>;

export const MessageResponseSchema = z.object({
  message: z.string(),
});
export type MessageResponse = z.infer<typeof MessageResponseSchema>;

export const MessagesSummarySchema = z.object({
  client_unread: z.number().int().optional(),
  sub_unread: z.number().int().optional(),
  unread: z.number().int(),
});
export type MessagesSummary = z.infer<typeof MessagesSummarySchema>;

export const MoneySummaryResponseSchema = z.object({
  ap_due: z.number(),
  ar_outstanding: z.number(),
});
export type MoneySummaryResponse = z.infer<typeof MoneySummaryResponseSchema>;

export const NationalAccountResponseSchema = z.object({
  account_id: z.string(),
  created_at: z.iso.datetime(),
  id: z.string(),
  name: z.string(),
  territory_id: z.string(),
});
export type NationalAccountResponse = z.infer<typeof NationalAccountResponseSchema>;

export const CursorPage_NationalAccountResponse_Schema = z.object({
  next_cursor: z.string().nullable().optional(),
  results: z.array(NationalAccountResponseSchema),
});
export type CursorPage_NationalAccountResponse_ = z.infer<typeof CursorPage_NationalAccountResponse_Schema>;

export const NationalAccountRowSchema = z.object({
  account_type: z.string().nullable().optional(),
  company_name: z.string(),
  division_count: z.number().int().nullable().optional(),
  id: z.string(),
  status: z.string(),
});
export type NationalAccountRow = z.infer<typeof NationalAccountRowSchema>;

export const NationalApprovalCreateSchema = z.object({
  notes: z.string().nullable().optional(),
  reference_id: z.string(),
  reference_type: z.enum(["WORK_ORDER", "QUOTE", "SERVICE_REQUEST", "INSPECTION", "RECURRING_SCHEDULE"]),
  territory_id: z.string(),
  type: z.enum(["NTE_EXCEEDED", "PRICE_INCREASE", "PO_OVERRIDE", "NATIONAL_WORK_ORDER", "NATIONAL_SERVICE_REQUEST", "NATIONAL_INSPECTION", "NATIONAL_RECURRING_SCHEDULE"]),
});
export type NationalApprovalCreate = z.infer<typeof NationalApprovalCreateSchema>;

export const NationalApprovalResponseSchema = z.object({
  account_name: z.string().nullable().optional(),
  estimated_cost: z.number().nullable().optional(),
  id: z.string(),
  notes: z.string().nullable(),
  nte_limit: z.number().nullable().optional(),
  order_number: z.string().nullable().optional(),
  parent_account_name: z.string().nullable().optional(),
  quote_nte_fingerprint: z.string().nullable().optional(),
  quote_nte_limit: z.number().nullable().optional(),
  quote_nte_reason: z.string().nullable().optional(),
  quote_nte_total: z.number().nullable().optional(),
  reference_id: z.string(),
  reference_type: z.string(),
  requested_at: z.iso.datetime(),
  requested_by_id: z.string().nullable(),
  requested_by_name: z.string().nullable().optional(),
  reviewed_at: z.iso.datetime().nullable(),
  reviewed_by_id: z.string().nullable(),
  reviewed_by_name: z.string().nullable().optional(),
  status: z.string(),
  territory_id: z.string(),
  territory_name: z.string().nullable().optional(),
  type: z.string(),
  wo_title: z.string().nullable().optional(),
});
export type NationalApprovalResponse = z.infer<typeof NationalApprovalResponseSchema>;

export const CursorPage_NationalApprovalResponse_Schema = z.object({
  next_cursor: z.string().nullable().optional(),
  results: z.array(NationalApprovalResponseSchema),
});
export type CursorPage_NationalApprovalResponse_ = z.infer<typeof CursorPage_NationalApprovalResponse_Schema>;

export const NationalApprovalUpdateSchema = z.object({
  notes: z.string().nullable().optional(),
  status: z.enum(["APPROVED", "REJECTED"]),
});
export type NationalApprovalUpdate = z.infer<typeof NationalApprovalUpdateSchema>;

export const NbdSummarySchema = z.object({
  national_accounts: z.array(NationalAccountRowSchema),
  pending_approvals: z.number().int(),
  pending_quotes: z.number().int(),
});
export type NbdSummary = z.infer<typeof NbdSummarySchema>;

export const CorporateDashboardResponseSchema = z.object({
  corporate_view: z.boolean().optional(),
  nbd: NbdSummarySchema,
  territory_stats: z.array(CorporateTerritoryStatSchema),
  totals: DashboardTotalsSchema,
});
export type CorporateDashboardResponse = z.infer<typeof CorporateDashboardResponseSchema>;

/** A suggested step, in the same shape the attention queue uses for its per-row action so the web renders both surfaces with one component. */
export const NextActionSchema = z.object({
  href: z.string(),
  label: z.string(),
});
export type NextAction = z.infer<typeof NextActionSchema>;

export const AtRiskAccountSchema = z.object({
  company_name: z.string(),
  id: z.string(),
  next_action: NextActionSchema.nullable().optional(),
  reason: z.string().optional(),
  score: z.number(),
});
export type AtRiskAccount = z.infer<typeof AtRiskAccountSchema>;

export const NoteCreateSchema = z.object({
  content: z.string().min(1),
  entity_id: z.string(),
  entity_type: z.string().min(1),
  territory_id: z.string(),
});
export type NoteCreate = z.infer<typeof NoteCreateSchema>;

export const NoteResponseSchema = z.object({
  author_id: z.string().nullable(),
  author_name: z.string().nullable().optional(),
  content: z.string(),
  created_at: z.iso.datetime(),
  edited_at: z.iso.datetime().nullable().optional(),
  entity_id: z.string(),
  entity_type: z.string(),
  id: z.string(),
  territory_id: z.string(),
  updated_at: z.iso.datetime(),
});
export type NoteResponse = z.infer<typeof NoteResponseSchema>;

export const CursorPage_NoteResponse_Schema = z.object({
  next_cursor: z.string().nullable().optional(),
  results: z.array(NoteResponseSchema),
});
export type CursorPage_NoteResponse_ = z.infer<typeof CursorPage_NoteResponse_Schema>;

export const NoteUpdateSchema = z.object({
  content: z.string().min(1),
});
export type NoteUpdate = z.infer<typeof NoteUpdateSchema>;

/** One entry in a person's in-app feed (#427). `id` is the DynamoDB sort key, not a uuid of its own — it is what `PATCH /{id}/read` names, and it carries the timestamp that orders the feed. Exposing the storage key is deliberate: inventing a second identifier would mean a lookup on every mark-read for no gain. */
export const NotificationFeedItemSchema = z.object({
  body: z.string().nullable().optional(),
  created_at: z.iso.datetime(),
  entity_id: z.string().nullable().optional(),
  entity_type: z.string().nullable().optional(),
  event_type: z.string(),
  id: z.string(),
  is_read: z.boolean(),
  link: z.string().nullable().optional(),
  title: z.string(),
});
export type NotificationFeedItem = z.infer<typeof NotificationFeedItemSchema>;

export const NotificationPreferenceResponseSchema = z.object({
  channel: z.string(),
  created_at: z.iso.datetime(),
  event_type: z.string(),
  id: z.string(),
  is_enabled: z.boolean(),
  updated_at: z.iso.datetime(),
});
export type NotificationPreferenceResponse = z.infer<typeof NotificationPreferenceResponseSchema>;

export const CursorPage_NotificationPreferenceResponse_Schema = z.object({
  next_cursor: z.string().nullable().optional(),
  results: z.array(NotificationPreferenceResponseSchema),
});
export type CursorPage_NotificationPreferenceResponse_ = z.infer<typeof CursorPage_NotificationPreferenceResponse_Schema>;

export const NotificationPreferenceUpsertSchema = z.object({
  channel: z.enum(["email", "in_app"]),
  event_type: z.string().min(1).max(128),
  is_enabled: z.boolean().optional(),
});
export type NotificationPreferenceUpsert = z.infer<typeof NotificationPreferenceUpsertSchema>;

export const OperationsSummarySchema = z.object({
  active_recurring_schedules: z.number().int().optional(),
  open_inspections: z.number().int().optional(),
  open_service_requests: z.number().int().optional(),
  open_tasks: z.number().int().optional(),
  open_work_orders: z.number().int().optional(),
  pending_approvals: z.number().int(),
  visits_tonight: z.number().int().optional(),
});
export type OperationsSummary = z.infer<typeof OperationsSummarySchema>;

/** One competing option nested inside the card. Only the primary counts in the pipeline. */
export const OpportunityOptionSchema = z.object({
  is_primary: z.boolean(),
  quote_id: z.string(),
  quote_number: z.string().nullable().optional(),
  status: z.string(),
  total: z.number().nullable().optional(),
  valid_until: z.string().nullable().optional(),
});
export type OpportunityOption = z.infer<typeof OpportunityOptionSchema>;

export const OpportunityResponseSchema = z.object({
  account_id: z.string(),
  account_name: z.string().nullable().optional(),
  actual_close_date: z.string().nullable().optional(),
  contact_id: z.string().nullable().optional(),
  created_at: z.iso.datetime(),
  description: z.string().nullable().optional(),
  expected_close_date: z.string().nullable().optional(),
  id: z.string(),
  location_id: z.string().nullable().optional(),
  lost_reason: z.string().nullable().optional(),
  options: z.array(OpportunityOptionSchema).optional(),
  owner_id: z.string().nullable().optional(),
  probability: z.number().nullable().optional(),
  source: z.string().nullable().optional(),
  stage: z.string(),
  title: z.string(),
  updated_at: z.iso.datetime(),
  value: z.number().nullable().optional(),
  value_source: z.enum(["QUOTE", "ESTIMATE", "NONE"]).optional(),
  version: z.number().int(),
  weighted_value: z.number().nullable().optional(),
});
export type OpportunityResponse = z.infer<typeof OpportunityResponseSchema>;

export const CursorPage_OpportunityResponse_Schema = z.object({
  next_cursor: z.string().nullable().optional(),
  results: z.array(OpportunityResponseSchema),
});
export type CursorPage_OpportunityResponse_ = z.infer<typeof CursorPage_OpportunityResponse_Schema>;

export const OpportunityUpdateSchema = z.object({
  description: z.string().nullable().optional(),
  expected_close_date: z.string().nullable().optional(),
  lost_reason: z.string().nullable().optional(),
  owner_id: z.string().nullable().optional(),
  probability: z.number().min(0).max(100).nullable().optional(),
  stage: z.string().nullable().optional(),
  title: z.string().min(1).max(255).nullable().optional(),
  value: z.number().nullable().optional(),
  version: z.number().int(),
});
export type OpportunityUpdate = z.infer<typeof OpportunityUpdateSchema>;

export const PaymentConfigResponseSchema = z.object({
  publishable_key: z.string().nullable(),
});
export type PaymentConfigResponse = z.infer<typeof PaymentConfigResponseSchema>;

export const PaymentIntentResponseSchema = z.object({
  amount: z.number().int(),
  client_secret: z.string(),
  method: z.string().nullable().optional(),
  stripe_account: z.string().nullable().optional(),
});
export type PaymentIntentResponse = z.infer<typeof PaymentIntentResponseSchema>;

/** #149b: what a given payment method would actually cost the payer, so a pay button can show the total BEFORE a PaymentIntent is created. `fee_cents` is a processing-cost surcharge passed through to the payer (never a Vantage cut — no `application_fee_amount` is ever taken); it exists so the payee nets the full amount despite Stripe's own cut. */
export const PaymentMethodQuoteSchema = z.object({
  fee_cents: z.number().int(),
  total_cents: z.number().int(),
});
export type PaymentMethodQuote = z.infer<typeof PaymentMethodQuoteSchema>;

export const PaymentMethodResponseSchema = z.object({
  brand: z.string().nullable().optional(),
  exp_month: z.number().int().nullable().optional(),
  exp_year: z.number().int().nullable().optional(),
  id: z.string(),
  is_default: z.boolean().optional(),
  last4: z.string().nullable().optional(),
  type: z.string(),
});
export type PaymentMethodResponse = z.infer<typeof PaymentMethodResponseSchema>;

export const PaymentQuoteResponseSchema = z.object({
  ach: PaymentMethodQuoteSchema,
  card: PaymentMethodQuoteSchema,
  invoice_amount_cents: z.number().int(),
});
export type PaymentQuoteResponse = z.infer<typeof PaymentQuoteResponseSchema>;

/** A bid request sourced from this quote (#357), back-linked via bid_requests.quote_id. One request per quote LINE — a line is one service at one location — so the ref carries the line it prices (`quote_item_id`) alongside the site, and the award columns the quote's "Sourcing" section renders: how many bids are in, and who won for how much. */
export const QuoteBidRequestRefSchema = z.object({
  awarded_amount: z.number().nullable().optional(),
  awarded_sub_name: z.string().nullable().optional(),
  bid_count: z.number().int().optional(),
  id: z.string(),
  location_id: z.string().nullable().optional(),
  location_name: z.string().nullable().optional(),
  quote_item_id: z.string().nullable().optional(),
  request_number: z.string(),
  status: z.string(),
});
export type QuoteBidRequestRef = z.infer<typeof QuoteBidRequestRefSchema>;

/** Source bid requests from a quote (#357) — one request per quote LINE. `quote_item_ids` omitted means every line on the quote; naming a subset lets staff go out to market for part of a quote and price the rest themselves. Bundling several lines into ONE RFP is deliberately not expressible here: the ratified shape is one line per request, and a bundled RFP is a separate future enhancement rather than a flag on this body. */
export const QuoteBidRequestsCreateSchema = z.object({
  deadline: z.iso.datetime().nullable().optional(),
  notes: z.string().nullable().optional(),
  quote_item_ids: z.array(z.string()).nullable().optional(),
});
export type QuoteBidRequestsCreate = z.infer<typeof QuoteBidRequestsCreateSchema>;

export const QuoteConvertAssignmentSchema = z.object({
  location_id: z.string().nullable().optional(),
  quote_item_id: z.string().nullable().optional(),
  subcontractor_id: z.string(),
});
export type QuoteConvertAssignment = z.infer<typeof QuoteConvertAssignmentSchema>;

export const QuoteConvertRequestSchema = z.object({
  scheduled_date: z.iso.datetime().nullable().optional(),
  subcontractor_assignments: z.array(QuoteConvertAssignmentSchema).optional(),
});
export type QuoteConvertRequest = z.infer<typeof QuoteConvertRequestSchema>;

export const QuoteConvertResponseSchema = z.object({
  converted_invoice_id: z.string().nullable(),
  converted_work_order_id: z.string().nullable(),
  invoice_ids: z.array(z.string()),
  invoice_numbers: z.array(z.string()),
  quote_id: z.string(),
  recurring_schedule_ids: z.array(z.string()).optional(),
  status: z.string(),
  unassigned_work_order_ids: z.array(z.string()).optional(),
  work_order_ids: z.array(z.string()),
  work_order_numbers: z.array(z.string()),
});
export type QuoteConvertResponse = z.infer<typeof QuoteConvertResponseSchema>;

export const QuoteCreateSchema = z.object({
  account_id: z.string(),
  bid_request_id: z.string().nullable().optional(),
  contact_id: z.string().nullable().optional(),
  deal_id: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
  location_id: z.string().nullable().optional(),
  notes: z.string().nullable().optional(),
  po_number: z.string().nullable().optional(),
  price_effective_from: z.string().nullable().optional(),
  recurring: z.boolean().optional(),
  service_category: z.string().nullable().optional(),
  snapshot_contact_email: z.string().nullable().optional(),
  snapshot_contact_name: z.string().nullable().optional(),
  snapshot_contact_phone: z.string().nullable().optional(),
  tax: z.number().nullable().optional(),
  title: z.string().min(1).max(255),
  valid_until: z.string().nullable().optional(),
});
export type QuoteCreate = z.infer<typeof QuoteCreateSchema>;

export const QuoteItemCreateSchema = z.object({
  basis: z.enum(["flat", "sqft", "restroom", "floor", "acre"]).nullable().optional(),
  bid_id: z.string().nullable().optional(),
  billing_basis: z.enum(["PER_VISIT", "WEEKLY", "MONTHLY", "QUARTERLY"]).nullable().optional(),
  calculation_method: z.enum(["FIXED_PERIOD", "PER_VISIT", "PER_HOUR"]).nullable().optional(),
  contractor_cost: z.number().nullable().optional(),
  description: z.string().min(1),
  is_recurring: z.boolean().optional(),
  location_id: z.string().nullable().optional(),
  quantity: z.number(),
  service_category: z.string().nullable().optional(),
  service_days: z.string().nullable().optional(),
  service_end_date: z.string().nullable().optional(),
  service_frequency: z.enum(["DAILY", "WEEKLY", "BIWEEKLY", "MONTHLY", "QUARTERLY", "YEARLY"]).nullable().optional(),
  service_id: z.string().nullable().optional(),
  service_start_date: z.string().nullable().optional(),
  service_time: z.string().nullable().optional(),
  unit_price: z.number().optional(),
});
export type QuoteItemCreate = z.infer<typeof QuoteItemCreateSchema>;

export const QuoteItemResponseSchema = z.object({
  basis: z.string().nullable(),
  bid_id: z.string().nullable(),
  billing_basis: z.string().nullable().optional(),
  calculation_method: z.string().nullable().optional(),
  contractor_cost: z.number().nullable(),
  description: z.string(),
  id: z.string(),
  is_recurring: z.boolean().optional(),
  location_id: z.string().nullable(),
  margin_pct: z.number().nullable(),
  quantity: z.number(),
  quote_id: z.string(),
  service_category: z.string().nullable(),
  service_days: z.string().nullable().optional(),
  service_end_date: z.string().nullable().optional(),
  service_frequency: z.string().nullable().optional(),
  service_id: z.string().nullable(),
  service_start_date: z.string().nullable().optional(),
  service_time: z.string().nullable().optional(),
  territory_id: z.string(),
  total_price: z.number(),
  unit_price: z.number(),
  work_order_id: z.string().nullable().optional(),
});
export type QuoteItemResponse = z.infer<typeof QuoteItemResponseSchema>;

export const QuoteItemUpdateSchema = z.object({
  basis: z.enum(["flat", "sqft", "restroom", "floor", "acre"]).nullable().optional(),
  billing_basis: z.enum(["PER_VISIT", "WEEKLY", "MONTHLY", "QUARTERLY"]).nullable().optional(),
  calculation_method: z.enum(["FIXED_PERIOD", "PER_VISIT", "PER_HOUR"]).nullable().optional(),
  contractor_cost: z.number().nullable().optional(),
  description: z.string().min(1).nullable().optional(),
  is_recurring: z.boolean().nullable().optional(),
  location_id: z.string().nullable().optional(),
  quantity: z.number().nullable().optional(),
  service_category: z.string().nullable().optional(),
  service_days: z.string().nullable().optional(),
  service_end_date: z.string().nullable().optional(),
  service_frequency: z.enum(["DAILY", "WEEKLY", "BIWEEKLY", "MONTHLY", "QUARTERLY", "YEARLY"]).nullable().optional(),
  service_id: z.string().nullable().optional(),
  service_start_date: z.string().nullable().optional(),
  service_time: z.string().nullable().optional(),
  unit_price: z.number().nullable().optional(),
});
export type QuoteItemUpdate = z.infer<typeof QuoteItemUpdateSchema>;

/** Internal-only exception state for the whole client quote, including tax. */
export const QuoteNteStatusSchema = z.object({
  approval_id: z.string().nullable().optional(),
  approval_required: z.boolean(),
  approval_status: z.string().nullable().optional(),
  can_review: z.boolean().optional(),
  limit: z.number().nullable().optional(),
  manager_id: z.string().nullable().optional(),
  manager_name: z.string().nullable().optional(),
  request_reason: z.string().nullable().optional(),
  review_notes: z.string().nullable().optional(),
  reviewed_by_name: z.string().nullable().optional(),
  revision_required: z.boolean().optional(),
  total: z.number(),
});
export type QuoteNteStatus = z.infer<typeof QuoteNteStatusSchema>;

export const QuoteReadinessIssueSchema = z.object({
  code: z.string(),
  field: z.string().nullable().optional(),
  message: z.string(),
});
export type QuoteReadinessIssue = z.infer<typeof QuoteReadinessIssueSchema>;

export const QuoteReadinessResponseSchema = z.object({
  issues: z.array(QuoteReadinessIssueSchema),
  ready: z.boolean(),
  route: z.enum(["DIRECT_CLIENT", "NBD_REVIEW"]),
});
export type QuoteReadinessResponse = z.infer<typeof QuoteReadinessResponseSchema>;

export const QuoteRequestCreateSchema = z.object({
  account_id: z.string(),
  contact_id: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
  location_id: z.string().nullable().optional(),
  notes: z.string().nullable().optional(),
  requested_date: z.string().nullable().optional(),
  service_category: z.string().nullable().optional(),
  source: z.string().nullable().optional(),
  title: z.string().min(1).max(255),
});
export type QuoteRequestCreate = z.infer<typeof QuoteRequestCreateSchema>;

export const QuoteRequestDistributeSchema = z.object({
  all_active_sites: z.boolean().optional(),
  budget_max: z.number().nullable().optional(),
  budget_min: z.number().nullable().optional(),
  deadline: z.iso.datetime().nullable().optional(),
  description: z.string().nullable().optional(),
  location_ids: z.array(z.string()).optional(),
  national_account_id: z.string(),
  notes: z.string().nullable().optional(),
  recurring: z.boolean().optional(),
  scheduled_end: z.iso.datetime().nullable().optional(),
  scheduled_start: z.iso.datetime().nullable().optional(),
  service_category_id: z.string().nullable().optional(),
  service_frequency: z.enum(["DAILY", "WEEKLY", "BIWEEKLY", "MONTHLY", "QUARTERLY", "YEARLY"]).nullable().optional(),
  title: z.string().min(1).max(255),
});
export type QuoteRequestDistribute = z.infer<typeof QuoteRequestDistributeSchema>;

export const QuoteRequestPhotoCreateSchema = z.object({
  file_size: z.number().int().max(10485760).gt(0),
  file_type: z.enum(["image/jpeg", "image/png", "image/webp"]),
  filename: z.string().min(1).max(255),
  upload_id: z.string(),
});
export type QuoteRequestPhotoCreate = z.infer<typeof QuoteRequestPhotoCreateSchema>;

export const QuoteRequestPhotoResponseSchema = z.object({
  download_url: z.string(),
  filename: z.string(),
  id: z.string(),
});
export type QuoteRequestPhotoResponse = z.infer<typeof QuoteRequestPhotoResponseSchema>;

export const QuoteRequestPhotoUploadSchema = z.object({
  id: z.string(),
  upload_url: z.string().nullable(),
});
export type QuoteRequestPhotoUpload = z.infer<typeof QuoteRequestPhotoUploadSchema>;

export const QuoteRequestResponseSchema = z.object({
  access_notes: z.string().nullable().optional(),
  account_id: z.string(),
  account_name: z.string().nullable().optional(),
  campaign_account_name: z.string().nullable().optional(),
  campaign_budget_max: z.number().nullable().optional(),
  campaign_budget_min: z.number().nullable().optional(),
  campaign_site_count: z.number().int().nullable().optional(),
  campaign_territory_count: z.number().int().nullable().optional(),
  contact_id: z.string().nullable(),
  contact_name: z.string().nullable().optional(),
  created_at: z.iso.datetime(),
  created_by_id: z.string().nullable().optional(),
  created_by_name: z.string().nullable().optional(),
  description: z.string().nullable(),
  distribution_batch_id: z.string().nullable().optional(),
  id: z.string(),
  location_id: z.string().nullable(),
  location_name: z.string().nullable().optional(),
  notes: z.string().nullable(),
  quote_id: z.string().nullable(),
  quote_number: z.string().nullable().optional(),
  quote_status: z.string().nullable().optional(),
  quote_total: z.number().nullable().optional(),
  request_number: z.string().nullable().optional(),
  requested_date: z.string().nullable(),
  service_category: z.string().nullable(),
  source: z.string().nullable(),
  status: z.string(),
  territory_id: z.string(),
  territory_name: z.string().nullable().optional(),
  title: z.string(),
  updated_at: z.iso.datetime(),
  version: z.number().int(),
});
export type QuoteRequestResponse = z.infer<typeof QuoteRequestResponseSchema>;

export const CursorPage_QuoteRequestResponse_Schema = z.object({
  next_cursor: z.string().nullable().optional(),
  results: z.array(QuoteRequestResponseSchema),
});
export type CursorPage_QuoteRequestResponse_ = z.infer<typeof CursorPage_QuoteRequestResponse_Schema>;

export const QuoteRequestDistributeResponseSchema = z.object({
  distributed: z.number().int(),
  quote_requests: z.array(QuoteRequestResponseSchema),
  skipped: z.number().int().optional(),
});
export type QuoteRequestDistributeResponse = z.infer<typeof QuoteRequestDistributeResponseSchema>;

export const QuoteRequestUpdateSchema = z.object({
  contact_id: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
  location_id: z.string().nullable().optional(),
  notes: z.string().nullable().optional(),
  requested_date: z.string().nullable().optional(),
  service_category: z.string().nullable().optional(),
  status: z.enum(["OPEN", "IN_REVIEW", "QUOTED", "DECLINED", "CANCELLED"]).nullable().optional(),
  title: z.string().min(1).max(255).nullable().optional(),
  version: z.number().int(),
});
export type QuoteRequestUpdate = z.infer<typeof QuoteRequestUpdateSchema>;

/** Create an editable replacement without changing the executed agreement. */
export const QuoteRevisionCreateSchema = z.object({
  valid_until: z.string().nullable().optional(),
});
export type QuoteRevisionCreate = z.infer<typeof QuoteRevisionCreateSchema>;

/** One deliberate send action: submit national work to NBD or invite the named client. */
export const QuoteSendCreateSchema = z.object({
  contact_id: z.string().nullable().optional(),
  expires_in_hours: z.number().int().min(1).max(2160).nullable().optional(),
  replace_existing: z.boolean().optional(),
});
export type QuoteSendCreate = z.infer<typeof QuoteSendCreateSchema>;

/** One event on a quote's timeline (#511 / SALES-045) — the served replacement for the five milestones the web fabricated from the quote's own stamps. A child sub-response: it renders only the actor's name, resolved by user_display_name in the SELECT. RESTORED 2026-08-29, having been deleted the same day by the zero-consumer endpoint audit. The feed had no caller because vantage-web has not adopted it yet — it still fabricates the milestone strip locally, which is the very thing this was built to replace. The endpoint was ahead of its UI, not behind it, and deleting it removed the served answer while leaving the fabricated one in place. */
export const QuoteTimelineEventResponseSchema = z.object({
  created_at: z.iso.datetime(),
  description: z.string().nullable().optional(),
  id: z.string(),
  title: z.string(),
  type: z.string(),
  user_id: z.string().nullable().optional(),
  user_name: z.string().nullable().optional(),
});
export type QuoteTimelineEventResponse = z.infer<typeof QuoteTimelineEventResponseSchema>;

export const QuoteUpdateSchema = z.object({
  contact_id: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
  location_id: z.string().nullable().optional(),
  notes: z.string().nullable().optional(),
  po_number: z.string().nullable().optional(),
  price_effective_from: z.string().nullable().optional(),
  recurring: z.boolean().nullable().optional(),
  service_category: z.string().nullable().optional(),
  snapshot_contact_email: z.string().nullable().optional(),
  snapshot_contact_name: z.string().nullable().optional(),
  snapshot_contact_phone: z.string().nullable().optional(),
  status: z.enum(["DRAFT", "SENT", "VIEWED", "ACCEPTED", "DECLINED", "EXPIRED", "WITHDRAWN"]).nullable().optional(),
  tax: z.number().nullable().optional(),
  title: z.string().min(1).max(255).nullable().optional(),
  valid_until: z.string().nullable().optional(),
  version: z.number().int(),
});
export type QuoteUpdate = z.infer<typeof QuoteUpdateSchema>;

export const QuoteWorkOrderRefSchema = z.object({
  id: z.string(),
  order_number: z.string(),
  status: z.string(),
});
export type QuoteWorkOrderRef = z.infer<typeof QuoteWorkOrderRefSchema>;

export const QuoteResponseSchema = z.object({
  accepted_at: z.iso.datetime().nullable(),
  account_id: z.string(),
  account_name: z.string().nullable().optional(),
  bid_request_id: z.string().nullable(),
  bid_requests: z.array(QuoteBidRequestRefSchema).optional(),
  contact_email: z.string().nullable().optional(),
  contact_id: z.string().nullable(),
  contact_name: z.string().nullable().optional(),
  converted_invoice_id: z.string().nullable(),
  converted_work_order_id: z.string().nullable(),
  cost_subtotal: z.number().nullable(),
  created_at: z.iso.datetime(),
  created_by_id: z.string().nullable(),
  created_by_name: z.string().nullable().optional(),
  deal_id: z.string().nullable(),
  declined_at: z.iso.datetime().nullable(),
  declined_reason: z.string().nullable().optional(),
  declined_reason_code: z.string().nullable().optional(),
  description: z.string().nullable(),
  id: z.string(),
  is_national: z.boolean().optional(),
  location_count: z.number().int().nullable().optional(),
  location_id: z.string().nullable(),
  location_name: z.string().nullable().optional(),
  margin_pct: z.number().nullable(),
  notes: z.string().nullable(),
  nte: QuoteNteStatusSchema.nullable().optional(),
  po_number: z.string().nullable(),
  price_effective_from: z.string().nullable(),
  quote_number: z.string(),
  recurring: z.boolean(),
  released_to_client: z.boolean(),
  revision_number: z.number().int().optional(),
  revision_of_quote_id: z.string().nullable().optional(),
  sent_at: z.iso.datetime().nullable(),
  service_category: z.string().nullable(),
  snapshot_contact_email: z.string().nullable(),
  snapshot_contact_name: z.string().nullable(),
  snapshot_contact_phone: z.string().nullable(),
  source_quote_request_id: z.string().nullable().optional(),
  status: z.string(),
  subtotal: z.number().nullable(),
  superseded_at: z.iso.datetime().nullable().optional(),
  superseded_by_quote_id: z.string().nullable().optional(),
  tax: z.number().nullable(),
  territory_id: z.string(),
  territory_name: z.string().nullable().optional(),
  title: z.string(),
  total: z.number().nullable(),
  updated_at: z.iso.datetime(),
  updated_by_id: z.string().nullable(),
  updated_by_name: z.string().nullable().optional(),
  valid_until: z.string().nullable(),
  version: z.number().int(),
  viewed_at: z.iso.datetime().nullable(),
  work_orders: z.array(QuoteWorkOrderRefSchema).optional(),
});
export type QuoteResponse = z.infer<typeof QuoteResponseSchema>;

export const CursorPage_QuoteResponse_Schema = z.object({
  next_cursor: z.string().nullable().optional(),
  results: z.array(QuoteResponseSchema),
});
export type CursorPage_QuoteResponse_ = z.infer<typeof CursorPage_QuoteResponse_Schema>;

/** #69: apply a weekly serviced-days pattern to many schedules at once (the rollout tool). `service_days` are JS getDay() ints (0 = Sunday … 6 = Saturday), matching the web picker; they are stored as an integer CSV that the generator expands (0=Sunday). */
export const RecurringScheduleBulkPatternRequestSchema = z.object({
  overwrite: z.boolean().optional(),
  schedule_ids: z.array(z.string()),
  service_days: z.array(z.number().int()),
});
export type RecurringScheduleBulkPatternRequest = z.infer<typeof RecurringScheduleBulkPatternRequestSchema>;

export const RecurringScheduleBulkPatternResultSchema = z.object({
  denied: z.number().int(),
  skipped_existing: z.number().int(),
  updated: z.number().int(),
});
export type RecurringScheduleBulkPatternResult = z.infer<typeof RecurringScheduleBulkPatternResultSchema>;

export const RecurringScheduleChecklistItemCreateSchema = z.object({
  label: z.string().min(1),
  requires_note: z.boolean().optional(),
  requires_photo: z.boolean().optional(),
  sort_order: z.number().int().optional(),
});
export type RecurringScheduleChecklistItemCreate = z.infer<typeof RecurringScheduleChecklistItemCreateSchema>;

export const RecurringScheduleChecklistItemResponseSchema = z.object({
  created_at: z.iso.datetime(),
  created_by_id: z.string().nullable(),
  id: z.string(),
  label: z.string(),
  recurring_schedule_id: z.string(),
  requires_note: z.boolean(),
  requires_photo: z.boolean(),
  sort_order: z.number().int(),
  territory_id: z.string(),
});
export type RecurringScheduleChecklistItemResponse = z.infer<typeof RecurringScheduleChecklistItemResponseSchema>;

export const RecurringScheduleChecklistItemUpdateSchema = z.object({
  label: z.string().min(1).nullable().optional(),
  requires_note: z.boolean().nullable().optional(),
  requires_photo: z.boolean().nullable().optional(),
  sort_order: z.number().int().nullable().optional(),
});
export type RecurringScheduleChecklistItemUpdate = z.infer<typeof RecurringScheduleChecklistItemUpdateSchema>;

export const RecurringScheduleCreateSchema = z.object({
  account_id: z.string(),
  assigned_to_id: z.string().nullable().optional(),
  day_of_month: z.number().int().min(1).max(31).nullable().optional(),
  description: z.string().nullable().optional(),
  dtstart: z.string().nullable().optional(),
  estimated_cost: z.number().nullable().optional(),
  location_id: z.string().nullable().optional(),
  next_generation: z.iso.datetime().nullable().optional(),
  notes: z.string().nullable().optional(),
  order_type: z.string().nullable().optional(),
  po_number: z.string().nullable().optional(),
  priority: z.string().nullable().optional(),
  rrule: z.string().nullable().optional(),
  rrule_until: z.string().nullable().optional(),
  service_category: z.string().nullable().optional(),
  service_days: z.string().nullable().optional(),
  subcontractor_id: z.string().nullable().optional(),
  time_of_day: z.string().nullable().optional(),
  title: z.string().min(1).max(255),
  unit_price: z.number().nullable().optional(),
});
export type RecurringScheduleCreate = z.infer<typeof RecurringScheduleCreateSchema>;

export const RecurringScheduleUpdateSchema = z.object({
  assigned_to_id: z.string().nullable().optional(),
  day_of_month: z.number().int().min(1).max(31).nullable().optional(),
  description: z.string().nullable().optional(),
  dtstart: z.string().nullable().optional(),
  estimated_cost: z.number().nullable().optional(),
  is_active: z.boolean().nullable().optional(),
  location_id: z.string().nullable().optional(),
  next_generation: z.iso.datetime().nullable().optional(),
  notes: z.string().nullable().optional(),
  order_type: z.string().nullable().optional(),
  po_number: z.string().nullable().optional(),
  priority: z.string().nullable().optional(),
  rrule: z.string().nullable().optional(),
  rrule_until: z.string().nullable().optional(),
  service_category: z.string().nullable().optional(),
  service_days: z.string().nullable().optional(),
  subcontractor_id: z.string().nullable().optional(),
  time_of_day: z.string().nullable().optional(),
  title: z.string().min(1).max(255).nullable().optional(),
  unit_price: z.number().nullable().optional(),
  version: z.number().int(),
});
export type RecurringScheduleUpdate = z.infer<typeof RecurringScheduleUpdateSchema>;

/** One row of the schedule detail's Work orders tab (#618). A deliberately narrow projection, not a `WorkOrderResponse`. The tab renders an id, a status chip, a number and a date, and the tab LABEL carries the count - so the whole tab has always read zero. Embedding the full work-order model would put ~40 fields per row behind a tab that shows four, on a response the detail page fetches every time. */
export const RecurringScheduleWorkOrderSchema = z.object({
  created_at: z.iso.datetime().nullable().optional(),
  id: z.string(),
  order_number: z.string().nullable().optional(),
  scheduled_date: z.string().nullable().optional(),
  status: z.string(),
});
export type RecurringScheduleWorkOrder = z.infer<typeof RecurringScheduleWorkOrderSchema>;

export const RecurringScheduleResponseSchema = z.object({
  account_id: z.string(),
  account_name: z.string().nullable().optional(),
  account_status: z.string().nullable().optional(),
  assigned_to_id: z.string().nullable(),
  assigned_to_name: z.string().nullable().optional(),
  created_at: z.iso.datetime(),
  created_by_id: z.string().nullable(),
  created_by_name: z.string().nullable().optional(),
  day_of_month: z.number().int().nullable(),
  day_of_week: z.number().int().nullable(),
  description: z.string().nullable(),
  dtstart: z.string().nullable().optional(),
  estimated_cost: z.number().nullable(),
  frequency: z.string().nullable(),
  id: z.string(),
  is_active: z.boolean(),
  last_generated: z.iso.datetime().nullable(),
  location_address: z.string().nullable().optional(),
  location_id: z.string().nullable(),
  location_name: z.string().nullable().optional(),
  next_generation: z.iso.datetime().nullable(),
  notes: z.string().nullable(),
  open_work_order_id: z.string().nullable().optional(),
  open_work_order_number: z.string().nullable().optional(),
  order_type: z.string().nullable(),
  pending_approval: z.boolean(),
  po_number: z.string().nullable(),
  priority: z.string().nullable(),
  recurring: z.boolean(),
  rrule: z.string().nullable().optional(),
  rrule_until: z.string().nullable().optional(),
  service_category: z.string().nullable(),
  service_days: z.string().nullable(),
  subcontractor_id: z.string().nullable(),
  subcontractor_name: z.string().nullable().optional(),
  territory_id: z.string(),
  territory_name: z.string().nullable().optional(),
  time_of_day: z.string().nullable(),
  title: z.string(),
  unit_price: z.number().nullable().optional(),
  updated_at: z.iso.datetime(),
  updated_by_id: z.string().nullable(),
  updated_by_name: z.string().nullable().optional(),
  version: z.number().int(),
  work_orders: z.array(RecurringScheduleWorkOrderSchema).nullable().optional(),
});
export type RecurringScheduleResponse = z.infer<typeof RecurringScheduleResponseSchema>;

export const CursorPage_RecurringScheduleResponse_Schema = z.object({
  next_cursor: z.string().nullable().optional(),
  results: z.array(RecurringScheduleResponseSchema),
});
export type CursorPage_RecurringScheduleResponse_ = z.infer<typeof CursorPage_RecurringScheduleResponse_Schema>;

export const ReportAccountRowSchema = z.object({
  city: z.string().nullable().optional(),
  company_name: z.string().nullable().optional(),
  count: z.number().int().nullable().optional(),
  id: z.string().nullable().optional(),
  service_requests: z.number().int().nullable().optional(),
  state: z.string().nullable().optional(),
  status: z.string().nullable().optional(),
  total_value: z.number().nullable().optional(),
  value: z.number().nullable().optional(),
  work_orders: z.number().int().nullable().optional(),
});
export type ReportAccountRow = z.infer<typeof ReportAccountRowSchema>;

export const ReportAccountsBlockSchema = z.object({
  new_accounts: z.number().int().nullable().optional(),
});
export type ReportAccountsBlock = z.infer<typeof ReportAccountsBlockSchema>;

export const ReportBidStatSchema = z.object({
  company_name: z.string().nullable().optional(),
  id: z.string().nullable().optional(),
  total_bids: z.number().int().nullable().optional(),
  won_bids: z.number().int().nullable().optional(),
});
export type ReportBidStat = z.infer<typeof ReportBidStatSchema>;

export const ReportBidsBlockSchema = z.object({
  accepted_bids: z.number().int().nullable().optional(),
  total_bids: z.number().int().nullable().optional(),
  total_requests: z.number().int().nullable().optional(),
});
export type ReportBidsBlock = z.infer<typeof ReportBidsBlockSchema>;

export const ReportCountsSchema = z.object({
  cancelled: z.number().int().nullable().optional(),
  completed: z.number().int().nullable().optional(),
  converted: z.number().int().nullable().optional(),
  in_progress: z.number().int().nullable().optional(),
  open: z.number().int().nullable().optional(),
  total: z.number().int().nullable().optional(),
  total_value: z.number().nullable().optional(),
});
export type ReportCounts = z.infer<typeof ReportCountsSchema>;

export const ReportItemRowSchema = z.object({
  count: z.number().int().nullable().optional(),
  month: z.string().nullable().optional(),
  orders: z.number().int().nullable().optional(),
  priority: z.string().nullable().optional(),
  revenue: z.number().nullable().optional(),
  service_category: z.string().nullable().optional(),
  status: z.string().nullable().optional(),
});
export type ReportItemRow = z.infer<typeof ReportItemRowSchema>;

export const ReportPerfRowSchema = z.object({
  company_name: z.string().nullable().optional(),
  completed_orders: z.number().int().optional(),
  id: z.string().nullable().optional(),
  service_categories: z.string().nullable().optional(),
  total_orders: z.number().int().optional(),
  total_revenue: z.number().nullable().optional(),
});
export type ReportPerfRow = z.infer<typeof ReportPerfRowSchema>;

export const ReportsResponseSchema = z.object({
  accountActivity: z.array(ReportAccountRowSchema).nullable().optional(),
  accounts: ReportAccountsBlockSchema.nullable().optional(),
  avgCompletionDays: z.number().nullable().optional(),
  bidStats: z.array(ReportBidStatSchema).nullable().optional(),
  bids: ReportBidsBlockSchema.nullable().optional(),
  byCategory: z.array(ReportItemRowSchema).nullable().optional(),
  byPriority: z.array(ReportItemRowSchema).nullable().optional(),
  byStatus: z.array(ReportItemRowSchema).nullable().optional(),
  inspections: ReportCountsSchema.nullable().optional(),
  performance: z.array(ReportPerfRowSchema).nullable().optional(),
  serviceRequests: ReportCountsSchema.nullable().optional(),
  topAccounts: z.array(ReportAccountRowSchema).nullable().optional(),
  workOrders: ReportCountsSchema.nullable().optional(),
});
export type ReportsResponse = z.infer<typeof ReportsResponseSchema>;

export const RoleItemSchema = z.object({
  key: z.string(),
  name: z.string(),
  portal: z.string(),
});
export type RoleItem = z.infer<typeof RoleItemSchema>;

export const SRTriageRequestSchema = z.object({
  action: z.enum(["corrective_visit", "corrective_standing", "schedule_change"]),
  resolution_notes: z.string().nullable().optional(),
  schedule_id: z.string().nullable().optional(),
  service_days: z.string().nullable().optional(),
  task: z.string().max(500).nullable().optional(),
  visit_date: z.string().nullable().optional(),
});
export type SRTriageRequest = z.infer<typeof SRTriageRequestSchema>;

export const SalesSummarySchema = z.object({
  pending_quotes: z.number().int(),
});
export type SalesSummary = z.infer<typeof SalesSummarySchema>;

export const ScheduleExceptionCreateSchema = z.object({
  exception_date: z.string(),
  kind: z.enum(["skip", "add", "reschedule"]),
  reason: z.string().nullable().optional(),
});
export type ScheduleExceptionCreate = z.infer<typeof ScheduleExceptionCreateSchema>;

export const ScheduleExceptionResponseSchema = z.object({
  created_at: z.iso.datetime(),
  exception_date: z.string(),
  id: z.string(),
  kind: z.string(),
  reason: z.string().nullable(),
  territory_id: z.string(),
  work_order_id: z.string(),
});
export type ScheduleExceptionResponse = z.infer<typeof ScheduleExceptionResponseSchema>;

export const ScopeClassificationResponseSchema = z.object({
  classifier_version: z.string(),
  confidence: z.number(),
  created_at: z.iso.datetime(),
  created_by_id: z.string().nullable().optional(),
  human_reason: z.string().nullable().optional(),
  human_verdict: z.enum(["IN_SCOPE", "OUT_OF_SCOPE"]).nullable().optional(),
  id: z.string(),
  matched_on: z.string().nullable().optional(),
  request_text: z.string(),
  reviewed_at: z.iso.datetime().nullable().optional(),
  reviewed_by_id: z.string().nullable().optional(),
  reviewed_by_name: z.string().nullable().optional(),
  sow_cell_id: z.string().nullable().optional(),
  sow_item_id: z.string().nullable().optional(),
  sow_item_label: z.string().nullable().optional(),
  subject_id: z.string(),
  subject_type: z.string(),
  territory_id: z.string(),
  verdict: z.enum(["IN_SCOPE", "OUT_OF_SCOPE", "NEEDS_HUMAN"]),
});
export type ScopeClassificationResponse = z.infer<typeof ScopeClassificationResponseSchema>;

export const CursorPage_ScopeClassificationResponse_Schema = z.object({
  next_cursor: z.string().nullable().optional(),
  results: z.array(ScopeClassificationResponseSchema),
});
export type CursorPage_ScopeClassificationResponse_ = z.infer<typeof CursorPage_ScopeClassificationResponse_Schema>;

/** The human's disposition. The reason is required — an override with no reason is not a record of a decision, and Decision B makes the citation and the reason both mandatory. */
export const ScopeClassificationReviewSchema = z.object({
  human_reason: z.string().min(1),
  human_verdict: z.enum(["IN_SCOPE", "OUT_OF_SCOPE"]),
});
export type ScopeClassificationReview = z.infer<typeof ScopeClassificationReviewSchema>;

export const ScopeClassifyRequestSchema = z.object({
  as_of: z.string().nullable().optional(),
  location_id: z.string(),
  request_text: z.string().min(1),
  service_category: z.string().nullable().optional(),
  subject_id: z.string(),
  subject_type: z.enum(["SERVICE_REQUEST", "CHANGE_ORDER"]),
});
export type ScopeClassifyRequest = z.infer<typeof ScopeClassifyRequestSchema>;

/** The evidence behind an accepted quote. Surfaced on the CRM-side detail so staff can see what was signed and check it, rather than trusting that a re-render still matches. */
export const SealedDocumentResponseSchema = z.object({
  byte_size: z.number().int(),
  consent_version: z.string(),
  content_sha256: z.string(),
  disclosure_version: z.string(),
  id: z.string(),
  signature_method: z.string(),
  signed_at: z.iso.datetime(),
  signer_contact_id: z.string().nullable().optional(),
  signer_email: z.string().nullable().optional(),
  signer_ip: z.string().nullable().optional(),
  signer_name: z.string(),
  signer_user_agent: z.string().nullable().optional(),
});
export type SealedDocumentResponse = z.infer<typeof SealedDocumentResponseSchema>;

export const SearchResultItemSchema = z.object({
  href: z.string(),
  id: z.string(),
  status: z.string().nullable().optional(),
  subtitle: z.string().nullable().optional(),
  title: z.string(),
  type: z.enum(["account", "contact", "work_order", "subcontractor", "invoice", "quote"]),
});
export type SearchResultItem = z.infer<typeof SearchResultItemSchema>;

export const SearchResponseSchema = z.object({
  message: z.string().nullable().optional(),
  results: z.array(SearchResultItemSchema),
});
export type SearchResponse = z.infer<typeof SearchResponseSchema>;

export const ServiceCreateSchema = z.object({
  category_id: z.string().nullable().optional(),
  code: z.string().min(1).max(64),
  is_active: z.boolean().optional(),
  name: z.string().min(1).max(255),
});
export type ServiceCreate = z.infer<typeof ServiceCreateSchema>;

export const ServicePriceCreateSchema = z.object({
  account_id: z.string().nullable().optional(),
  effective_from: z.string(),
  effective_to: z.string().optional(),
  location_id: z.string().nullable().optional(),
  service_id: z.string(),
  territory_id: z.string(),
  unit_price: z.number().min(0),
});
export type ServicePriceCreate = z.infer<typeof ServicePriceCreateSchema>;

export const ServicePriceResolveResponseSchema = z.object({
  effective_from: z.string().nullable().optional(),
  effective_to: z.string().nullable().optional(),
  source_quote_id: z.string().nullable().optional(),
  unit_price: z.number().nullable().optional(),
});
export type ServicePriceResolveResponse = z.infer<typeof ServicePriceResolveResponseSchema>;

export const ServicePriceResponseSchema = z.object({
  account_id: z.string().nullable(),
  created_at: z.iso.datetime(),
  created_by_id: z.string().nullable(),
  effective_from: z.string(),
  effective_to: z.string(),
  id: z.string(),
  location_id: z.string().nullable(),
  service_category: z.string().nullable().optional(),
  service_id: z.string().nullable().optional(),
  source_quote_id: z.string().nullable(),
  territory_id: z.string(),
  unit_price: z.number(),
});
export type ServicePriceResponse = z.infer<typeof ServicePriceResponseSchema>;

export const CursorPage_ServicePriceResponse_Schema = z.object({
  next_cursor: z.string().nullable().optional(),
  results: z.array(ServicePriceResponseSchema),
});
export type CursorPage_ServicePriceResponse_ = z.infer<typeof CursorPage_ServicePriceResponse_Schema>;

export const ServicePriceUpdateSchema = z.object({
  effective_from: z.string().nullable().optional(),
  effective_to: z.string().nullable().optional(),
  unit_price: z.number().min(0).nullable().optional(),
});
export type ServicePriceUpdate = z.infer<typeof ServicePriceUpdateSchema>;

export const ServiceRequestCreateSchema = z.object({
  account_id: z.string(),
  category: z.string().nullable().optional(),
  contact_id: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
  location_id: z.string().nullable().optional(),
  preferred_date: z.iso.datetime().nullable().optional(),
  priority: z.enum(["LOW", "MEDIUM", "HIGH", "URGENT"]).optional(),
  reported_by: z.string().nullable().optional(),
  reported_via: z.string().nullable().optional(),
  title: z.string().min(1).max(255),
});
export type ServiceRequestCreate = z.infer<typeof ServiceRequestCreateSchema>;

/** Confirmed intake content; the Issue supplies its site and that site's account. */
export const ServiceRequestFromIssueCreateSchema = z.object({
  category: z.string().nullable().optional(),
  contact_id: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
  preferred_date: z.iso.datetime().nullable().optional(),
  priority: z.enum(["LOW", "MEDIUM", "HIGH", "URGENT"]).optional(),
  reported_by: z.string().nullable().optional(),
  reported_via: z.string().nullable().optional(),
  title: z.string().min(1).max(255),
});
export type ServiceRequestFromIssueCreate = z.infer<typeof ServiceRequestFromIssueCreateSchema>;

export const ServiceRequestResponseSchema = z.object({
  account_id: z.string(),
  account_name: z.string().nullable().optional(),
  assigned_to_id: z.string().nullable(),
  assigned_to_name: z.string().nullable().optional(),
  category: z.string().nullable(),
  contact_email: z.string().nullable().optional(),
  contact_id: z.string().nullable(),
  contact_name: z.string().nullable().optional(),
  contact_phone: z.string().nullable().optional(),
  created_at: z.iso.datetime(),
  created_by_id: z.string().nullable(),
  created_by_name: z.string().nullable().optional(),
  description: z.string().nullable(),
  id: z.string(),
  location_address: z.string().nullable().optional(),
  location_id: z.string().nullable(),
  location_name: z.string().nullable().optional(),
  pending_approval: z.boolean(),
  preferred_date: z.iso.datetime().nullable(),
  priority: z.string(),
  quote_request_id: z.string().nullable().optional(),
  reported_by: z.string().nullable(),
  reported_via: z.string().nullable(),
  request_number: z.string(),
  resolution_notes: z.string().nullable(),
  resolution_type: z.string().nullable().optional(),
  resolved_at: z.iso.datetime().nullable(),
  source: z.string().nullable().optional(),
  source_message_id: z.string().nullable().optional(),
  status: z.string(),
  territory_id: z.string(),
  territory_name: z.string().nullable().optional(),
  title: z.string(),
  updated_at: z.iso.datetime(),
  updated_by_id: z.string().nullable(),
  updated_by_name: z.string().nullable().optional(),
  version: z.number().int(),
  work_order_id: z.string().nullable(),
  work_order_number: z.string().nullable().optional(),
});
export type ServiceRequestResponse = z.infer<typeof ServiceRequestResponseSchema>;

export const CursorPage_ServiceRequestResponse_Schema = z.object({
  next_cursor: z.string().nullable().optional(),
  results: z.array(ServiceRequestResponseSchema),
});
export type CursorPage_ServiceRequestResponse_ = z.infer<typeof CursorPage_ServiceRequestResponse_Schema>;

/** The record created by moving an item to the other door, plus the id it replaced. */
export const ReclassifyResponseSchema = z.object({
  quote_request_id: z.string().nullable().optional(),
  replaced_id: z.string(),
  service_request: ServiceRequestResponseSchema.nullable().optional(),
  service_request_id: z.string().nullable().optional(),
});
export type ReclassifyResponse = z.infer<typeof ReclassifyResponseSchema>;

export const SRTriageResponseSchema = z.object({
  checklist_item_id: z.string().nullable().optional(),
  complaint_rung_ids: z.array(z.string()).optional(),
  recurring_checklist_item_id: z.string().nullable().optional(),
  recurring_schedule_id: z.string().nullable().optional(),
  service_request: ServiceRequestResponseSchema,
  work_order_day_id: z.string().nullable().optional(),
  work_order_id: z.string().nullable().optional(),
});
export type SRTriageResponse = z.infer<typeof SRTriageResponseSchema>;

export const ServiceRequestUpdateSchema = z.object({
  assigned_to_id: z.string().nullable().optional(),
  category: z.string().nullable().optional(),
  contact_id: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
  location_id: z.string().nullable().optional(),
  preferred_date: z.iso.datetime().nullable().optional(),
  priority: z.enum(["LOW", "MEDIUM", "HIGH", "URGENT"]).nullable().optional(),
  resolution_notes: z.string().nullable().optional(),
  status: z.enum(["OPEN", "IN_PROGRESS", "RESOLVED", "CLOSED", "CANCELLED"]).nullable().optional(),
  title: z.string().min(1).max(255).nullable().optional(),
  version: z.number().int(),
});
export type ServiceRequestUpdate = z.infer<typeof ServiceRequestUpdateSchema>;

export const ServiceResponseSchema = z.object({
  category_id: z.string().nullable(),
  code: z.string(),
  created_at: z.iso.datetime(),
  id: z.string(),
  is_active: z.boolean(),
  name: z.string(),
});
export type ServiceResponse = z.infer<typeof ServiceResponseSchema>;

export const CursorPage_ServiceResponse_Schema = z.object({
  next_cursor: z.string().nullable().optional(),
  results: z.array(ServiceResponseSchema),
});
export type CursorPage_ServiceResponse_ = z.infer<typeof CursorPage_ServiceResponse_Schema>;

export const ServiceUpdateSchema = z.object({
  category_id: z.string().nullable().optional(),
  is_active: z.boolean().nullable().optional(),
  name: z.string().min(1).max(255).nullable().optional(),
});
export type ServiceUpdate = z.infer<typeof ServiceUpdateSchema>;

export const SetupIntentResponseSchema = z.object({
  client_secret: z.string(),
});
export type SetupIntentResponse = z.infer<typeof SetupIntentResponseSchema>;

/** Staff-side: invite ONE named contact to sign ONE quote. */
export const SignatureInviteCreateSchema = z.object({
  contact_id: z.string(),
  expires_in_hours: z.number().int().min(1).max(2160).nullable().optional(),
  replace_existing: z.boolean().optional(),
  send: z.boolean().optional(),
});
export type SignatureInviteCreate = z.infer<typeof SignatureInviteCreateSchema>;

/** The ONE response that carries the raw token. It is never stored and never logged, so if the caller loses it the only remedy is to revoke this invite and issue another. */
export const SignatureInviteIssuedSchema = z.object({
  account_id: z.string(),
  contact_id: z.string(),
  created_at: z.iso.datetime(),
  created_by_id: z.string().nullable().optional(),
  expires_at: z.iso.datetime(),
  id: z.string(),
  quote_id: z.string(),
  redeemed_at: z.iso.datetime().nullable().optional(),
  revoked_at: z.iso.datetime().nullable().optional(),
  send_count: z.number().int().optional(),
  sent_at: z.iso.datetime().nullable().optional(),
  sent_to_email: z.string().nullable().optional(),
  state: z.enum(["LIVE", "EXPIRED", "REDEEMED", "REVOKED"]),
  token: z.string(),
});
export type SignatureInviteIssued = z.infer<typeof SignatureInviteIssuedSchema>;

export const SignatureInviteResponseSchema = z.object({
  account_id: z.string(),
  contact_id: z.string(),
  created_at: z.iso.datetime(),
  created_by_id: z.string().nullable().optional(),
  expires_at: z.iso.datetime(),
  id: z.string(),
  quote_id: z.string(),
  redeemed_at: z.iso.datetime().nullable().optional(),
  revoked_at: z.iso.datetime().nullable().optional(),
  send_count: z.number().int().optional(),
  sent_at: z.iso.datetime().nullable().optional(),
  sent_to_email: z.string().nullable().optional(),
  state: z.enum(["LIVE", "EXPIRED", "REDEEMED", "REVOKED"]),
});
export type SignatureInviteResponse = z.infer<typeof SignatureInviteResponseSchema>;

export const QuoteSendResponseSchema = z.object({
  invite: SignatureInviteResponseSchema.nullable().optional(),
  quote: QuoteResponseSchema,
  route: z.enum(["DIRECT_CLIENT", "NBD_REVIEW"]),
});
export type QuoteSendResponse = z.infer<typeof QuoteSendResponseSchema>;

export const SigningInviteAcceptedSchema = z.object({
  accepted_at: z.iso.datetime().nullable().optional(),
  content_sha256: z.string(),
  quote_id: z.string(),
  quote_number: z.string(),
  signed_at: z.iso.datetime().nullable().optional(),
  signed_by: z.string().nullable().optional(),
  status: z.string(),
});
export type SigningInviteAccepted = z.infer<typeof SigningInviteAcceptedSchema>;

export const SigningInviteItemSchema = z.object({
  description: z.string(),
  quantity: z.number(),
  total_price: z.number(),
  unit_price: z.number(),
});
export type SigningInviteItem = z.infer<typeof SigningInviteItemSchema>;

/** Enough to render the signing page, and nothing that is not on the contract. No invite id and no account id: the recipient does not need our identifiers, and every one we hand out is one more thing that can be quoted back at a support desk as proof of something. */
export const SigningInvitePageSchema = z.object({
  account_name: z.string(),
  consent_version: z.string(),
  expires_at: z.iso.datetime(),
  items: z.array(SigningInviteItemSchema).optional(),
  notes: z.string().nullable().optional(),
  quote_id: z.string(),
  quote_number: z.string(),
  reviewed_terms_sha256: z.string(),
  signer_email: z.string().nullable().optional(),
  signer_name: z.string().nullable().optional(),
  status: z.string(),
  subtotal: z.number().nullable().optional(),
  title: z.string().nullable().optional(),
  total: z.number().nullable().optional(),
  valid_until: z.string().nullable().optional(),
});
export type SigningInvitePage = z.infer<typeof SigningInvitePageSchema>;

export const SigningInviteRedeemSchema = z.object({
  consent_version: z.string().nullable().optional(),
  reviewed_terms_sha256: z.string(),
  signature_method: z.enum(["typed", "drawn"]).optional(),
  signature_path: z.string().nullable().optional(),
  signed_by: z.string().min(1).max(255),
  token: z.string().min(1),
});
export type SigningInviteRedeem = z.infer<typeof SigningInviteRedeemSchema>;

/** The presented secret. Body-only, on every public signing-invite call. */
export const SigningInviteTokenSchema = z.object({
  token: z.string().min(1),
});
export type SigningInviteToken = z.infer<typeof SigningInviteTokenSchema>;

export const SowAmendmentCreateSchema = z.object({
  body: z.string().min(1),
  change_order_work_order_id: z.string().nullable().optional(),
  effective_from: z.string(),
  effective_to: z.string().nullable().optional(),
  price_decision: z.enum(["BILLABLE", "ABSORBED"]),
});
export type SowAmendmentCreate = z.infer<typeof SowAmendmentCreateSchema>;

export const SowAmendmentResponseSchema = z.object({
  body: z.string(),
  change_order_work_order_id: z.string().nullable().optional(),
  committed_at: z.iso.datetime().nullable().optional(),
  committed_by_id: z.string().nullable().optional(),
  committed_by_name: z.string().nullable().optional(),
  effective_from: z.string(),
  effective_to: z.string(),
  id: z.string(),
  price_decision: z.string(),
  proposed_at: z.iso.datetime(),
  proposed_by_id: z.string().nullable().optional(),
  proposed_by_name: z.string().nullable().optional(),
  sow_cell_id: z.string(),
  status: z.string(),
});
export type SowAmendmentResponse = z.infer<typeof SowAmendmentResponseSchema>;

export const SowCellCreateSchema = z.object({
  account_id: z.string(),
  client_scope: z.string().min(1),
  effective_from: z.string(),
  effective_to: z.string().nullable().optional(),
  location_id: z.string(),
  service_category: z.string().min(1).max(100),
  source_quote_id: z.string().nullable().optional(),
  sub_instruction: z.string().nullable().optional(),
});
export type SowCellCreate = z.infer<typeof SowCellCreateSchema>;

/** A3: scope is versioned, not edited. There is no PATCH on a cell. Correcting what we owe closes the standing version the day before this one opens and copies the item set forward, in one transaction — so the history stays reconstructable and a correction cannot silently empty the scope. */
export const SowCellVersionCreateSchema = z.object({
  client_scope: z.string().min(1).nullable().optional(),
  effective_from: z.string(),
  sub_instruction: z.string().nullable().optional(),
});
export type SowCellVersionCreate = z.infer<typeof SowCellVersionCreateSchema>;

export const SowItemCreateSchema = z.object({
  area: z.string().nullable().optional(),
  frequency_days: z.number().int().gt(0).nullable().optional(),
  is_hot_spot: z.boolean().optional(),
  label: z.string().min(1).max(500),
  note: z.string().nullable().optional(),
  requires_note: z.boolean().optional(),
  requires_photo: z.boolean().optional(),
  sort_order: z.number().int().optional(),
  source: z.enum(["QUOTE", "COMPLAINT", "AMENDMENT", "MANUAL"]).optional(),
  source_ref: z.string().nullable().optional(),
});
export type SowItemCreate = z.infer<typeof SowItemCreateSchema>;

/** One discrete task in the standing scope (#88). Rides its cell's version. */
export const SowItemResponseSchema = z.object({
  area: z.string().nullable().optional(),
  created_at: z.iso.datetime().nullable().optional(),
  frequency_days: z.number().int().nullable().optional(),
  id: z.string(),
  is_hot_spot: z.boolean().optional(),
  label: z.string(),
  note: z.string().nullable().optional(),
  requires_note: z.boolean().optional(),
  requires_photo: z.boolean().optional(),
  sort_order: z.number().int().optional(),
  source: z.string(),
  source_ref: z.string().nullable().optional(),
  sow_cell_id: z.string(),
});
export type SowItemResponse = z.infer<typeof SowItemResponseSchema>;

/** The effective covered scope for a cell on a date, from `resolve_sow()`. Committed amendments only — an uncommitted proposal does not bind. */
export const ResolvedSowResponseSchema = z.object({
  account_id: z.string(),
  amendments: z.array(z.record(z.string(), z.unknown())).optional(),
  client_scope: z.string(),
  effective_from: z.string(),
  effective_to: z.string(),
  items: z.array(SowItemResponseSchema).optional(),
  location_id: z.string(),
  service_category: z.string(),
  source_quote_id: z.string().nullable().optional(),
  sow_cell_id: z.string(),
  sub_instruction: z.string().nullable().optional(),
});
export type ResolvedSowResponse = z.infer<typeof ResolvedSowResponseSchema>;

export const SowCellResponseSchema = z.object({
  account_id: z.string(),
  account_name: z.string().nullable().optional(),
  client_scope: z.string(),
  created_at: z.iso.datetime(),
  created_by_id: z.string().nullable().optional(),
  created_by_name: z.string().nullable().optional(),
  effective_from: z.string(),
  effective_to: z.string(),
  id: z.string(),
  items: z.array(SowItemResponseSchema).optional(),
  location_id: z.string(),
  location_name: z.string().nullable().optional(),
  service_category: z.string(),
  source_quote_id: z.string().nullable().optional(),
  sub_instruction: z.string().nullable().optional(),
  territory_id: z.string(),
  updated_at: z.iso.datetime(),
});
export type SowCellResponse = z.infer<typeof SowCellResponseSchema>;

export const CursorPage_SowCellResponse_Schema = z.object({
  next_cursor: z.string().nullable().optional(),
  results: z.array(SowCellResponseSchema),
});
export type CursorPage_SowCellResponse_ = z.infer<typeof CursorPage_SowCellResponse_Schema>;

export const SowItemUpdateSchema = z.object({
  area: z.string().nullable().optional(),
  frequency_days: z.number().int().gt(0).nullable().optional(),
  is_hot_spot: z.boolean().nullable().optional(),
  label: z.string().min(1).max(500).nullable().optional(),
  note: z.string().nullable().optional(),
  requires_note: z.boolean().nullable().optional(),
  requires_photo: z.boolean().nullable().optional(),
  sort_order: z.number().int().nullable().optional(),
});
export type SowItemUpdate = z.infer<typeof SowItemUpdateSchema>;

export const SubAccountCreateSchema = z.object({
  account_id: z.string(),
  assigned_date: z.string().nullable().optional(),
  contract_cost: z.number().nullable().optional(),
  contract_value: z.number().nullable().optional(),
  ic_cost: z.number().nullable().optional(),
  notes: z.string().nullable().optional(),
  service_category_id: z.string(),
});
export type SubAccountCreate = z.infer<typeof SubAccountCreateSchema>;

export const SubAccountResponseSchema = z.object({
  account_id: z.string(),
  account_name: z.string().nullable().optional(),
  assigned_date: z.string().nullable(),
  contract_cost: z.number().nullable(),
  contract_value: z.number().nullable(),
  created_at: z.iso.datetime(),
  ic_cost: z.number().nullable(),
  id: z.string(),
  notes: z.string().nullable(),
  service_category_id: z.string().nullable(),
  service_category_label: z.string().nullable().optional(),
  subcontractor_id: z.string(),
  territory_id: z.string(),
});
export type SubAccountResponse = z.infer<typeof SubAccountResponseSchema>;

export const SubAccountUpdateSchema = z.object({
  assigned_date: z.string().nullable().optional(),
  contract_cost: z.number().nullable().optional(),
  contract_value: z.number().nullable().optional(),
  ic_cost: z.number().nullable().optional(),
  notes: z.string().nullable().optional(),
  service_category_id: z.string().nullable().optional(),
});
export type SubAccountUpdate = z.infer<typeof SubAccountUpdateSchema>;

/** Body for `POST /subcontractors/{sub_id}/invite-admin` (CRM-side). */
export const SubAdminInviteSchema = z.object({
  email: z.email(),
  name: z.string().min(1).max(255),
});
export type SubAdminInvite = z.infer<typeof SubAdminInviteSchema>;

/** The subcontractor's half of the two-sided gate (Decision J3, vantage-backend#219). A closed vocabulary rather than free text: `app_accept_awarded_bid` raises check_violation on anything that is not ACCEPTED or DECLINED, and a 422 naming the two allowed values is a better answer than a 409 carrying a database message. */
export const SubBidAcceptanceSchema = z.object({
  decision: z.enum(["ACCEPTED", "DECLINED"]),
});
export type SubBidAcceptance = z.infer<typeof SubBidAcceptanceSchema>;

export const SubBidCreateSchema = z.object({
  amount: z.number().min(0),
  estimated_hours: z.number().min(0).nullable().optional(),
  notes: z.string().nullable().optional(),
  proposed_end: z.iso.datetime().nullable().optional(),
  proposed_start: z.iso.datetime().nullable().optional(),
});
export type SubBidCreate = z.infer<typeof SubBidCreateSchema>;

/** The invited company's pre-bid response; it is coverage information, not a bid. */
export const SubBidIntentSchema = z.object({
  reason: z.string().max(1000).nullable().optional(),
  status: z.enum(["CONSIDERING", "WILL_BID", "WILL_NOT_BID"]),
});
export type SubBidIntent = z.infer<typeof SubBidIntentSchema>;

/** A bid request as seen by an invited sub — the fields needed to bid, plus the sub's own bid (`my_bid`) if it has submitted one. Internal CRM audit fields (created_by/updated_by/version/work_order_id) are not exposed to the sub. NO `budget_min` / `budget_max` — deliberately. They are the office's budget for the job, which is the client price less the target margin, so a sub who sees `budget_max` can divide and derive our margin. `portals/roles-and-permissions.md` §3.3 bars a sub from seeing "margins, costs, client details", and requires the field be STRIPPED FROM THE RESPONSE rather than hidden in the UI. They remain on the CRM `BidRequestResponse`, which is correct. Request notes are internal too and are omitted. The subcontractor's own bid notes remain available on `my_bid`. Reported by @aadlinux358 as vantage-backend#218. */
export const SubBidRequestResponseSchema = z.object({
  account_id: z.string().nullable(),
  address_line1: z.string().nullable().optional(),
  address_line2: z.string().nullable().optional(),
  city: z.string().nullable().optional(),
  closeout_required: z.boolean().optional(),
  created_at: z.iso.datetime(),
  deadline: z.iso.datetime().nullable(),
  description: z.string().nullable(),
  documents: z.array(DocumentResponseSchema).optional(),
  id: z.string(),
  intent_at: z.iso.datetime().nullable().optional(),
  intent_reason: z.string().nullable().optional(),
  intent_status: z.string().nullable().optional(),
  invitation_viewed_at: z.iso.datetime().nullable().optional(),
  location_id: z.string().nullable(),
  location_name: z.string().nullable().optional(),
  my_bid: BidResponseSchema.nullable().optional(),
  recurring: z.boolean(),
  request_number: z.string(),
  scheduled_end: z.iso.datetime().nullable(),
  scheduled_start: z.iso.datetime().nullable(),
  service_category_id: z.string().nullable(),
  service_frequency: z.string().nullable().optional(),
  square_footage: z.number().nullable().optional(),
  state: z.string().nullable().optional(),
  status: z.string(),
  title: z.string(),
  zip: z.string().nullable().optional(),
});
export type SubBidRequestResponse = z.infer<typeof SubBidRequestResponseSchema>;

export const CursorPage_SubBidRequestResponse_Schema = z.object({
  next_cursor: z.string().nullable().optional(),
  results: z.array(SubBidRequestResponseSchema),
});
export type CursorPage_SubBidRequestResponse_ = z.infer<typeof CursorPage_SubBidRequestResponse_Schema>;

export const SubBidUpdateSchema = z.object({
  amount: z.number().min(0).nullable().optional(),
  estimated_hours: z.number().min(0).nullable().optional(),
  notes: z.string().nullable().optional(),
  proposed_end: z.iso.datetime().nullable().optional(),
  proposed_start: z.iso.datetime().nullable().optional(),
});
export type SubBidUpdate = z.infer<typeof SubBidUpdateSchema>;

/** The sub takes back a still-SUBMITTED bid (#537 / PORT-032). The reason mirrors `BidReject`'s: optional, and recorded in the AUDIT entry, not on the bid — `bids.notes` is the subcontractor's own note on their bid and there is no withdrawal-reason column, so writing it to the row would overwrite their text. */
export const SubBidWithdrawalSchema = z.object({
  reason: z.string().max(1000).nullable().optional(),
});
export type SubBidWithdrawal = z.infer<typeof SubBidWithdrawalSchema>;

export const SubChecklistCompletionUpdateSchema = z.object({
  completed: z.boolean(),
  completion_note: z.string().nullable().optional(),
  photo_path: z.string().nullable().optional(),
});
export type SubChecklistCompletionUpdate = z.infer<typeof SubChecklistCompletionUpdateSchema>;

/** A subcontractor's own company record, as the SUB PORTAL is allowed to see it. `/sub/v1/profile/me/company` returned the full staff `SubcontractorResponse`: 37 published fields of which the projection populates 9. The other 28 were always null, and among them were `territory_name` (a routing label the ruling on #390 forbids showing a portal user), `notes` and `rating` (City Wide's private assessment OF this subcontractor), and `created_by_name` / `updated_by_name` (staff identities). None of those was leaking, because none was selected - but a published contract is what the frontend generates its types from, and vantage-web had already written the code to render the territory name. It renders "Operations Team" today only because the value arrives null. A contract that advertises a field is an invitation to populate it. So this is the honest shape: exactly what the portal projection returns, nothing else. Adding a field here should mean deciding a subcontractor may see it. WIDENED BY #411 with the address, the service lines and the three compliance-expiry dates. The sub dashboard has a banner warning a subcontractor that its insurance, licence or workers-comp is lapsing; it was fully wired up and had NEVER ONCE FIRED, because the three dates it reads were not in this response. That is a subcontractor's own early warning on the same compliance loop #388 and #406 hardened from the staff side. STILL DELIBERATELY ABSENT, and both are decided rather than pending (@ryanlacy1, 2026-08-21): * `rating` - City Wide's private assessment of the sub. Their field-quality policy is that the contractor never sees it as a number. It is also the hand-typed scalar that #308/#313 are replacing with computed metrics, so it is the wrong field to expose regardless. Sharing sub-facing performance later means those objective measures, as a deliberate product move. * `notes` - staff notes about them. Not rendered today; not to start. */
export const SubCompanyResponseSchema = z.object({
  address_line1: z.string().nullable().optional(),
  address_line2: z.string().nullable().optional(),
  city: z.string().nullable().optional(),
  comp_expiry: z.string().nullable().optional(),
  company_name: z.string(),
  created_at: z.iso.datetime(),
  email: z.string().nullable().optional(),
  id: z.string(),
  insurance_expiry: z.string().nullable().optional(),
  license_expiry: z.string().nullable().optional(),
  payment_terms: z.string().nullable().optional(),
  phone: z.string().nullable().optional(),
  service_categories: z.array(z.string()).optional(),
  state: z.string().nullable().optional(),
  status: z.string().nullable().optional(),
  territory_id: z.string(),
  updated_at: z.iso.datetime(),
  zip: z.string().nullable().optional(),
});
export type SubCompanyResponse = z.infer<typeof SubCompanyResponseSchema>;

/** The site a subcontractor was sent to (#304 Gap 2) — where it is and how to get in. DELIBERATELY NOT A LOCATION RECORD. `locations` also carries `monthly_rate` (what the CLIENT pays for the site), the negotiated checklist, escalation notes and the building's commercial dimensions. The gap between that rate and the sub's own cost is our margin, so none of it belongs on this side of the wall. The nine fields here are the whole of what `app_sub_visible_location` returns, and it cannot return a tenth. */
export const SubLocationResponseSchema = z.object({
  access_notes: z.string().nullable().optional(),
  address_line1: z.string().nullable().optional(),
  address_line2: z.string().nullable().optional(),
  city: z.string().nullable().optional(),
  id: z.string(),
  name: z.string().nullable().optional(),
  state: z.string().nullable().optional(),
  timezone: z.string().nullable().optional(),
  zip: z.string().nullable().optional(),
});
export type SubLocationResponse = z.infer<typeof SubLocationResponseSchema>;

export const SubMessageAttachmentSchema = z.object({
  file_size: z.number().int().nullable().optional(),
  file_type: z.string().nullable().optional(),
  filename: z.string(),
  id: z.string(),
});
export type SubMessageAttachment = z.infer<typeof SubMessageAttachmentSchema>;

export const SubMessageCreateSchema = z.object({
  content: z.string().min(1),
  document_ids: z.array(z.string()).nullable().optional(),
  message_type: z.string().nullable().optional(),
  subject: z.string().nullable().optional(),
  thread_id: z.string().nullable().optional(),
  work_order_id: z.string().nullable().optional(),
});
export type SubMessageCreate = z.infer<typeof SubMessageCreateSchema>;

export const SubMessageResponseSchema = z.object({
  attachments: z.array(SubMessageAttachmentSchema).optional(),
  author_alias: z.string().nullable().optional(),
  content: z.string(),
  created_at: z.iso.datetime(),
  crm_user_id: z.string().nullable(),
  from_subcontractor: z.boolean(),
  id: z.string(),
  is_read: z.boolean(),
  message_type: z.string().nullable(),
  sender_brand: z.string().nullable().optional(),
  subcontractor_id: z.string(),
  subject: z.string().nullable(),
  thread_id: z.string().nullable(),
  work_order_id: z.string().nullable(),
  work_order_number: z.string().nullable().optional(),
});
export type SubMessageResponse = z.infer<typeof SubMessageResponseSchema>;

export const CursorPage_SubMessageResponse_Schema = z.object({
  next_cursor: z.string().nullable().optional(),
  results: z.array(SubMessageResponseSchema),
});
export type CursorPage_SubMessageResponse_ = z.infer<typeof CursorPage_SubMessageResponse_Schema>;

/** GET /sub/v1/payouts/stripe-connect — the caller's own connect status only, for the sub-portal Payouts page to render before the reader has ever clicked Connect. Deliberately narrower than `SubcontractorStripeConnectResponse`: there is no onboarding link to show until the reader starts (or resumes) onboarding, and the raw Stripe account id never leaves the server (vantage-backend#146). */
export const SubPayoutsConnectStatusResponseSchema = z.object({
  stripe_connect_status: z.string().nullable().optional(),
});
export type SubPayoutsConnectStatusResponse = z.infer<typeof SubPayoutsConnectStatusResponseSchema>;

export const SubPerformanceResponseSchema = z.object({
  active_job_count: z.number().int().optional(),
  avg_completion_days: z.number().nullable(),
  awaiting_payment_total: z.number().optional(),
  bid_win_rate: z.number().nullable(),
  bids_won: z.number().int(),
  billed_this_month: z.number(),
  completion_rate: z.number().nullable(),
  jobs_completed: z.number().int(),
  on_time_rate: z.number().nullable().optional(),
  open_bid_count: z.number().int().optional(),
  pending_invoice_count: z.number().int(),
  revenue_ytd: z.number(),
});
export type SubPerformanceResponse = z.infer<typeof SubPerformanceResponseSchema>;

/** PATCH /sub/v1/profile/me — the caller's own name and phone, nothing else (#536). Deliberately narrower than SubUserUpdate: no role (this surface must not be a self-promotion path) and no email (the Cognito login identity — re-keying it is a different, larger operation). All three sub roles may use it; the row is always the caller's own, keyed on cognito_sub. An explicit null phone clears it; an omitted field is unchanged. */
export const SubProfileSelfUpdateSchema = z.object({
  name: z.string().min(1).max(255).nullable().optional(),
  phone: z.string().nullable().optional(),
});
export type SubProfileSelfUpdate = z.infer<typeof SubProfileSelfUpdateSchema>;

export const SubProxyGrantSchema = z.object({
  territory_id: z.string().min(1).max(64),
});
export type SubProxyGrant = z.infer<typeof SubProxyGrantSchema>;

export const SubProxyGrantResponseSchema = z.object({
  created_at: z.iso.datetime(),
  granted_by_id: z.string().nullable(),
  granted_by_name: z.string().nullable().optional(),
  granted_to_user_id: z.string(),
  id: z.string(),
  revoked_at: z.iso.datetime().nullable(),
  territory_id: z.string(),
  territory_name: z.string().nullable().optional(),
});
export type SubProxyGrantResponse = z.infer<typeof SubProxyGrantResponseSchema>;

export const SubServiceCategoriesResponseSchema = z.object({
  category_ids: z.array(z.string()),
  subcontractor_id: z.string(),
});
export type SubServiceCategoriesResponse = z.infer<typeof SubServiceCategoriesResponseSchema>;

/** Replace-set body for `PUT /{sub_id}/service-categories`. */
export const SubServiceCategoriesUpdateSchema = z.object({
  category_ids: z.array(z.string()).optional(),
});
export type SubServiceCategoriesUpdate = z.infer<typeof SubServiceCategoriesUpdateSchema>;

export const SubServicedAccountResponseSchema = z.object({
  account_id: z.string(),
  active_job_count: z.number().int(),
  city: z.string().nullable(),
  name: z.string(),
  service_category: z.string().nullable(),
  site_address: z.string().nullable(),
  state: z.string().nullable(),
});
export type SubServicedAccountResponse = z.infer<typeof SubServicedAccountResponseSchema>;

export const CursorPage_SubServicedAccountResponse_Schema = z.object({
  next_cursor: z.string().nullable().optional(),
  results: z.array(SubServicedAccountResponseSchema),
});
export type CursorPage_SubServicedAccountResponse_ = z.infer<typeof CursorPage_SubServicedAccountResponse_Schema>;

export const SubUserCreateSchema = z.object({
  email: z.email(),
  name: z.string().min(1).max(255),
  role: z.enum(["SUB_ADMIN", "SUB_WRITER", "SUB_READER"]),
});
export type SubUserCreate = z.infer<typeof SubUserCreateSchema>;

export const SubUserResponseSchema = z.object({
  cognito_sub: z.string(),
  created_at: z.iso.datetime(),
  email: z.string(),
  id: z.string(),
  is_active: z.boolean(),
  name: z.string(),
  phone: z.string().nullable().optional(),
  role: z.string(),
  subcontractor_id: z.string(),
  updated_at: z.iso.datetime(),
});
export type SubUserResponse = z.infer<typeof SubUserResponseSchema>;

export const CursorPage_SubUserResponse_Schema = z.object({
  next_cursor: z.string().nullable().optional(),
  results: z.array(SubUserResponseSchema),
});
export type CursorPage_SubUserResponse_ = z.infer<typeof CursorPage_SubUserResponse_Schema>;

/** Result of a CRM-side portal-access change (#358). `changed` is the load-bearing field. Deactivating an already-deactivated user is a success that altered nothing, and a caller that cannot tell the two apart will either report "done" for a no-op or treat an idempotent repeat as a failure. `user` is null exactly when `changed` is false, because there is no new state to return. */
export const SubUserStateResponseSchema = z.object({
  changed: z.boolean(),
  message: z.string(),
  user: SubUserResponseSchema.nullable().optional(),
});
export type SubUserStateResponse = z.infer<typeof SubUserStateResponseSchema>;

export const SubUserUpdateSchema = z.object({
  name: z.string().min(1).max(255).nullable().optional(),
  role: z.enum(["SUB_ADMIN", "SUB_WRITER", "SUB_READER"]).nullable().optional(),
});
export type SubUserUpdate = z.infer<typeof SubUserUpdateSchema>;

/** A vendor (AP) bill as seen by the sub that created it. The territory's review internals — `client_invoice_id` (the transactional-margin link), `document_id`, and the CRM audit ids — are not exposed to the sub. */
export const SubVendorBillResponseSchema = z.object({
  bill_number: z.string(),
  created_at: z.iso.datetime(),
  due_date: z.string().nullable(),
  id: z.string(),
  notes: z.string().nullable(),
  paid_date: z.string().nullable(),
  payment_terms: z.string().nullable(),
  status: z.string(),
  subcontractor_id: z.string(),
  subtotal: z.number().nullable(),
  tax: z.number().nullable(),
  total: z.number().nullable(),
  updated_at: z.iso.datetime(),
  version: z.number().int(),
  work_order_id: z.string().nullable(),
});
export type SubVendorBillResponse = z.infer<typeof SubVendorBillResponseSchema>;

export const SubVendorBillUpdateSchema = z.object({
  notes: z.string().nullable().optional(),
  tax: z.number().min(0).nullable().optional(),
  version: z.number().int(),
});
export type SubVendorBillUpdate = z.infer<typeof SubVendorBillUpdateSchema>;

export const SubWorkOrderDayUpdateSchema = z.object({
  note: z.string().nullable().optional(),
  photo_path: z.string().nullable().optional(),
  status: z.enum(["pending", "done", "skipped"]),
});
export type SubWorkOrderDayUpdate = z.infer<typeof SubWorkOrderDayUpdateSchema>;

/** A work order as seen by its assigned sub — operational fields, plus its own agreed rate. The territory's cost internals (estimated_cost/actual_cost/not_to_exceed, work_order_lines) and CRM audit fields are not exposed to the sub. `agreed_sub_cost` looks like an exception and is not one: it is what we agreed to pay THIS sub for THIS job (#304 Gap 1), so it is the sub's own number rather than ours. IT MUST NOT BE COPIED TO `ClientWorkOrderResponse`, which begins with an identical field list a few lines above. That model is the client's view, and our cost to the subcontractor is the other side of the margin — the ruling protects the spread in both directions, which is why the client portal already has `contractor_cost` stripped from it. A careless edit matching on the shared prefix would put our AP figure in front of the client. */
export const SubWorkOrderResponseSchema = z.object({
  account_id: z.string(),
  agreed_sub_cost: z.number().nullable().optional(),
  completed_date: z.iso.datetime().nullable(),
  created_at: z.iso.datetime(),
  delayed_at: z.iso.datetime().nullable(),
  description: z.string().nullable(),
  id: z.string(),
  location_id: z.string().nullable(),
  notes: z.string().nullable(),
  order_number: z.string(),
  order_type: z.string().nullable(),
  priority: z.string().nullable(),
  scheduled_date: z.iso.datetime().nullable(),
  scheduled_duration: z.number().int().nullable(),
  scheduled_time: z.string().nullable(),
  service_category: z.string().nullable(),
  started_at: z.iso.datetime().nullable(),
  status: z.string(),
  subcontractor_id: z.string().nullable(),
  title: z.string(),
  updated_at: z.iso.datetime(),
  week_start: z.string().nullable(),
  work_complete_claimed_at: z.iso.datetime().nullable().optional(),
});
export type SubWorkOrderResponse = z.infer<typeof SubWorkOrderResponseSchema>;

export const CursorPage_SubWorkOrderResponse_Schema = z.object({
  next_cursor: z.string().nullable().optional(),
  results: z.array(SubWorkOrderResponseSchema),
});
export type CursorPage_SubWorkOrderResponse_ = z.infer<typeof CursorPage_SubWorkOrderResponse_Schema>;

export const SubWorkOrderStatusUpdateSchema = z.object({
  status: z.enum(["IN_PROGRESS", "ON_HOLD", "DELAYED", "COMPLETED"]),
});
export type SubWorkOrderStatusUpdate = z.infer<typeof SubWorkOrderStatusUpdateSchema>;

export const SubcontractorCreateSchema = z.object({
  address_line1: z.string().nullable().optional(),
  address_line2: z.string().nullable().optional(),
  business_type: z.enum(["company", "individual"]).nullable().optional(),
  capacity: z.string().nullable().optional(),
  city: z.string().nullable().optional(),
  comp_expiry: z.string().nullable().optional(),
  company_name: z.string().min(1).max(255),
  email: z.string().nullable().optional(),
  insurance_expiry: z.string().nullable().optional(),
  license_expiry: z.string().nullable().optional(),
  notes: z.string().nullable().optional(),
  payment_terms: z.string().nullable().optional(),
  phone: z.string().nullable().optional(),
  preferred_language: z.string().nullable().optional(),
  rating: z.number().min(0).max(5).nullable().optional(),
  service_category_ids: z.array(z.string()).optional(),
  state: z.string().nullable().optional(),
  status: z.enum(["ACTIVE", "INACTIVE", "ONBOARDING"]).optional(),
  territory_id: z.string(),
  zip: z.string().nullable().optional(),
});
export type SubcontractorCreate = z.infer<typeof SubcontractorCreateSchema>;

/** Rate the sub that did this job. 1-5, one per work order. `rating` is bounded here AND by a CHECK on the table. The duplication is deliberate: this layer answers a bad request with a readable 422, and the constraint makes the guarantee true of the TABLE rather than of the one path that happens to write it today. */
export const SubcontractorRatingCreateSchema = z.object({
  comment: z.string().nullable().optional(),
  rating: z.number().int().min(1).max(5),
});
export type SubcontractorRatingCreate = z.infer<typeof SubcontractorRatingCreateSchema>;

export const SubcontractorRatingResponseSchema = z.object({
  comment: z.string().nullable().optional(),
  created_at: z.iso.datetime(),
  created_by_id: z.string().nullable().optional(),
  created_by_name: z.string().nullable().optional(),
  id: z.string(),
  rating: z.number().int(),
  subcontractor_id: z.string(),
  territory_id: z.string(),
  work_order_id: z.string(),
  work_order_number: z.string().nullable().optional(),
  work_order_title: z.string().nullable().optional(),
});
export type SubcontractorRatingResponse = z.infer<typeof SubcontractorRatingResponseSchema>;

export const CursorPage_SubcontractorRatingResponse_Schema = z.object({
  next_cursor: z.string().nullable().optional(),
  results: z.array(SubcontractorRatingResponseSchema),
});
export type CursorPage_SubcontractorRatingResponse_ = z.infer<typeof CursorPage_SubcontractorRatingResponse_Schema>;

export const SubcontractorResponseSchema = z.object({
  address_line1: z.string().nullable().optional(),
  address_line2: z.string().nullable().optional(),
  bids_submitted: z.number().int().nullable().optional(),
  bids_won: z.number().int().nullable().optional(),
  business_type: z.string().nullable().optional(),
  capacity: z.string().nullable().optional(),
  city: z.string().nullable().optional(),
  comp_exempt: z.boolean().optional(),
  comp_exempt_reason: z.string().nullable().optional(),
  comp_exempt_recorded_at: z.iso.datetime().nullable().optional(),
  comp_exempt_recorded_by_id: z.string().nullable().optional(),
  comp_expiry: z.string().nullable().optional(),
  company_name: z.string(),
  created_at: z.iso.datetime(),
  created_by_id: z.string().nullable().optional(),
  created_by_name: z.string().nullable().optional(),
  email: z.string().nullable(),
  id: z.string(),
  insurance_expiry: z.string().nullable().optional(),
  jobs_completed: z.number().int().nullable().optional(),
  last_award_at: z.iso.datetime().nullable().optional(),
  license_expiry: z.string().nullable().optional(),
  notes: z.string().nullable().optional(),
  on_time_pct: z.number().nullable().optional(),
  on_time_pct_30d: z.number().nullable().optional(),
  on_time_pct_prev_30d: z.number().nullable().optional(),
  on_time_trend: z.enum(["up", "flat", "down"]).nullable().optional(),
  open_work_order_count: z.number().int().nullable().optional(),
  payment_terms: z.string().nullable().optional(),
  phone: z.string().nullable(),
  photo_compliance_pct: z.number().nullable().optional(),
  preferred_language: z.string().nullable().optional(),
  qc_pass_rate: z.number().nullable().optional(),
  rating: z.number().nullable().optional(),
  service_categories: z.array(z.string()).optional(),
  state: z.string().nullable().optional(),
  status: z.string(),
  stripe_connect_status: z.string().nullable().optional(),
  territory_id: z.string(),
  territory_name: z.string().nullable().optional(),
  updated_at: z.iso.datetime(),
  updated_by_id: z.string().nullable().optional(),
  updated_by_name: z.string().nullable().optional(),
  version: z.number().int().nullable().optional(),
  win_rate: z.number().nullable().optional(),
  zip: z.string().nullable().optional(),
});
export type SubcontractorResponse = z.infer<typeof SubcontractorResponseSchema>;

export const CursorPage_SubcontractorResponse_Schema = z.object({
  next_cursor: z.string().nullable().optional(),
  results: z.array(SubcontractorResponseSchema),
});
export type CursorPage_SubcontractorResponse_ = z.infer<typeof CursorPage_SubcontractorResponse_Schema>;

/** A candidate for a bid request: the subcontractor plus why it is (or is not) invitable. Eligibility is per-call context rather than a property of the sub, so it rides alongside the row instead of inside it. A BLOCKED candidate is still returned, with its reasons named -- dropping it would make a stricter compliance rule look like the sub had vanished. */
export const EligibleSubcontractorResponseSchema = z.object({
  already_invited: z.boolean().optional(),
  blocking_reasons: z.array(z.string()).optional(),
  eligible: z.boolean(),
  subcontractor: SubcontractorResponseSchema,
});
export type EligibleSubcontractorResponse = z.infer<typeof EligibleSubcontractorResponseSchema>;

export const CursorPage_EligibleSubcontractorResponse_Schema = z.object({
  next_cursor: z.string().nullable().optional(),
  results: z.array(EligibleSubcontractorResponseSchema),
});
export type CursorPage_EligibleSubcontractorResponse_ = z.infer<typeof CursorPage_EligibleSubcontractorResponse_Schema>;

/** Onboarding-link response for POST /subcontractors/{id}/stripe-connect. Mirrors TerritoryStripeConnectResponse (#ACH-1). */
export const SubcontractorStripeConnectResponseSchema = z.object({
  onboarding_url: z.string(),
  stripe_connect_status: z.string(),
});
export type SubcontractorStripeConnectResponse = z.infer<typeof SubcontractorStripeConnectResponseSchema>;

export const SubcontractorUpdateSchema = z.object({
  address_line1: z.string().nullable().optional(),
  address_line2: z.string().nullable().optional(),
  business_type: z.enum(["company", "individual"]).nullable().optional(),
  capacity: z.string().nullable().optional(),
  city: z.string().nullable().optional(),
  comp_exempt: z.boolean().nullable().optional(),
  comp_exempt_reason: z.string().max(1000).nullable().optional(),
  comp_expiry: z.string().nullable().optional(),
  company_name: z.string().min(1).max(255).nullable().optional(),
  email: z.string().nullable().optional(),
  insurance_expiry: z.string().nullable().optional(),
  license_expiry: z.string().nullable().optional(),
  notes: z.string().nullable().optional(),
  payment_terms: z.string().nullable().optional(),
  phone: z.string().nullable().optional(),
  preferred_language: z.string().nullable().optional(),
  rating: z.number().min(0).max(5).nullable().optional(),
  state: z.string().nullable().optional(),
  status: z.enum(["ACTIVE", "INACTIVE", "ONBOARDING"]).nullable().optional(),
  territory_id: z.string().nullable().optional(),
  version: z.number().int(),
  zip: z.string().nullable().optional(),
});
export type SubcontractorUpdate = z.infer<typeof SubcontractorUpdateSchema>;

export const SubcontractorsSummarySchema = z.object({
  active: z.number().int(),
  compliance_flags: z.number().int(),
});
export type SubcontractorsSummary = z.infer<typeof SubcontractorsSummarySchema>;

export const NavSummaryResponseSchema = z.object({
  accounts: AccountsSummarySchema,
  messages: MessagesSummarySchema,
  operations: OperationsSummarySchema,
  sales: SalesSummarySchema,
  subcontractors: SubcontractorsSummarySchema,
});
export type NavSummaryResponse = z.infer<typeof NavSummaryResponseSchema>;

/** #618: read-only activity feed for one task, projected from the unified `audit_log`. Deliberately the same shape as `WorkOrderActivityResponse` rather than a shared model: renaming that one would be a contract change on a live page, and two identical shapes with different names is the cheaper of the two mistakes. Tasks began writing audit rows in this change, so a task last touched before it deployed has an empty feed. That is honest - the events genuinely were not recorded - and it fills in from here. */
export const TaskActivityResponseSchema = z.object({
  action: z.string(),
  actor_name: z.string().nullable(),
  actor_type: z.string().nullable(),
  created_at: z.iso.datetime(),
  diff: z.record(z.string(), z.unknown()).nullable().optional(),
  id: z.string(),
});
export type TaskActivityResponse = z.infer<typeof TaskActivityResponseSchema>;

export const CursorPage_TaskActivityResponse_Schema = z.object({
  next_cursor: z.string().nullable().optional(),
  results: z.array(TaskActivityResponseSchema),
});
export type CursorPage_TaskActivityResponse_ = z.infer<typeof CursorPage_TaskActivityResponse_Schema>;

export const TaskCreateSchema = z.object({
  account_id: z.string().nullable().optional(),
  assigned_to_id: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
  due_date: z.iso.datetime().nullable().optional(),
  parent_task_id: z.string().nullable().optional(),
  priority: z.string().nullable().optional(),
  service_request_id: z.string().nullable().optional(),
  sort_order: z.number().int().optional(),
  task_type: z.enum(["TASK", "CHECKLIST_ITEM", "JOB_CARD", "COMPLAINT_RUNG"]).nullable().optional(),
  territory_id: z.string(),
  title: z.string().min(1),
  work_order_id: z.string().nullable().optional(),
});
export type TaskCreate = z.infer<typeof TaskCreateSchema>;

export const TaskResponseSchema = z.object({
  account_id: z.string().nullable(),
  account_name: z.string().nullable().optional(),
  assigned_to_id: z.string().nullable(),
  assigned_to_name: z.string().nullable().optional(),
  completion_date: z.iso.datetime().nullable(),
  created_at: z.iso.datetime(),
  created_by_id: z.string().nullable(),
  created_by_name: z.string().nullable().optional(),
  description: z.string().nullable(),
  due_date: z.iso.datetime().nullable(),
  id: z.string(),
  parent_task_id: z.string().nullable(),
  priority: z.string().nullable(),
  service_request_id: z.string().nullable().optional(),
  service_request_number: z.string().nullable().optional(),
  sort_order: z.number().int(),
  status: z.string(),
  task_type: z.string().nullable(),
  territory_id: z.string(),
  territory_name: z.string().nullable().optional(),
  title: z.string(),
  updated_at: z.iso.datetime(),
  updated_by_id: z.string().nullable(),
  updated_by_name: z.string().nullable().optional(),
  version: z.number().int(),
  work_order_id: z.string().nullable(),
  work_order_number: z.string().nullable().optional(),
  work_order_title: z.string().nullable().optional(),
});
export type TaskResponse = z.infer<typeof TaskResponseSchema>;

export const CursorPage_TaskResponse_Schema = z.object({
  next_cursor: z.string().nullable().optional(),
  results: z.array(TaskResponseSchema),
});
export type CursorPage_TaskResponse_ = z.infer<typeof CursorPage_TaskResponse_Schema>;

export const TaskUpdateSchema = z.object({
  assigned_to_id: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
  due_date: z.iso.datetime().nullable().optional(),
  priority: z.string().nullable().optional(),
  sort_order: z.number().int().nullable().optional(),
  status: z.enum(["OPEN", "IN_PROGRESS", "COMPLETED", "CANCELLED"]).nullable().optional(),
  task_type: z.enum(["TASK", "CHECKLIST_ITEM", "JOB_CARD", "COMPLAINT_RUNG"]).nullable().optional(),
  title: z.string().min(1).nullable().optional(),
  version: z.number().int(),
});
export type TaskUpdate = z.infer<typeof TaskUpdateSchema>;

export const TerritoryCreateSchema = z.object({
  address_line1: z.string().nullable().optional(),
  address_line2: z.string().nullable().optional(),
  city: z.string().nullable().optional(),
  code: z.string().min(1).max(64),
  name: z.string().min(1).max(255),
  parent_territory_id: z.string().nullable().optional(),
  state: z.string().nullable().optional(),
  status: z.enum(["active", "onboarding", "inactive"]).optional(),
  zip: z.string().nullable().optional(),
});
export type TerritoryCreate = z.infer<typeof TerritoryCreateSchema>;

export const TerritoryDashboardKpisSchema = z.object({
  billed_mom: z.number().optional(),
  billed_this_month: z.number().optional(),
  gross_margin_pct: z.number().nullable().optional(),
  margin_delta: z.number().nullable().optional(),
  open_work_orders: z.number().int().optional(),
  open_work_orders_wow: z.number().int().optional(),
  outstanding_ar: z.number().optional(),
  overdue_ar_count: z.number().int().optional(),
});
export type TerritoryDashboardKpis = z.infer<typeof TerritoryDashboardKpisSchema>;

export const TerritoryDashboardResponseSchema = z.object({
  active_work_orders: z.array(DashboardWorkOrderSchema).optional(),
  at_risk_accounts: z.array(AtRiskAccountSchema).optional(),
  attention: z.array(z.unknown()).optional(),
  corporate_view: z.boolean().optional(),
  kpis: TerritoryDashboardKpisSchema,
  recent_activities: z.array(DashboardActivitySchema).optional(),
  schedule: z.array(DashboardScheduleItemSchema).optional(),
  territory_name: z.string().nullable().optional(),
});
export type TerritoryDashboardResponse = z.infer<typeof TerritoryDashboardResponseSchema>;

export const TerritoryGrantResponseSchema = z.object({
  account_id: z.string(),
  created_at: z.iso.datetime(),
  id: z.string(),
  territory_id: z.string(),
});
export type TerritoryGrantResponse = z.infer<typeof TerritoryGrantResponseSchema>;

export const TerritoryMarginCreateSchema = z.object({
  category_id: z.string(),
  floor_margin_pct: z.number().min(0).max(100),
  target_margin_pct: z.number().min(0).max(100),
  territory_id: z.string(),
});
export type TerritoryMarginCreate = z.infer<typeof TerritoryMarginCreateSchema>;

export const TerritoryMarginResponseSchema = z.object({
  category_id: z.string(),
  created_at: z.iso.datetime(),
  floor_margin_pct: z.number(),
  id: z.string(),
  target_margin_pct: z.number(),
  territory_id: z.string(),
});
export type TerritoryMarginResponse = z.infer<typeof TerritoryMarginResponseSchema>;

export const CursorPage_TerritoryMarginResponse_Schema = z.object({
  next_cursor: z.string().nullable().optional(),
  results: z.array(TerritoryMarginResponseSchema),
});
export type CursorPage_TerritoryMarginResponse_ = z.infer<typeof CursorPage_TerritoryMarginResponse_Schema>;

export const TerritoryMarginUpdateSchema = z.object({
  floor_margin_pct: z.number().min(0).max(100).nullable().optional(),
  target_margin_pct: z.number().min(0).max(100).nullable().optional(),
});
export type TerritoryMarginUpdate = z.infer<typeof TerritoryMarginUpdateSchema>;

export const TerritoryResponseSchema = z.object({
  address_line1: z.string().nullable(),
  address_line2: z.string().nullable(),
  city: z.string().nullable(),
  code: z.string(),
  created_at: z.iso.datetime(),
  id: z.string(),
  is_corp: z.boolean(),
  name: z.string(),
  parent_territory_id: z.string().nullable(),
  state: z.string().nullable(),
  status: z.string(),
  stripe_connect_status: z.string().nullable(),
  updated_at: z.iso.datetime(),
  zip: z.string().nullable(),
});
export type TerritoryResponse = z.infer<typeof TerritoryResponseSchema>;

export const CursorPage_TerritoryResponse_Schema = z.object({
  next_cursor: z.string().nullable().optional(),
  results: z.array(TerritoryResponseSchema),
});
export type CursorPage_TerritoryResponse_ = z.infer<typeof CursorPage_TerritoryResponse_Schema>;

/** Onboarding-link response for POST /territories/{id}/stripe-connect. */
export const TerritoryStripeConnectResponseSchema = z.object({
  onboarding_url: z.string(),
  stripe_connect_status: z.string(),
});
export type TerritoryStripeConnectResponse = z.infer<typeof TerritoryStripeConnectResponseSchema>;

export const TerritoryUpdateSchema = z.object({
  address_line1: z.string().nullable().optional(),
  address_line2: z.string().nullable().optional(),
  city: z.string().nullable().optional(),
  code: z.string().min(1).max(64).nullable().optional(),
  name: z.string().min(1).max(255).nullable().optional(),
  parent_territory_id: z.string().nullable().optional(),
  state: z.string().nullable().optional(),
  status: z.enum(["active", "onboarding", "inactive"]).nullable().optional(),
  zip: z.string().nullable().optional(),
});
export type TerritoryUpdate = z.infer<typeof TerritoryUpdateSchema>;

/** The computed account-scoring tier (City Wide's "Star Policy", generic in the platform; vantage-infra 061–063). Attached to the account/location responses. Read the whole field, not just `band`: * the `tier` field is `None` ⇒ the tier policy is OFF for this account (hide the chip); * `tier` present with `band = None` ⇒ policy ON but no ratings yet ("No tier yet"); * `band` set ⇒ computed. `value` is the score that produced it. Bands and the aggregation strategy are configuration (tunable without a deploy), so `band` is a string rather than a fixed enum in principle — but the seeded City Wide policy uses the three below, and the web renders those tones. */
export const TierBadgeSchema = z.object({
  band: z.enum(["GREEN", "YELLOW", "RED"]).nullable().optional(),
  client: z.number().nullable().optional(),
  fsm: z.number().nullable().optional(),
  value: z.number().nullable().optional(),
});
export type TierBadge = z.infer<typeof TierBadgeSchema>;

export const AccountResponseSchema = z.object({
  account_type: z.string().nullable().optional(),
  address_line1: z.string().nullable(),
  address_line2: z.string().nullable(),
  assigned_rep_id: z.string().nullable(),
  assigned_rep_name: z.string().nullable().optional(),
  city: z.string().nullable(),
  company_name: z.string(),
  contract_end_date: z.string().nullable(),
  contract_start_date: z.string().nullable(),
  created_at: z.iso.datetime(),
  created_by_id: z.string().nullable(),
  created_by_name: z.string().nullable().optional(),
  do_not_contact: z.boolean(),
  do_not_contact_source_id: z.string().nullable().optional(),
  do_not_contact_source_name: z.string().nullable().optional(),
  effective_do_not_contact: z.boolean().optional(),
  email: z.string().nullable(),
  external_ref: z.string().nullable(),
  id: z.string(),
  inactive_reason: z.string().nullable().optional(),
  industry: z.string().nullable(),
  is_national: z.boolean(),
  is_nbd: z.boolean(),
  latest_quote_id: z.string().nullable().optional(),
  latest_quote_status: z.string().nullable().optional(),
  location_count: z.number().int().nullable().optional(),
  managed_by_id: z.string().nullable().optional(),
  managed_by_name: z.string().nullable().optional(),
  negotiated_checklist: z.record(z.string(), z.unknown()).nullable(),
  open_work_order_count: z.number().int().nullable().optional(),
  parent_account_id: z.string().nullable(),
  parent_company_name: z.string().nullable().optional(),
  payment_terms: z.string().nullable().optional(),
  phone: z.string().nullable(),
  segment: z.string().nullable().optional(),
  service_frequency: z.string().nullable(),
  service_line: z.enum(["JANITORIAL", "OTHER_SERVICES", "BOTH"]).nullable().optional(),
  square_footage: z.number().int().nullable(),
  state: z.string().nullable(),
  status: z.string(),
  tax_id: z.string().nullable().optional(),
  territory_id: z.string(),
  territory_name: z.string().nullable().optional(),
  tier: TierBadgeSchema.nullable().optional(),
  updated_at: z.iso.datetime(),
  updated_by_id: z.string().nullable(),
  updated_by_name: z.string().nullable().optional(),
  version: z.number().int(),
  website: z.string().nullable(),
  zip: z.string().nullable(),
});
export type AccountResponse = z.infer<typeof AccountResponseSchema>;

export const CursorPage_AccountResponse_Schema = z.object({
  next_cursor: z.string().nullable().optional(),
  results: z.array(AccountResponseSchema),
});
export type CursorPage_AccountResponse_ = z.infer<typeof CursorPage_AccountResponse_Schema>;

export const LocationResponseSchema = z.object({
  access_notes: z.string().nullable().optional(),
  account_do_not_contact: z.boolean().optional(),
  account_id: z.string(),
  account_is_national: z.boolean().optional(),
  account_name: z.string().nullable().optional(),
  acreage: z.number().nullable(),
  address_line1: z.string().nullable(),
  address_line2: z.string().nullable(),
  building_class: z.string().nullable(),
  building_type_id: z.string().nullable().optional(),
  city: z.string().nullable(),
  created_at: z.iso.datetime(),
  division_name: z.string().nullable().optional(),
  do_not_contact: z.boolean().optional(),
  effective_do_not_contact: z.boolean().optional(),
  floors: z.number().int().nullable(),
  geo: z.record(z.string(), z.unknown()).nullable(),
  id: z.string(),
  is_active: z.boolean(),
  missed_sighting_count: z.number().int().optional(),
  monthly_rate: z.number().nullable(),
  name: z.string(),
  negotiated_checklist: z.record(z.string(), z.unknown()).nullable(),
  notes: z.string().nullable().optional(),
  parent_location_id: z.string().nullable(),
  parking_spaces: z.number().int().nullable(),
  recurring_cadences: z.array(z.string()).optional(),
  recurring_schedule_active_count: z.number().int().optional(),
  restrooms: z.number().int().nullable(),
  service_request_open_count: z.number().int().optional(),
  square_footage: z.number().int().nullable(),
  state: z.string().nullable(),
  territory_id: z.string(),
  territory_name: z.string().nullable().optional(),
  tier: TierBadgeSchema.nullable().optional(),
  timezone: z.string().nullable(),
  updated_at: z.iso.datetime(),
  work_order_count: z.number().int().optional(),
  work_order_open_count: z.number().int().optional(),
  zip: z.string().nullable(),
});
export type LocationResponse = z.infer<typeof LocationResponseSchema>;

export const CursorPage_LocationResponse_Schema = z.object({
  next_cursor: z.string().nullable().optional(),
  results: z.array(LocationResponseSchema),
});
export type CursorPage_LocationResponse_ = z.infer<typeof CursorPage_LocationResponse_Schema>;

export const UdcAdminCreateSchema = z.object({
  code: z.string().min(1),
  domain: z.string().min(1),
  label: z.string().min(1),
  numeric_value: z.number().nullable().optional(),
  sort_order: z.number().int().optional(),
});
export type UdcAdminCreate = z.infer<typeof UdcAdminCreateSchema>;

export const UdcAdminUpdateSchema = z.object({
  is_active: z.boolean().nullable().optional(),
  label: z.string().min(1).nullable().optional(),
  numeric_value: z.number().nullable().optional(),
  sort_order: z.number().int().nullable().optional(),
});
export type UdcAdminUpdate = z.infer<typeof UdcAdminUpdateSchema>;

/** A udc_lookups row. `id` is the uuid other tables reference (e.g. subcontractor_accounts.service_category_id) — use it, not the bare `code`. */
export const UdcLookupItemSchema = z.object({
  code: z.string(),
  domain: z.string(),
  id: z.string(),
  is_active: z.boolean(),
  label: z.string(),
  numeric_value: z.number().nullable().optional(),
  sort_order: z.number().int(),
});
export type UdcLookupItem = z.infer<typeof UdcLookupItemSchema>;

export const UserAccountGrantSchema = z.object({
  account_id: z.string(),
  role: z.enum(["ACCOUNT_MANAGER", "READ_ONLY"]).optional(),
});
export type UserAccountGrant = z.infer<typeof UserAccountGrantSchema>;

export const UserAccountGrantReadSchema = z.object({
  account_id: z.string(),
  account_name: z.string().nullable().optional(),
  role: z.string(),
});
export type UserAccountGrantRead = z.infer<typeof UserAccountGrantReadSchema>;

export const UserCreateSchema = z.object({
  email: z.email(),
  job_title_code: z.string().max(30).nullable().optional(),
  name: z.string().min(1).max(255),
  phone: z.string().nullable().optional(),
  portal: z.enum(["CORPORATE", "TERRITORY"]),
  public_alias: z.string().min(1).max(80).nullable().optional(),
  role: z.enum(["SUPER_ADMIN", "CORPORATE_WRITER", "TERRITORY_ADMIN", "TERRITORY_MANAGER", "TERRITORY_WRITER", "TERRITORY_READER", "SUB_PROXY"]),
  territory_id: z.string().nullable().optional(),
});
export type UserCreate = z.infer<typeof UserCreateSchema>;

export const UserPreferencePutSchema = z.object({
  value: z.unknown(),
});
export type UserPreferencePut = z.infer<typeof UserPreferencePutSchema>;

export const UserPreferenceResponseSchema = z.object({
  key: z.string(),
  value: z.unknown().nullable().optional(),
});
export type UserPreferenceResponse = z.infer<typeof UserPreferenceResponseSchema>;

export const UserTerritoryGrantSchema = z.object({
  assignable_modules: z.array(z.string()).nullable().optional(),
  job_title_code: z.string().max(30).nullable().optional(),
  role: z.enum(["SUPER_ADMIN", "CORPORATE_WRITER", "TERRITORY_ADMIN", "TERRITORY_MANAGER", "TERRITORY_WRITER", "TERRITORY_READER", "SUB_PROXY"]).optional(),
  territory_id: z.string().min(1).max(64),
  territory_name: z.string().nullable().optional(),
});
export type UserTerritoryGrant = z.infer<typeof UserTerritoryGrantSchema>;

export const UserResponseSchema = z.object({
  account_assignments: z.array(UserAccountGrantReadSchema).optional(),
  assignable_modules: z.array(z.string()).nullable().optional(),
  cognito_sub: z.string(),
  created_at: z.iso.datetime(),
  email: z.string(),
  id: z.string(),
  is_active: z.boolean(),
  mfa_enabled: z.boolean().nullable().optional(),
  mfa_last_used_at: z.iso.datetime().nullable().optional(),
  name: z.string(),
  phone: z.string().nullable(),
  portal: z.string(),
  public_alias: z.string().nullable().optional(),
  role: z.string(),
  territory_assignments: z.array(UserTerritoryGrantSchema).optional(),
  territory_id: z.string().nullable(),
  territory_name: z.string().nullable().optional(),
  updated_at: z.iso.datetime(),
});
export type UserResponse = z.infer<typeof UserResponseSchema>;

/** Edit a membership's DESCRIPTIVE facts — the job title (migration 055) and the modules the person may be assigned in (vantage-infra 153) — WITHOUT touching its role. Why this is not just the grant upsert: POST /users/{id}/territories carries a `role` that DEFAULTS to TERRITORY_WRITER, so editing through it would silently demote a territory admin whose caller forgot to resend the role. An organisational edit must not be able to become a permission change by omission — the same distinction migration 055 exists to hold. Both fields are independently optional and the two "not sent" rules DIFFER, which is the care this model needs: `job_title_code: null` CLEARS the title, so absent has to be read from `model_fields_set`; `assignable_modules: null` and absent both mean leave the rows alone, and `[]` is the act that removes a restriction. */
export const UserTerritoryMembershipUpdateSchema = z.object({
  assignable_modules: z.array(z.string()).nullable().optional(),
  job_title_code: z.string().max(30).nullable().optional(),
});
export type UserTerritoryMembershipUpdate = z.infer<typeof UserTerritoryMembershipUpdateSchema>;

export const UserUpdateSchema = z.object({
  name: z.string().min(1).max(255).nullable().optional(),
  phone: z.string().nullable().optional(),
  public_alias: z.string().max(80).nullable().optional(),
  role: z.enum(["SUPER_ADMIN", "CORPORATE_WRITER", "TERRITORY_ADMIN", "TERRITORY_MANAGER", "TERRITORY_WRITER", "TERRITORY_READER", "SUB_PROXY"]).nullable().optional(),
  territory_id: z.string().nullable().optional(),
});
export type UserUpdate = z.infer<typeof UserUpdateSchema>;

export const VendorBillCreateSchema = z.object({
  due_date: z.string().nullable().optional(),
  notes: z.string().nullable().optional(),
  payment_terms: z.string().nullable().optional(),
  tax: z.number().min(0).nullable().optional(),
  work_order_id: z.string().nullable().optional(),
});
export type VendorBillCreate = z.infer<typeof VendorBillCreateSchema>;

export const VendorBillItemCreateSchema = z.object({
  description: z.string().min(1).max(500),
  quantity: z.number().gt(0).optional(),
  unit_price: z.number().min(0),
  work_order_id: z.string().nullable().optional(),
});
export type VendorBillItemCreate = z.infer<typeof VendorBillItemCreateSchema>;

export const VendorBillItemResponseSchema = z.object({
  bill_id: z.string(),
  description: z.string(),
  id: z.string(),
  quantity: z.number(),
  territory_id: z.string(),
  total_price: z.number(),
  unit_price: z.number(),
  work_order_id: z.string().nullable(),
});
export type VendorBillItemResponse = z.infer<typeof VendorBillItemResponseSchema>;

export const VendorBillItemUpdateSchema = z.object({
  description: z.string().min(1).max(500).nullable().optional(),
  quantity: z.number().gt(0).nullable().optional(),
  unit_price: z.number().min(0).nullable().optional(),
  work_order_id: z.string().nullable().optional(),
});
export type VendorBillItemUpdate = z.infer<typeof VendorBillItemUpdateSchema>;

export const VendorBillResponseSchema = z.object({
  account_id: z.string().nullable().optional(),
  account_name: z.string().nullable().optional(),
  bill_number: z.string(),
  client_invoice_id: z.string().nullable(),
  client_invoice_number: z.string().nullable().optional(),
  created_at: z.iso.datetime(),
  created_by_id: z.string().nullable(),
  created_by_name: z.string().nullable().optional(),
  document_id: z.string().nullable(),
  due_date: z.string().nullable(),
  id: z.string(),
  notes: z.string().nullable(),
  paid_date: z.string().nullable(),
  payment_status: z.string().nullable().optional(),
  payment_terms: z.string().nullable(),
  payout_blocked_by: z.string().nullable().optional(),
  payout_blocked_by_id: z.string().nullable().optional(),
  payout_blocked_code: z.string().nullable().optional(),
  payout_blocked_reason: z.string().nullable().optional(),
  payout_blocked_service_dates: z.array(z.string()).optional(),
  receipt_url: z.string().nullable().optional(),
  status: z.string(),
  subcontractor_id: z.string(),
  subcontractor_name: z.string().nullable().optional(),
  subtotal: z.number().nullable(),
  tax: z.number().nullable(),
  territory_id: z.string(),
  territory_name: z.string().nullable().optional(),
  total: z.number().nullable(),
  updated_at: z.iso.datetime(),
  updated_by_id: z.string().nullable(),
  updated_by_name: z.string().nullable().optional(),
  version: z.number().int(),
  work_order_id: z.string().nullable(),
  work_order_number: z.string().nullable().optional(),
  work_order_title: z.string().nullable().optional(),
});
export type VendorBillResponse = z.infer<typeof VendorBillResponseSchema>;

export const CursorPage_VendorBillResponse_Schema = z.object({
  next_cursor: z.string().nullable().optional(),
  results: z.array(VendorBillResponseSchema),
});
export type CursorPage_VendorBillResponse_ = z.infer<typeof CursorPage_VendorBillResponse_Schema>;

export const VendorBillUpdateSchema = z.object({
  client_invoice_id: z.string().nullable().optional(),
  due_date: z.string().nullable().optional(),
  notes: z.string().nullable().optional(),
  payment_terms: z.string().nullable().optional(),
  status: z.enum(["PENDING", "APPROVED", "PAID", "REJECTED", "DISPUTED"]).nullable().optional(),
  version: z.number().int(),
});
export type VendorBillUpdate = z.infer<typeof VendorBillUpdateSchema>;

/** The receipt for a Visit-side capture (`POST /inspections/{id}/opportunities`, CA2). Deliberately a receipt, not a `DealResponse`: the runner needs enough to confirm the capture and link to the pipeline record — the deal's id, the title it will render under, the entry stage it landed in, the value as stored — and nothing else, because the capture UI is a chip, not a deal page. `deal_title` is the resolved name for `deal_id` (it is the deal's own title, read back from the INSERT), which satisfies the resolved-name metadata standard for the one relationship this receipt renders. `activity_id` is the structural origin marker's row — the activities entry linking the deal to the visit that found it. */
export const VisitOpportunityCaptureResponseSchema = z.object({
  activity_id: z.string(),
  captured_at: z.iso.datetime().nullable().optional(),
  deal_id: z.string(),
  deal_title: z.string(),
  stage: z.string(),
  value: z.number().nullable().optional(),
});
export type VisitOpportunityCaptureResponse = z.infer<typeof VisitOpportunityCaptureResponseSchema>;

/** #233: the ten-second opportunity capture inside a qualified visit. The rep saw something sellable on the walk. This is the whole form: what it is, roughly what it's worth, and a sentence about it — deliberately smaller than `DealCreate`, because a capture that asks for a stage, a source, a contact and a close date is a capture that does not happen on a walk. Everything else about the resulting deal is derived: the account and the site come from the visit, the territory from the site, the stage from the pipeline's entry stage (the same default `POST /crm/v1/deals` applies), the owner from the caller. `estimated_value` is a float rather than a Decimal to match `DealCreate.value`, the field it feeds — money crosses this API's wire as a float everywhere (`deals.value` is numeric(12,2) in the database, which is where the precision lives). */
export const VisitOpportunityCreateSchema = z.object({
  estimated_value: z.number().min(0).nullable().optional(),
  note: z.string().nullable().optional(),
  title: z.string().min(1),
});
export type VisitOpportunityCreate = z.infer<typeof VisitOpportunityCreateSchema>;

export const QualifiedVisitCreateSchema = z.object({
  account_id: z.string(),
  client_rating: z.number().min(1).max(10).nullable().optional(),
  client_seen: z.boolean().nullable().optional(),
  contact_id: z.string().nullable().optional(),
  fsm_rating: z.number().min(1).max(10).nullable().optional(),
  inspection_id: z.string().nullable().optional(),
  location_id: z.string().nullable().optional(),
  notes: z.string().nullable().optional(),
  opportunity: VisitOpportunityCreateSchema.nullable().optional(),
  subcontractor_id: z.string().nullable().optional(),
  title: z.string().min(1).nullable().optional(),
  work_order_id: z.string().nullable().optional(),
});
export type QualifiedVisitCreate = z.infer<typeof QualifiedVisitCreateSchema>;

export const WorkOrderActivityResponseSchema = z.object({
  action: z.string(),
  actor_name: z.string().nullable(),
  actor_type: z.string().nullable(),
  created_at: z.iso.datetime(),
  diff: z.unknown().optional(),
  id: z.string(),
});
export type WorkOrderActivityResponse = z.infer<typeof WorkOrderActivityResponseSchema>;

export const CursorPage_WorkOrderActivityResponse_Schema = z.object({
  next_cursor: z.string().nullable().optional(),
  results: z.array(WorkOrderActivityResponseSchema),
});
export type CursorPage_WorkOrderActivityResponse_ = z.infer<typeof CursorPage_WorkOrderActivityResponse_Schema>;

export const WorkOrderChecklistItemCreateSchema = z.object({
  area: z.string().nullable().optional(),
  is_required: z.boolean().optional(),
  label: z.string().min(1),
  requires_note: z.boolean().optional(),
  requires_photo: z.boolean().optional(),
  sort_order: z.number().int().optional(),
  work_order_day_id: z.string().nullable().optional(),
});
export type WorkOrderChecklistItemCreate = z.infer<typeof WorkOrderChecklistItemCreateSchema>;

export const WorkOrderChecklistItemResponseSchema = z.object({
  area: z.string().nullable().optional(),
  completed: z.boolean(),
  completed_at: z.iso.datetime().nullable(),
  completed_by_id: z.string().nullable(),
  completed_by_type: z.string().nullable(),
  completion_note: z.string().nullable(),
  created_at: z.iso.datetime(),
  created_by_id: z.string().nullable(),
  expected_result: z.string().nullable().optional(),
  id: z.string(),
  is_required: z.boolean(),
  label: z.string(),
  photo_path: z.string().nullable(),
  requires_note: z.boolean(),
  requires_photo: z.boolean(),
  sort_order: z.number().int(),
  source_issue_id: z.string().nullable().optional(),
  source_issue_inspection_id: z.string().nullable().optional(),
  territory_id: z.string(),
  visit_id: z.string().nullable(),
  withdrawn_at: z.iso.datetime().nullable().optional(),
  withdrawn_by_id: z.string().nullable().optional(),
  work_order_day_id: z.string().nullable().optional(),
  work_order_id: z.string(),
});
export type WorkOrderChecklistItemResponse = z.infer<typeof WorkOrderChecklistItemResponseSchema>;

export const WorkOrderChecklistItemUpdateSchema = z.object({
  area: z.string().nullable().optional(),
  completed: z.boolean().nullable().optional(),
  completion_note: z.string().nullable().optional(),
  is_required: z.boolean().nullable().optional(),
  label: z.string().min(1).nullable().optional(),
  photo_path: z.string().nullable().optional(),
  requires_note: z.boolean().nullable().optional(),
  requires_photo: z.boolean().nullable().optional(),
  sort_order: z.number().int().nullable().optional(),
  withdrawn: z.boolean().nullable().optional(),
  work_order_day_id: z.string().nullable().optional(),
});
export type WorkOrderChecklistItemUpdate = z.infer<typeof WorkOrderChecklistItemUpdateSchema>;

export const WorkOrderCreateSchema = z.object({
  account_id: z.string(),
  agreed_sub_cost: z.number().min(0).nullable().optional(),
  assigned_to_id: z.string().nullable().optional(),
  change_order_of: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
  estimated_cost: z.number().nullable().optional(),
  location_id: z.string().nullable().optional(),
  not_to_exceed: z.number().nullable().optional(),
  notes: z.string().nullable().optional(),
  order_type: z.string().nullable().optional(),
  po_number: z.string().nullable().optional(),
  priority: z.string().nullable().optional(),
  scheduled_date: z.iso.datetime().nullable().optional(),
  scheduled_duration: z.number().int().nullable().optional(),
  scheduled_time: z.string().nullable().optional(),
  service_category: z.string().nullable().optional(),
  service_request_id: z.string().nullable().optional(),
  source_quote_id: z.string().nullable().optional(),
  subcontractor_id: z.string().nullable().optional(),
  title: z.string().min(1).max(255),
  week_start: z.string().nullable().optional(),
});
export type WorkOrderCreate = z.infer<typeof WorkOrderCreateSchema>;

/** Resolve an outstanding service night by administrative authority (#539). A written reason and NOTHING ELSE. The owner ruling asks for a reason and explicitly does not ask for a photograph — the office is not standing in the building, which is the same reasoning that made the photo mandatory for the crew in #214/#235 and spares the office it here. The constraint must be `StringConstraints`, NOT `Field(strip_whitespace=True)`. On Pydantic v2 that Field keyword is not a validation setting at all - it lands in `json_schema_extra` and silently does nothing, so `" "` satisfies `min_length=1` on its three spaces and arrives as a blank justification. The database CHECK (`btrim(...) <> ''`, migration 135) would still refuse it, but as a constraint violation rather than a clean 422, which is a worse answer to give. */
export const WorkOrderDayAdminOverrideSchema = z.object({
  reason: z.string().min(1),
});
export type WorkOrderDayAdminOverride = z.infer<typeof WorkOrderDayAdminOverrideSchema>;

export const WorkOrderDayCreateSchema = z.object({
  note: z.string().nullable().optional(),
  service_date: z.string(),
  status: z.enum(["pending", "done", "skipped", "closed"]).optional(),
  work_order_id: z.string(),
});
export type WorkOrderDayCreate = z.infer<typeof WorkOrderDayCreateSchema>;

export const WorkOrderDayResponseSchema = z.object({
  admin_override_at: z.iso.datetime().nullable().optional(),
  admin_override_by_id: z.string().nullable().optional(),
  admin_override_reason: z.string().nullable().optional(),
  cancelled_by_close_at: z.iso.datetime().nullable().optional(),
  cancelled_by_close_by_id: z.string().nullable().optional(),
  closed_at: z.iso.datetime().nullable().optional(),
  closed_by_id: z.string().nullable().optional(),
  closeout_required: z.boolean().optional(),
  completed_at: z.iso.datetime().nullable(),
  completed_by_id: z.string().nullable(),
  completed_by_type: z.string().nullable(),
  created_at: z.iso.datetime(),
  created_by_id: z.string().nullable().optional(),
  created_by_name: z.string().nullable().optional(),
  id: z.string(),
  missed: z.boolean().optional(),
  note: z.string().nullable(),
  photo_path: z.string().nullable().optional(),
  service_date: z.string(),
  status: z.string(),
  territory_id: z.string(),
  work_order_id: z.string(),
});
export type WorkOrderDayResponse = z.infer<typeof WorkOrderDayResponseSchema>;

export const CursorPage_WorkOrderDayResponse_Schema = z.object({
  next_cursor: z.string().nullable().optional(),
  results: z.array(WorkOrderDayResponseSchema),
});
export type CursorPage_WorkOrderDayResponse_ = z.infer<typeof CursorPage_WorkOrderDayResponse_Schema>;

export const WorkOrderDayUpdateSchema = z.object({
  note: z.string().nullable().optional(),
  status: z.enum(["pending", "done", "skipped", "closed"]).nullable().optional(),
});
export type WorkOrderDayUpdate = z.infer<typeof WorkOrderDayUpdateSchema>;

export const WorkOrderFeedbackCreateSchema = z.object({
  comments: z.string().nullable().optional(),
  contact_id: z.string().nullable().optional(),
  rating: z.number().int().min(1).max(5),
  submitted_by: z.string().nullable().optional(),
});
export type WorkOrderFeedbackCreate = z.infer<typeof WorkOrderFeedbackCreateSchema>;

export const WorkOrderFeedbackResponseSchema = z.object({
  comments: z.string().nullable(),
  contact_id: z.string().nullable(),
  id: z.string(),
  rating: z.number().int().nullable(),
  submitted_at: z.iso.datetime(),
  submitted_by: z.string().nullable(),
  territory_id: z.string(),
  work_order_id: z.string(),
});
export type WorkOrderFeedbackResponse = z.infer<typeof WorkOrderFeedbackResponseSchema>;

export const WorkOrderInternalNoteCreateSchema = z.object({
  content: z.string().min(1),
});
export type WorkOrderInternalNoteCreate = z.infer<typeof WorkOrderInternalNoteCreateSchema>;

export const WorkOrderInternalNoteResponseSchema = z.object({
  author_is_corporate: z.boolean(),
  author_name: z.string().nullable(),
  content: z.string(),
  created_at: z.iso.datetime(),
  id: z.string(),
});
export type WorkOrderInternalNoteResponse = z.infer<typeof WorkOrderInternalNoteResponseSchema>;

export const WorkOrderLineCreateSchema = z.object({
  contractor_cost: z.number().nullable().optional(),
  qty: z.number().optional(),
  service_category: z.string().nullable().optional(),
  service_id: z.string(),
  service_price_id: z.string().nullable().optional(),
  unit_price: z.number(),
});
export type WorkOrderLineCreate = z.infer<typeof WorkOrderLineCreateSchema>;

export const WorkOrderLineResponseSchema = z.object({
  amount: z.number(),
  below_margin_floor: z.boolean().optional(),
  contractor_cost: z.number().nullable(),
  created_at: z.iso.datetime(),
  id: z.string(),
  qty: z.number(),
  service_category: z.string().nullable(),
  service_id: z.string(),
  service_price_id: z.string().nullable(),
  territory_id: z.string(),
  unit_price: z.number(),
  work_order_id: z.string(),
});
export type WorkOrderLineResponse = z.infer<typeof WorkOrderLineResponseSchema>;

export const WorkOrderLineUpdateSchema = z.object({
  contractor_cost: z.number().nullable().optional(),
  qty: z.number().nullable().optional(),
  service_category: z.string().nullable().optional(),
  service_id: z.string().nullable().optional(),
  service_price_id: z.string().nullable().optional(),
  unit_price: z.number().nullable().optional(),
});
export type WorkOrderLineUpdate = z.infer<typeof WorkOrderLineUpdateSchema>;

export const WorkOrderResponseSchema = z.object({
  account_id: z.string(),
  account_name: z.string().nullable().optional(),
  actual_cost: z.number().nullable(),
  agreed_sub_cost: z.number().nullable().optional(),
  assigned_to_id: z.string().nullable(),
  assigned_to_name: z.string().nullable().optional(),
  cancellation_reason_code: z.string().nullable(),
  change_order_of: z.string().nullable(),
  change_order_status: z.string().nullable().optional(),
  client_billable: z.boolean().nullable().optional(),
  client_signed_at: z.iso.datetime().nullable().optional(),
  client_signed_by: z.string().nullable().optional(),
  client_thread_writable: z.boolean().optional(),
  completed_date: z.iso.datetime().nullable(),
  created_at: z.iso.datetime(),
  created_by_id: z.string().nullable(),
  created_by_name: z.string().nullable().optional(),
  delayed_at: z.iso.datetime().nullable(),
  description: z.string().nullable(),
  estimated_cost: z.number().nullable(),
  id: z.string(),
  inspection_completed_date: z.string().nullable().optional(),
  inspection_id: z.string().nullable().optional(),
  inspection_status: z.string().nullable().optional(),
  is_national: z.boolean().optional(),
  location_id: z.string().nullable(),
  location_name: z.string().nullable().optional(),
  not_to_exceed: z.number().nullable(),
  notes: z.string().nullable(),
  order_number: z.string(),
  order_type: z.string().nullable(),
  po_number: z.string().nullable(),
  priority: z.string().nullable(),
  recurring_schedule_id: z.string().nullable(),
  recurring_schedule_is_active: z.boolean().nullable().optional(),
  required_national_approval: z.boolean().optional(),
  scheduled_date: z.iso.datetime().nullable(),
  scheduled_duration: z.number().int().nullable(),
  scheduled_time: z.string().nullable(),
  service_category: z.string().nullable(),
  service_request_id: z.string().nullable(),
  source_quote_id: z.string().nullable(),
  started_at: z.iso.datetime().nullable(),
  status: z.string(),
  subcontractor_id: z.string().nullable(),
  subcontractor_name: z.string().nullable().optional(),
  territory_id: z.string(),
  territory_name: z.string().nullable().optional(),
  title: z.string(),
  updated_at: z.iso.datetime(),
  updated_by_id: z.string().nullable(),
  updated_by_name: z.string().nullable().optional(),
  version: z.number().int(),
  week_start: z.string().nullable(),
  work_complete_claimed_at: z.iso.datetime().nullable().optional(),
});
export type WorkOrderResponse = z.infer<typeof WorkOrderResponseSchema>;

export const CursorPage_WorkOrderResponse_Schema = z.object({
  next_cursor: z.string().nullable().optional(),
  results: z.array(WorkOrderResponseSchema),
});
export type CursorPage_WorkOrderResponse_ = z.infer<typeof CursorPage_WorkOrderResponse_Schema>;

/** The operator sign-off stamp. WHO is the caller, never the body — a stamp naming somebody else is not a stamp, and BE1's whole point is that the verifier is identified. */
export const WorkOrderSignOffCreateSchema = z.object({
  inspection_id: z.string().nullable().optional(),
  note: z.string().nullable().optional(),
});
export type WorkOrderSignOffCreate = z.infer<typeof WorkOrderSignOffCreateSchema>;

export const WorkOrderTeamMessageCreateSchema = z.object({
  content: z.string().min(1),
});
export type WorkOrderTeamMessageCreate = z.infer<typeof WorkOrderTeamMessageCreateSchema>;

/** Territory-only work-order message; never exposed to Corporate or external portals. */
export const WorkOrderTeamMessageResponseSchema = z.object({
  author_name: z.string().nullable(),
  content: z.string(),
  created_at: z.iso.datetime(),
  id: z.string(),
});
export type WorkOrderTeamMessageResponse = z.infer<typeof WorkOrderTeamMessageResponseSchema>;

export const WorkOrderUpdateSchema = z.object({
  actual_cost: z.number().nullable().optional(),
  agreed_sub_cost: z.number().min(0).nullable().optional(),
  assigned_to_id: z.string().nullable().optional(),
  cancellation_reason_code: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
  estimated_cost: z.number().nullable().optional(),
  location_id: z.string().nullable().optional(),
  not_to_exceed: z.number().nullable().optional(),
  notes: z.string().nullable().optional(),
  order_type: z.string().nullable().optional(),
  po_number: z.string().nullable().optional(),
  priority: z.string().nullable().optional(),
  scheduled_date: z.iso.datetime().nullable().optional(),
  scheduled_duration: z.number().int().nullable().optional(),
  scheduled_time: z.string().nullable().optional(),
  service_category: z.string().nullable().optional(),
  status: z.enum(["OPEN", "SCHEDULED", "DELAYED", "IN_PROGRESS", "ON_HOLD", "COMPLETED", "CANCELLED", "PENDING_APPROVAL"]).nullable().optional(),
  subcontractor_id: z.string().nullable().optional(),
  title: z.string().min(1).max(255).nullable().optional(),
  version: z.number().int(),
  week_start: z.string().nullable().optional(),
});
export type WorkOrderUpdate = z.infer<typeof WorkOrderUpdateSchema>;

/** The verification state of ONE one-time work order — one evaluation, plus at most one resolution of the sign-off requirement. `sign_off_required` is derived from `rung`, not stored, because it is the same fact said twice and a stored duplicate is a fact that can disagree with itself. */
export const WorkOrderVerificationResponseSchema = z.object({
  created_at: z.iso.datetime(),
  evaluated_at: z.iso.datetime(),
  evaluated_by_id: z.string().nullable().optional(),
  evaluated_by_name: z.string().nullable().optional(),
  id: z.string(),
  rung: z.enum(["CREW_CHECKLIST", "OPERATOR_SIGN_OFF"]),
  rung_reason_code: z.enum(["NATIONAL_ACCOUNT", "VALUE_GATE_UNSET"]),
  sign_off_inspection_id: z.string().nullable().optional(),
  sign_off_note: z.string().nullable().optional(),
  sign_off_required: z.boolean(),
  signed_off_at: z.iso.datetime().nullable().optional(),
  signed_off_by_id: z.string().nullable().optional(),
  signed_off_by_name: z.string().nullable().optional(),
  territory_id: z.string(),
  value_gate_threshold: z.number().nullable().optional(),
  waived_at: z.iso.datetime().nullable().optional(),
  waived_by_id: z.string().nullable().optional(),
  waived_by_name: z.string().nullable().optional(),
  waiver_reason: z.string().nullable().optional(),
  work_order_id: z.string(),
  work_order_number: z.string().nullable().optional(),
});
export type WorkOrderVerificationResponse = z.infer<typeof WorkOrderVerificationResponseSchema>;

/** The NBD waiver. BE1: 'the waiver is logged, so a waived job is a recorded exception with a name on it rather than a silent one' — so the reason is required here, required by a CHECK on the table, and written to the audit trail besides. */
export const WorkOrderWaiverCreateSchema = z.object({
  reason: z.string().min(1),
});
export type WorkOrderWaiverCreate = z.infer<typeof WorkOrderWaiverCreateSchema>;
